import {
  Box,
  Flex,
  IconButton,
  IconButton_default,
  Loading,
  Text,
  Tooltip,
  __async,
  __export,
  __objRest,
  __privateAdd,
  __privateGet,
  __privateSet,
  __publicField,
  __spreadProps,
  __spreadValues,
  config,
  createTheme,
  css,
  define_process_env_default,
  dialogClose,
  dialogOpen,
  flexCenter,
  globalStyles,
  keyframes,
  popoverAnimation,
  sheetFadeIn,
  sheetFadeOut,
  sheetSlideIn,
  sheetSlideOut,
  slideDown,
  slideUp,
  styled,
  textEllipsis,
  theme,
  toastAnimation
} from "./chunk-5DQ3WTED.js";

// src/Button/Button.tsx
import React2 from "react";

// src/Theme/ThemeProvider.tsx
import React, { useCallback, useEffect as useEffect2, useMemo, useRef, useState as useState2 } from "react";

// src/Theme/useSSR.tsx
import { useEffect, useState } from "react";
var isBrowser = () => {
  return Boolean(typeof window !== "undefined" && window.document && window.document.createElement);
};
var useSSR = () => {
  const [browser, setBrowser] = useState(false);
  useEffect(() => {
    setBrowser(isBrowser());
  }, []);
  return {
    isBrowser: browser,
    isServer: !browser
  };
};
var useSSR_default = useSSR;

// src/Theme/ThemeProvider.tsx
var defaultAspectRatio = {
  width: 1,
  height: 1
};
var ThemeTypes = /* @__PURE__ */ ((ThemeTypes2) => {
  ThemeTypes2["default"] = "default";
  return ThemeTypes2;
})(ThemeTypes || {});
var defaultContext = {
  themeType: "default" /* default */,
  theme,
  aspectRatio: { width: 1, height: 1 },
  toggleTheme: (_themeToUpdateTo) => {
    return;
  }
};
var ThemeContext = React.createContext(defaultContext);
var HMSThemeProvider = ({
  themeType,
  theme: userTheme,
  aspectRatio = defaultAspectRatio,
  children
}) => {
  const systemTheme = "default" /* default */;
  const [currentTheme, setCurrentTheme] = useState2(themeType || systemTheme);
  const previousClassName = useRef("");
  const { isBrowser: isBrowser2 } = useSSR_default();
  const updatedTheme = useMemo(() => {
    const updatedTheme2 = createTheme({ themeType: currentTheme, theme: userTheme || {} });
    if (!isBrowser2) {
      return updatedTheme2;
    }
    if (previousClassName.current) {
      document.documentElement.classList.remove(previousClassName.current);
    }
    previousClassName.current = updatedTheme2.className;
    document.documentElement.classList.add(updatedTheme2);
    return updatedTheme2;
  }, [userTheme, currentTheme, isBrowser2]);
  const toggleTheme = useCallback((themeToUpdateTo) => {
    if (themeToUpdateTo) {
      setCurrentTheme(themeToUpdateTo);
      return;
    }
    setCurrentTheme("default" /* default */);
  }, []);
  useEffect2(() => {
    if (themeType) {
      setCurrentTheme(themeType);
    }
  }, [themeType]);
  return /* @__PURE__ */ React.createElement(
    ThemeContext.Provider,
    {
      value: { themeType: currentTheme, theme: updatedTheme, aspectRatio, toggleTheme }
    },
    children
  );
};
var useTheme = () => React.useContext(ThemeContext);

// src/Button/Button.tsx
var getOutlinedVariants = (base, hover, active, disabled, text, textDisabled) => {
  return {
    bg: "$transparent",
    border: `solid $space$px $colors${base}`,
    c: text,
    "&[disabled]": {
      c: textDisabled,
      bg: "$transparent",
      border: `solid $space$px $colors${disabled}`,
      cursor: "not-allowed"
    },
    "&:not([disabled]):hover": {
      border: `solid $space$px $colors${hover}`,
      bg: "$transparent"
    },
    "&:not([disabled]):active": {
      border: `solid $space$px $colors${active}`,
      bg: "$transparent"
    },
    "&:not([disabled]):focus-visible": {
      boxShadow: `0 0 0 3px $colors${base}`
    }
  };
};
var getButtonVariants = (base, hover, active, disabled, text, textDisabled) => {
  return {
    bg: base,
    border: `1px solid ${base}`,
    c: text,
    "&[disabled]": {
      c: textDisabled,
      cursor: "not-allowed",
      bg: disabled,
      border: `1px solid ${disabled}`
    },
    "&:not([disabled]):hover": {
      bg: hover,
      border: `1px solid ${hover}`
    },
    "&:not([disabled]):active": {
      bg: active,
      border: `1px solid ${active}`
    },
    "&:not([disabled]):focus-visible": {
      boxShadow: `0 0 0 3px ${base}`
    }
  };
};
var StyledButton = styled("button", __spreadProps(__spreadValues({}, flexCenter), {
  cursor: "pointer",
  fontFamily: "$sans",
  lineHeight: "inherit",
  textTransform: "none",
  position: "relative",
  outline: "none",
  border: "none",
  fs: "$md",
  r: "$1",
  backgroundColor: "$primary_default",
  fontWeight: "500",
  whiteSpace: "nowrap",
  p: "$4 $8",
  "-webkit-appearance": "button",
  "&:focus": {
    outline: "none"
  },
  "&:not([disabled]):focus-visible": {
    boxShadow: "0 0 0 3px $colors$primary_default"
  },
  transition: "all 0.2s  ease",
  compoundVariants: [
    {
      variant: "standard",
      outlined: true,
      css: getOutlinedVariants(
        "$secondary_default",
        "$secondary_bright",
        "$secondary_dim",
        "$secondary_disabled",
        "$on_surface_high",
        "$on_surface_low"
      )
    },
    {
      variant: "danger",
      outlined: true,
      css: getOutlinedVariants(
        "$alert_error_default",
        "$alert_error_bright",
        "$alert_error_dim",
        "$alert_error_brighter",
        "$on_surface_high",
        "$on_surface_low"
      )
    },
    {
      variant: "primary",
      outlined: true,
      css: getOutlinedVariants(
        "$primary_default",
        "$primary_bright",
        "$primary_dim",
        "$primary_disabled",
        "$on_surface_high",
        "$on_surface_low"
      )
    }
  ],
  variants: {
    variant: {
      standard: getButtonVariants(
        "$surface_brighter",
        "$surface_bright",
        "$surface_default",
        "$surface_dim",
        "$on_surface_high",
        "$on_surface_low"
      ),
      danger: getButtonVariants(
        "$alert_error_default",
        "$alert_error_bright",
        "$alert_error_dim",
        "$alert_error_dim",
        "$alert_error_brighter",
        "$on_primary_low"
      ),
      primary: getButtonVariants(
        "$primary_default",
        "$primary_bright",
        "$primary_dim",
        "$primary_disabled",
        "$on_primary_high",
        "$on_primary_low"
      )
    },
    outlined: {
      true: {}
    },
    icon: {
      true: {}
    }
  },
  defaultVariants: {
    variant: "primary"
  }
}));
var Button = React2.forwardRef((_a7, ref) => {
  var _b7 = _a7, { children, loading = false, icon } = _b7, buttonProps = __objRest(_b7, ["children", "loading", "icon"]);
  return /* @__PURE__ */ React2.createElement(StyledButton, __spreadProps(__spreadValues({}, buttonProps), { ref }), /* @__PURE__ */ React2.createElement(React2.Fragment, null, loading && /* @__PURE__ */ React2.createElement(
    Flex,
    {
      align: "center",
      justify: "center",
      css: { w: "100%", position: "absolute", left: "0", color: "$on_primary_low" }
    },
    /* @__PURE__ */ React2.createElement(Loading, { color: "currentColor" })
  ), /* @__PURE__ */ React2.createElement(
    Flex,
    {
      align: "center",
      justify: "center",
      css: { visibility: loading ? "hidden" : "visible", gap: icon ? "$4" : "0" }
    },
    children
  )));
});

// src/Avatar/Avatar.tsx
import React3 from "react";
import { PersonIcon } from "@100mslive/react-icons";

// src/Avatar/getAvatarBg.ts
var getInitials = (name) => {
  var _a7, _b7, _c;
  if (!name) {
    return void 0;
  } else {
    return (_c = (_b7 = (_a7 = name.trim().match(/(^\S\S?|\b\S)?/g)) == null ? void 0 : _a7.join("")) == null ? void 0 : _b7.match(/(^\S|\S$)?/g)) == null ? void 0 : _c.join("").toUpperCase();
  }
};
var getAvatarBg = (name) => {
  const initials = getInitials(name);
  const indexFactor = 20;
  const colorIndex = ((initials == null ? void 0 : initials.codePointAt(0)) || 0) % indexFactor + 1;
  return { initials: initials || "", color: colorsList[colorIndex - 1] };
};
var colorsList = [
  "#616161",
  "#F44336",
  "#3F51B5",
  "#4CAF50",
  "#FFA000",
  "#795548",
  "#E91E63",
  "#2F80FF",
  "#8BC34A",
  "#F57C00",
  "#4E342E",
  "#9C27B0",
  "#00BCD4",
  "#C0CA33",
  "#F4511E",
  "#673AB7",
  "#009688",
  "#FBC02D",
  "#BF360C",
  "#455A64"
];

// src/Avatar/Avatar.tsx
var getAvatarShape = (radii) => ({
  borderRadius: radii
});
var StyledAvatar = styled("div", __spreadProps(__spreadValues({}, flexCenter), {
  color: "$colors$on_primary_high",
  fontFamily: "$sans",
  aspectRatio: 1,
  fontWeight: 600,
  fontSize: "$space$9",
  variants: {
    shape: {
      circle: getAvatarShape("$round"),
      square: getAvatarShape("$1")
    },
    size: {
      small: {
        height: "$16 !important",
        fontSize: "$space$6"
      },
      medium: {
        height: "$18 !important",
        fontSize: "$space$10"
      },
      large: {
        height: "$20 !important",
        fontSize: "$space$12"
      }
    }
  },
  defaultVariants: {
    shape: "circle"
  }
}));
var Avatar = (_a7) => {
  var _b7 = _a7, { name, css: css2 } = _b7, props = __objRest(_b7, ["name", "css"]);
  const { initials } = getAvatarBg(name);
  let { color } = getAvatarBg(name);
  if (!name) {
    color = "#7E47EB";
  }
  return /* @__PURE__ */ React3.createElement(StyledAvatar, __spreadValues({ css: __spreadValues({ bg: color }, css2) }, props), initials || /* @__PURE__ */ React3.createElement(PersonIcon, { height: 40, width: 40 }));
};

// src/Divider/Divider.tsx
var getVerticalSpace = (space) => ({
  marginLeft: space,
  marginRight: space
});
var getHorizontalSpace = (space) => ({
  marginTop: space,
  marginBottom: space
});
var VerticalDivider = styled("span", {
  height: "25px",
  width: "1px",
  backgroundColor: "$border_bright",
  variants: {
    space: {
      1: getVerticalSpace("$1"),
      2: getVerticalSpace("$2"),
      3: getVerticalSpace("$3"),
      4: getVerticalSpace("$4")
    }
  },
  defaultVariants: {
    space: 1
  }
});
var HorizontalDivider = styled("span", {
  height: "1px",
  width: "100%",
  display: "block",
  backgroundColor: "$border_bright",
  variants: {
    space: {
      1: getHorizontalSpace("$1"),
      2: getHorizontalSpace("$2"),
      3: getHorizontalSpace("$3"),
      4: getHorizontalSpace("$4")
    }
  },
  defaultVariants: {
    space: 1
  }
});

// src/Switch/Switch.tsx
import React4 from "react";
import * as BaseSwitch from "@radix-ui/react-switch";
var SwitchRoot = styled(BaseSwitch.Root, {
  all: "unset",
  width: "30px",
  height: "14px",
  border: "solid $space$px $secondary_default",
  borderRadius: "$3",
  p: "$2",
  position: "relative",
  cursor: "pointer",
  '&[data-state="checked"]': {
    backgroundColor: "$primary_default",
    border: "solid $space$px $primary_default"
  },
  "&:focus": {
    outline: "none"
  },
  "&[disabled]": {
    opacity: 0.5,
    cursor: "not-allowed"
  }
});
var SwitchThumb = styled(BaseSwitch.Thumb, {
  display: "block",
  top: "3px",
  position: "absolute",
  width: "$md",
  height: "$md",
  backgroundColor: "$secondary_default",
  borderRadius: "$round",
  transition: "right 500ms ease-in",
  left: "$2",
  '&[data-state="checked"]': {
    left: "unset",
    right: "$2",
    backgroundColor: "$on_primary_high"
  }
});
var Switch = (props) => /* @__PURE__ */ React4.createElement(SwitchRoot, __spreadValues({}, props), /* @__PURE__ */ React4.createElement(SwitchThumb, null));
Switch.displayName = "Switch";

// src/Select/Select.tsx
import React5 from "react";
import { ChevronDownIcon } from "@100mslive/react-icons";
var Root2 = styled("div", {
  color: "$on_primary_high",
  display: "inline-flex",
  position: "relative",
  outline: "none",
  overflow: "hidden",
  borderRadius: "$1",
  backgroundColor: "$surface_default",
  maxWidth: "100%"
});
var SelectRoot = styled("select", {
  h: "$16",
  fontSize: "$md",
  fontWeight: "500",
  lineHeight: "inherit",
  textTransform: "none",
  appearance: "none",
  color: "$on_secondary_high",
  padding: "5px",
  paddingLeft: "12px",
  paddingRight: "30px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "$secondary_default",
  "&:not([disabled]):focus-visible": {
    boxShadow: "0 0 0 3px $colors$primary_default"
  },
  "&[disabled]": {
    opacity: 0.5,
    cursor: "not-allowed"
  }
});
var Arrow = styled("span", {
  color: "$on_secondary_high",
  width: "30px",
  height: "100%",
  position: "absolute",
  right: 0,
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  transition: "border .2s ease 0s"
});
var DefaultDownIcon = (_a7) => {
  var props = __objRest(_a7, []);
  return /* @__PURE__ */ React5.createElement(Arrow, __spreadValues({}, props), /* @__PURE__ */ React5.createElement(ChevronDownIcon, null));
};
var Select = {
  Root: Root2,
  DownIcon: Arrow,
  DefaultDownIcon,
  Select: SelectRoot
};

// src/Slider/Slider.tsx
import React6 from "react";
import * as BaseSlider from "@radix-ui/react-slider";
var Root4 = styled(BaseSlider.Root, {
  position: "relative",
  display: "flex",
  alignItems: "center",
  userSelect: "none",
  touchAction: "none",
  width: "100%",
  '&[data-orientation="horizontal"]': {
    height: 20
  }
});
var Track2 = styled(BaseSlider.Track, {
  backgroundColor: "$surface_bright",
  position: "relative",
  flexGrow: 1,
  borderRadius: "$round",
  cursor: "pointer",
  '&[data-orientation="horizontal"]': { height: 3 }
});
var Range2 = styled(BaseSlider.Range, {
  position: "absolute",
  backgroundColor: "$primary_default",
  borderRadius: "$round",
  height: "100%"
});
var Thumb3 = styled(BaseSlider.Thumb, {
  all: "unset",
  display: "block",
  width: "$8",
  height: "$8",
  backgroundColor: "$primary_default",
  cursor: "pointer",
  boxShadow: `0 2px 10px $colors$surface_default`,
  borderRadius: 10,
  "&:hover": { backgroundColor: "$primary_default" },
  "&:focus": { boxShadow: "none" }
});
var Slider = (_a7) => {
  var _b7 = _a7, {
    showTooltip = true,
    thumbStyles
  } = _b7, props = __objRest(_b7, [
    "showTooltip",
    "thumbStyles"
  ]);
  var _a8;
  return /* @__PURE__ */ React6.createElement(Root4, __spreadValues({}, props), /* @__PURE__ */ React6.createElement(Track2, null, /* @__PURE__ */ React6.createElement(Range2, null)), showTooltip ? /* @__PURE__ */ React6.createElement(Tooltip, { title: String((_a8 = props.value) == null ? void 0 : _a8[0]) }, /* @__PURE__ */ React6.createElement(Thumb3, { css: thumbStyles })) : /* @__PURE__ */ React6.createElement(Thumb3, { css: thumbStyles }));
};

// src/Modal/Dialog.tsx
import { Root as Root5 } from "@radix-ui/react-dialog";
import { styled as styled2 } from "@stitches/react";

// src/Modal/DialogContent.tsx
import React7 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CrossIcon } from "@100mslive/react-icons";
var DialogClose = styled(DialogPrimitive.Close, {
  backgroundColor: "transparent",
  padding: "0",
  margin: "0",
  border: "none",
  backgroundImage: "none"
});
var StyledDialogTrigger = styled(DialogPrimitive.Trigger, {
  appearance: "none !important"
  // Needed for safari it shows white overlay
});
var StyledDialogOverlay = styled(DialogPrimitive.Overlay, {
  backgroundColor: "rgba(0, 0, 0, 0.5);",
  position: "fixed",
  inset: 0
});
var StyledDialogPortal = styled(DialogPrimitive.Portal, {});
var StyledDialogContent = styled(DialogPrimitive.Content, {
  color: "$on_surface_medium",
  backgroundColor: "$surface_default",
  borderRadius: "8px",
  position: "fixed",
  top: "50%",
  left: "50%",
  border: "$space$px solid $border_bright",
  boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  transform: "translate(-50%, -50%)",
  zIndex: 999,
  padding: "$12",
  "@allowMotion": {
    '&[data-state="open"]': {
      animation: `${dialogOpen} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
    },
    '&[data-state="closed"]': {
      animation: `${dialogClose} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
    }
  },
  "&:focus": { outline: "none" }
});
var DialogTitle = styled(DialogPrimitive.Title, {
  margin: 0
});
var DialogDescription = styled(DialogPrimitive.Description, {});
var DialogDefaultCloseIcon = (props) => /* @__PURE__ */ React7.createElement(DialogClose, { asChild: true }, /* @__PURE__ */ React7.createElement(IconButton, __spreadValues({}, props), /* @__PURE__ */ React7.createElement(CrossIcon, null)));

// src/Modal/Dialog.tsx
var StyledDialog = styled2(Root5, {});
var Dialog = {
  Root: StyledDialog,
  Trigger: StyledDialogTrigger,
  Overlay: StyledDialogOverlay,
  Content: StyledDialogContent,
  Title: DialogTitle,
  Description: DialogDescription,
  Close: DialogClose,
  DefaultClose: DialogDefaultCloseIcon,
  Portal: StyledDialogPortal
};

// src/Input/Input.tsx
import React8 from "react";
import { CopyIcon, EyeCloseIcon, EyeOpenIcon } from "@100mslive/react-icons";
var Input = styled("input", {
  fontFamily: "$sans",
  lineHeight: "inherit",
  backgroundColor: "$surface_default",
  borderRadius: "8px",
  outline: "none",
  border: "1px solid $border_default",
  padding: "0.5rem 0.75rem",
  minHeight: "30px",
  color: "$on_surface_high",
  fontSize: "$md",
  "&:focus": {
    boxShadow: "0 0 0 1px $colors$primary_default",
    border: "1px solid transparent"
  },
  "&::placeholder": {
    color: "$on_surface_medium"
  },
  variants: {
    alert_error_default: {
      true: {
        "&:focus": {
          boxShadow: "0 0 0 3px $colors$alert_error_default"
        }
      }
    }
  }
});
var PasswordRoot = styled("div", {
  w: "100%",
  position: "relative",
  display: "flex"
});
var PasswordShowIcon = (_a7) => {
  var _b7 = _a7, {
    showPassword,
    css: css2
  } = _b7, props = __objRest(_b7, [
    "showPassword",
    "css"
  ]);
  return /* @__PURE__ */ React8.createElement(Flex, __spreadValues({ css: __spreadValues({}, css2) }, props), showPassword ? /* @__PURE__ */ React8.createElement(EyeOpenIcon, null) : /* @__PURE__ */ React8.createElement(EyeCloseIcon, null));
};
var PasswordCopyIcon = (_a7) => {
  var _b7 = _a7, { css: css2 } = _b7, props = __objRest(_b7, ["css"]);
  return /* @__PURE__ */ React8.createElement(Flex, __spreadValues({ css: __spreadValues({}, css2) }, props), /* @__PURE__ */ React8.createElement(CopyIcon, null));
};
var PasswordIcons = React8.forwardRef(
  (_a7, ref) => {
    var _b7 = _a7, { css: css2 } = _b7, props = __objRest(_b7, ["css"]);
    return /* @__PURE__ */ React8.createElement(
      Flex,
      __spreadValues({
        css: __spreadValues({
          position: "absolute",
          top: 0,
          height: "100%",
          zIndex: 10,
          right: "$4",
          bg: "$surface_bright",
          alignItems: "center"
        }, css2),
        ref
      }, props),
      props.children
    );
  }
);
var ReactInput = React8.forwardRef((_a7, ref) => {
  var _b7 = _a7, { showPassword = false, css: css2 } = _b7, props = __objRest(_b7, ["showPassword", "css"]);
  return /* @__PURE__ */ React8.createElement(
    Input,
    __spreadProps(__spreadValues({
      css: __spreadValues({ flexGrow: 1, width: "100%" }, css2),
      type: showPassword ? "text" : "password"
    }, props), {
      ref
    })
  );
});
var PasswordInput = {
  Root: PasswordRoot,
  Icons: PasswordIcons,
  Input: ReactInput,
  ShowIcon: PasswordShowIcon,
  CopyIcon: PasswordCopyIcon
};

// src/VideoTile/StyledVideoTile.tsx
var Root6 = styled("div", {
  padding: "0.75rem"
  // show videotile context menu on hover
  // [`&:hover .tile-menu`]: {
  //   display: 'inline-block',
  // },
});
var Container = styled("div", {
  width: "100%",
  height: "100%",
  position: "relative",
  overflow: "hidden",
  borderRadius: "$2",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: "$background_default",
  variants: {
    transparentBg: {
      true: {
        background: "transparent"
      }
    },
    noRadius: {
      true: {
        borderRadius: 0
      }
    }
  }
});
var Overlay2 = styled("div", {
  position: "absolute",
  width: "100%",
  height: "100%"
});
var Info = styled("div", {
  color: "$on_primary_high",
  position: "absolute",
  bottom: "5px",
  left: "50%",
  fontSize: "$sm",
  transform: "translateX(-50%)",
  textAlign: "center",
  width: "80%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  fontFamily: "$sans"
});
var AttributeBox = styled("div", __spreadProps(__spreadValues({
  position: "absolute",
  top: "$4",
  color: "$on_secondary_high",
  bg: "$secondary_dim",
  borderRadius: "$round",
  width: "$14",
  height: "$14",
  mb: "5px"
}, flexCenter), {
  variants: {
    size: {
      small: {
        width: "$10",
        height: "$10",
        "& > svg": {
          width: "$8",
          height: "$8"
        }
      },
      medium: {
        width: "$13",
        height: "$13"
      }
    },
    position: {
      left: { left: "$4" },
      right: { right: "$4" }
    }
  },
  defaultVariants: {
    size: "medium",
    position: "left"
  }
}));
var AudioIndicator = styled(AttributeBox, { defaultVariants: { position: "right" } });
var FullScreenButton = styled("button", {
  width: "2.25rem",
  height: "2.25rem",
  color: "$on_surface_high",
  borderRadius: "$round",
  backgroundColor: "$surface_default",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "$2",
  right: "$2",
  zIndex: 5,
  "&:not([disabled]):focus": {
    outline: "none",
    boxShadow: "0 0 0 3px $colors$primary_bright"
  }
});
var AvatarContainer = styled(Box, __spreadProps(__spreadValues({}, flexCenter), {
  flexDirection: "column",
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translateX(-50%) translateY(-50%)",
  width: "40%",
  height: "40%",
  "& > div": {
    maxHeight: "$20",
    height: "100%"
  }
}));
var StyledVideoTile = {
  Root: Root6,
  Container,
  Overlay: Overlay2,
  Info,
  AvatarContainer,
  AttributeBox,
  AudioIndicator,
  FullScreenButton
};

// src/VideoList/videoListUtils.tsx
var getLeft = (index, currentPageIndex) => {
  if (index === currentPageIndex) {
    return 0;
  }
  if (index + 1 === currentPageIndex) {
    return "-100%";
  }
  if (index - 1 === currentPageIndex) {
    return "100%";
  }
  if (index < currentPageIndex) {
    return "-200%";
  }
  return "200%";
};

// src/VideoList/StyledVideoList.tsx
var Root7 = styled("div", {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column"
});
var Container2 = styled("div", {
  height: "100%",
  width: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  overflow: "hidden"
});
var View = styled("div", {
  height: "100%",
  width: "100%",
  position: "absolute",
  display: "flex",
  placeContent: "center",
  flexWrap: "wrap",
  alignItems: "center"
});
var StyledVideoList = {
  Root: Root7,
  Container: Container2,
  View
};

// src/Pagination/StyledPagination.tsx
var Root8 = styled("div", {
  height: "2rem",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  "& > * + *": {
    marginRight: "0",
    marginLeft: "0.5rem"
  }
});
var Chevron = styled("button", {
  color: "$on_surface_high",
  "&:focus": {
    outline: "none"
  },
  "&[disabled]": {
    color: "$on_surface_low",
    cursor: "not-allowed"
  },
  backgroundColor: "transparent",
  display: "flex"
});
var Dots = styled("div", {
  display: "flex",
  alignItems: "center",
  "& > * + *": {
    marginRight: "0",
    marginLeft: "0.5rem"
  }
});
var Dot = styled("button", {
  "&:focus": {
    outline: "none"
  },
  borderRadius: "9999px",
  width: "0.5rem",
  height: "0.5rem",
  backgroundColor: "$on_surface_low",
  padding: "0px",
  border: "none",
  cursor: "pointer",
  variants: {
    active: {
      true: {
        backgroundColor: "$on_surface_high"
      }
    }
  }
});
var StyledPagination = {
  Root: Root8,
  Chevron,
  Dots,
  Dot
};

// src/Dropdown/Dropdown.tsx
import {
  CheckboxItem,
  Content as Content2,
  Group,
  Item,
  ItemIndicator,
  Label,
  Portal as Portal2,
  Root as Root9,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger as Trigger2
} from "@radix-ui/react-dropdown-menu";
var DropdownRoot = styled(Root9, {});
var DropdownTrigger = styled(Trigger2, {
  cursor: "pointer",
  appearance: "none !important",
  '&[data-state="open"]': {
    backgroundColor: "$surface_bright"
  },
  "&:focus": {
    outline: "none"
  },
  "&:focus-visible": {
    boxShadow: "0 0 0 3px $colors$primary_default"
  }
});
var DropdownTriggerItem = styled(SubTrigger, {
  w: "100%",
  color: "$on_surface_high",
  p: "$8",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    cursor: "pointer",
    bg: "$surface_brighter"
  },
  "&:focus-visible": {
    bg: "$surface_brighter",
    outline: "none"
  }
});
var DropdownItem = styled(Item, {
  color: "$on_surface_high",
  p: "$8",
  display: "flex",
  alignItems: "center",
  outline: "none",
  backgroundColor: "$surface_dim",
  "&:hover": {
    cursor: "pointer",
    bg: "$surface_bright"
  },
  "&:focus-visible": {
    bg: "$surface_bright"
  }
});
var DropdownItemSeparator = styled(Separator, {
  h: 1,
  backgroundColor: "$border_bright",
  m: "$4 $8"
});
var DropdownContent = styled(Content2, {
  w: "$80",
  maxHeight: "$64",
  r: "$1",
  py: "$4",
  backgroundColor: "$surface_dim",
  overflowY: "auto",
  boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  zIndex: 20,
  fontFamily: "$sans"
});
var DropdownLabel = styled(Label, {
  display: "flex",
  alignItems: "center",
  h: "$12",
  w: "100%",
  p: "$8 $4"
});
var DropdownGroup = styled(Group, {});
var DropdownSubMenu = styled(Sub, {});
var DropdownSubMenuContent = styled(SubContent, {
  w: "$80",
  maxHeight: "$64",
  r: "$1",
  py: "$4",
  backgroundColor: "$surface_bright",
  overflowY: "auto",
  boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  zIndex: 20
});
var DropdownCheckboxItem = styled(CheckboxItem, {
  color: "$on_surface_high",
  p: "$8",
  display: "flex",
  alignItems: "center",
  outline: "none",
  "&:hover": {
    cursor: "pointer",
    bg: "$surface_brighter"
  },
  "&:focus-visible": {
    bg: "$surface_brighter"
  },
  gap: "$2"
});
var DropdownItemIndicator = styled(ItemIndicator, {
  w: "$10",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
});
var Dropdown = {
  Root: DropdownRoot,
  Trigger: DropdownTrigger,
  TriggerItem: DropdownTriggerItem,
  Content: DropdownContent,
  Portal: Portal2,
  SubMenu: DropdownSubMenu,
  SubMenuContent: DropdownSubMenuContent,
  Item: DropdownItem,
  Label: DropdownLabel,
  Group: DropdownGroup,
  ItemSeparator: DropdownItemSeparator,
  CheckboxItem: DropdownCheckboxItem,
  ItemIndicator: DropdownItemIndicator
};

// src/Video/Video.tsx
import React9 from "react";
import { useVideo } from "@100mslive/react-sdk";
var StyledVideo = styled("video", {
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "$2",
  objectFit: "cover",
  background: "$background_default",
  variants: {
    mirror: {
      true: {
        transform: "scaleX(-1)"
      }
    },
    screenShare: {
      true: {
        objectFit: "contain"
      }
    },
    degraded: {
      // send the video behind when it's degraded so avatar can show on top of it. Video will be stuck frame in this case.
      // not hiding by using display none, because it will lead it to be detached as it will no longer be in view.
      true: {
        zIndex: -100
      }
    },
    noRadius: {
      true: {
        borderRadius: 0
      }
    }
  },
  defaultVariants: {
    mirror: false
  }
});
var Video = (_a7) => {
  var _b7 = _a7, { trackId, attach } = _b7, props = __objRest(_b7, ["trackId", "attach"]);
  const { videoRef } = useVideo({ trackId, attach });
  return /* @__PURE__ */ React9.createElement(StyledVideo, __spreadValues({ autoPlay: true, muted: true, playsInline: true, controls: false, ref: videoRef }, props));
};

// src/TileMenu/StyledMenuTile.tsx
import * as Popover from "@radix-ui/react-popover";
var Root11 = Popover.Root;
var StyledTrigger = styled(Popover.Trigger, __spreadProps(__spreadValues({
  position: "absolute",
  bottom: "$2",
  right: "$2",
  zIndex: 10,
  width: "$13",
  height: "$13",
  color: "$on_surface_high",
  borderRadius: "$2",
  backgroundColor: "$surface_bright",
  cursor: "pointer",
  border: "none"
}, flexCenter), {
  "&:not([disabled]):focus": {
    outline: "none",
    boxShadow: "0 0 0 3px $colors$primary_bright"
  }
}));
var StyledContent = styled(Popover.Content, __spreadValues({
  fontFamily: "$sans",
  backgroundColor: "$surface_dim",
  padding: "$5 0",
  display: "flex",
  flexDirection: "column",
  borderRadius: "$space$6",
  zIndex: 11,
  width: "$60"
}, popoverAnimation));
var styledItem = {
  fontSize: "$sm",
  color: "$on_surface_high",
  display: "flex",
  alignItems: "center",
  padding: "$4 $6",
  width: "100%",
  backgroundColor: "$menuBg"
};
var StyledItemButton = styled("button", __spreadProps(__spreadValues({}, styledItem), {
  height: "$14",
  "&:hover": {
    backgroundColor: "$surface_brighter"
  },
  cursor: "pointer",
  // TODO: default focus applied cause issues with this style
  "&:focus": {
    outline: "none"
    // backgroundColor: '$grey3',
  },
  "& > * + *": {
    marginRight: "0",
    marginLeft: "$4"
  }
}));
var StyledVolumeItem = styled("div", __spreadProps(__spreadValues({}, styledItem), {
  alignItems: "start",
  flexDirection: "column",
  marginBottom: "0"
}));
var Flex2 = styled("div", {
  display: "flex",
  "& > * + *": {
    marginRight: "0",
    marginLeft: "$4"
  }
});
var RemoveMenuItem = styled(StyledItemButton, {
  color: "$alert_error_default",
  borderTop: "1px solid $border_bright"
});
var StyledMenuTile = {
  Root: Root11,
  Trigger: StyledTrigger,
  Content: StyledContent,
  ItemButton: StyledItemButton,
  VolumeItem: StyledVolumeItem,
  RemoveItem: RemoveMenuItem
};

// src/AudioLevel/AudioLevel.tsx
import { useCallback as useCallback2, useRef as useRef2 } from "react";
import { useAudioLevelStyles } from "@100mslive/react-sdk";
function useBorderAudioLevel(audioTrackId) {
  const { theme: theme2 } = useTheme();
  const color = theme2.colors.primary_default.value;
  const getStyle = useCallback2(
    (level) => {
      const style = {
        transition: "outline 0.4s ease-in-out"
      };
      style["outline"] = level ? `${sigmoid(level) * 4}px solid ${color}` : "0px solid transparent";
      return style;
    },
    [color]
  );
  const ref = useRef2(null);
  useAudioLevelStyles({
    trackId: audioTrackId,
    getStyle,
    ref
  });
  return ref;
}
var sigmoid = (z) => {
  return 1 / (1 + Math.exp(-z));
};

// src/Popover/index.tsx
import { Arrow as Arrow2, Close as Close2, Content as Content4, Popover as Root12, Portal as Portal3, Trigger as Trigger4 } from "@radix-ui/react-popover";
var StyledContent2 = styled(Content4, __spreadValues({
  padding: "$6",
  borderRadius: "$2",
  backgroundColor: "$background_default",
  boxShadow: "$sm",
  zIndex: 12
}, popoverAnimation));
var StyledArrow = styled(Arrow2, __spreadValues({}, popoverAnimation));
var StyledTrigger2 = styled(Trigger4, {});
var Popover2 = {
  Root: Root12,
  Content: StyledContent2,
  Trigger: StyledTrigger2,
  Portal: Portal3,
  Arrow: StyledArrow,
  Close: Close2
};

// src/Stats/Stats.tsx
import React10, { Fragment } from "react";
import {
  selectConnectionQualityByPeerID,
  selectHMSStats,
  simulcastMapping,
  useHMSStatsStore,
  useHMSStore
} from "@100mslive/react-sdk";

// src/Stats/formatBytes.ts
var formatBytes = (bytes, unit = "B", decimals = 2) => {
  if (bytes === 0) {
    return `0 ${unit}`;
  }
  if (!bytes) {
    return "-";
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["", "K", "M", "G", "T", "P", "E", "Z", "Y"].map((size) => size + unit);
  let i = Math.floor(Math.log(bytes) / Math.log(k));
  i === 0 && i++;
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

// src/Stats/StyledStats.tsx
var Root13 = styled("div", {
  backgroundColor: "rgba(0,0,0,0.75)",
  position: "absolute",
  top: "$3",
  left: "$3",
  zIndex: 7,
  borderRadius: "$2",
  padding: "$2",
  fontSize: "$xs",
  overflowY: "auto",
  maxHeight: "75%",
  maxWidth: "85%"
});
var Table = styled("table", {});
var Row = styled("tr", {
  width: "100%",
  "& > * + *": {
    px: "$4"
  },
  whiteSpace: "nowrap",
  textAlign: "left"
});
var Label2 = styled("td", {
  color: "$on_primary_high",
  fontWeight: "$regular"
});
var Value = styled("td", {
  color: "$on_primary_high"
});
var Gap = styled("tr", {
  height: "$4"
});
var Stats = {
  Root: Root13,
  Row,
  Label: Label2,
  Value,
  Table,
  Gap
};

// src/Stats/Stats.tsx
function VideoTileStats({ videoTrackID, audioTrackID, peerID, isLocal = false }) {
  var _a7, _b7, _c;
  const audioSelector = isLocal ? selectHMSStats.localAudioTrackStatsByID : selectHMSStats.trackStatsByID;
  const audioTrackStats = useHMSStatsStore(audioSelector(audioTrackID));
  const localVideoTrackStats = useHMSStatsStore(selectHMSStats.localVideoTrackStatsByID(videoTrackID));
  const remoteVideoTrackStats = useHMSStatsStore(selectHMSStats.trackStatsByID(videoTrackID));
  const videoTrackStats = isLocal ? localVideoTrackStats == null ? void 0 : localVideoTrackStats[0] : remoteVideoTrackStats;
  const downlinkScore = (_a7 = useHMSStore(selectConnectionQualityByPeerID(peerID))) == null ? void 0 : _a7.downlinkQuality;
  const availableOutgoingBitrate = useHMSStatsStore(selectHMSStats.availablePublishBitrate);
  if (!(audioTrackStats || videoTrackStats)) {
    return null;
  }
  return /* @__PURE__ */ React10.createElement(Stats.Root, null, /* @__PURE__ */ React10.createElement("table", null, /* @__PURE__ */ React10.createElement("tbody", null, isLocal ? /* @__PURE__ */ React10.createElement(Fragment, null, /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullishAndNot0(availableOutgoingBitrate),
      label: "AOBR",
      tooltip: "Available Outgoing Bitrate",
      value: formatBytes(availableOutgoingBitrate, "b/s")
    }
  ), localVideoTrackStats == null ? void 0 : localVideoTrackStats.map((stat) => {
    var _a8, _b8;
    if (!stat) {
      return null;
    }
    const layer = stat.rid ? simulcastMapping[stat.rid] : "";
    return /* @__PURE__ */ React10.createElement(Fragment, null, layer && /* @__PURE__ */ React10.createElement(StatsRow, { label: layer.toUpperCase(), value: "" }), /* @__PURE__ */ React10.createElement(
      StatsRow,
      {
        show: isNotNullishAndNot0(stat.frameWidth),
        label: "Width",
        value: (_a8 = stat.frameWidth) == null ? void 0 : _a8.toString()
      }
    ), /* @__PURE__ */ React10.createElement(
      StatsRow,
      {
        show: isNotNullishAndNot0(stat.frameHeight),
        label: "Height",
        value: (_b8 = stat.frameHeight) == null ? void 0 : _b8.toString()
      }
    ), /* @__PURE__ */ React10.createElement(
      StatsRow,
      {
        show: isNotNullishAndNot0(stat.framesPerSecond),
        label: "FPS",
        value: `${stat.framesPerSecond} ${isNotNullishAndNot0(stat.framesDropped) ? `(${stat.framesDropped} dropped)` : ""}`
      }
    ), /* @__PURE__ */ React10.createElement(
      StatsRow,
      {
        show: isNotNullish(stat.bitrate),
        label: "Bitrate(V)",
        value: formatBytes(stat.bitrate, "b/s")
      }
    ), /* @__PURE__ */ React10.createElement(Stats.Gap, null));
  })) : /* @__PURE__ */ React10.createElement(Fragment, null, /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullishAndNot0(videoTrackStats == null ? void 0 : videoTrackStats.frameWidth),
      label: "Width",
      value: (_b7 = videoTrackStats == null ? void 0 : videoTrackStats.frameWidth) == null ? void 0 : _b7.toString()
    }
  ), /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullishAndNot0(videoTrackStats == null ? void 0 : videoTrackStats.frameHeight),
      label: "Height",
      value: (_c = videoTrackStats == null ? void 0 : videoTrackStats.frameHeight) == null ? void 0 : _c.toString()
    }
  ), /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullishAndNot0(videoTrackStats == null ? void 0 : videoTrackStats.framesPerSecond),
      label: "FPS",
      value: `${videoTrackStats == null ? void 0 : videoTrackStats.framesPerSecond} ${isNotNullishAndNot0(videoTrackStats == null ? void 0 : videoTrackStats.framesDropped) ? `(${videoTrackStats == null ? void 0 : videoTrackStats.framesDropped} dropped)` : ""}`
    }
  ), /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullish(videoTrackStats == null ? void 0 : videoTrackStats.bitrate),
      label: "Bitrate(V)",
      value: formatBytes(videoTrackStats == null ? void 0 : videoTrackStats.bitrate, "b/s")
    }
  )), /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullish(audioTrackStats == null ? void 0 : audioTrackStats.bitrate),
      label: "Bitrate(A)",
      value: formatBytes(audioTrackStats == null ? void 0 : audioTrackStats.bitrate, "b/s")
    }
  ), /* @__PURE__ */ React10.createElement(StatsRow, { show: isNotNullish(downlinkScore), label: "Downlink", value: downlinkScore }), /* @__PURE__ */ React10.createElement(StatsRow, { show: isNotNullish(videoTrackStats == null ? void 0 : videoTrackStats.codec), label: "Codec(V)", value: videoTrackStats == null ? void 0 : videoTrackStats.codec }), /* @__PURE__ */ React10.createElement(StatsRow, { show: isNotNullish(audioTrackStats == null ? void 0 : audioTrackStats.codec), label: "Codec(A)", value: audioTrackStats == null ? void 0 : audioTrackStats.codec }), /* @__PURE__ */ React10.createElement(PacketLostAndJitter, { audioTrackStats, videoTrackStats }))));
}
var PacketLostAndJitter = ({
  audioTrackStats,
  videoTrackStats
}) => {
  var _a7, _b7;
  const isLocalPeer = (audioTrackStats == null ? void 0 : audioTrackStats.type.includes("outbound")) || (videoTrackStats == null ? void 0 : videoTrackStats.type.includes("outbound"));
  const audioStats = isLocalPeer ? audioTrackStats == null ? void 0 : audioTrackStats.remote : audioTrackStats;
  const videoStats = isLocalPeer ? videoTrackStats == null ? void 0 : videoTrackStats.remote : videoTrackStats;
  return /* @__PURE__ */ React10.createElement(React10.Fragment, null, /* @__PURE__ */ React10.createElement(TrackPacketsLostRow, { label: "Packet Loss(V)", stats: videoStats }), /* @__PURE__ */ React10.createElement(TrackPacketsLostRow, { label: "Packet Loss(A)", stats: audioStats }), /* @__PURE__ */ React10.createElement(StatsRow, { show: isNotNullish(videoStats == null ? void 0 : videoStats.jitter), label: "Jitter(V)", value: (_a7 = videoStats == null ? void 0 : videoStats.jitter) == null ? void 0 : _a7.toFixed(4) }), /* @__PURE__ */ React10.createElement(StatsRow, { show: isNotNullish(audioStats == null ? void 0 : audioStats.jitter), label: "Jitter(A)", value: (_b7 = audioStats == null ? void 0 : audioStats.jitter) == null ? void 0 : _b7.toFixed(4) }));
};
var TrackPacketsLostRow = ({
  stats,
  label
}) => {
  const packetsLostRate = `${(stats == null ? void 0 : stats.packetsLostRate) ? stats.packetsLostRate.toFixed(2) : 0}/s`;
  return /* @__PURE__ */ React10.createElement(
    StatsRow,
    {
      show: isNotNullishAndNot0(stats == null ? void 0 : stats.packetsLost),
      label,
      value: `${stats == null ? void 0 : stats.packetsLost}(${packetsLostRate})`
    }
  );
};
var RawStatsRow = ({
  label = "",
  value = "",
  tooltip = "",
  show = true
}) => {
  const statsLabel = /* @__PURE__ */ React10.createElement(Stats.Label, { css: { fontWeight: !value ? "$semiBold" : "$regular" } }, label);
  return /* @__PURE__ */ React10.createElement(React10.Fragment, null, show ? /* @__PURE__ */ React10.createElement(Stats.Row, null, tooltip ? /* @__PURE__ */ React10.createElement(Tooltip, { side: "top", title: tooltip }, statsLabel) : statsLabel, value === "" ? /* @__PURE__ */ React10.createElement(Stats.Value, null) : /* @__PURE__ */ React10.createElement(Stats.Value, null, value)) : null);
};
var StatsRow = React10.memo(RawStatsRow);
function isNotNullishAndNot0(value) {
  return isNotNullish(value) && value !== 0;
}
function isNotNullish(value) {
  return value !== void 0 && value !== null;
}

// src/Checkbox/Checkbox.tsx
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
var CheckboxRoot = styled(CheckboxPrimitive.Root, {
  all: "unset",
  border: "1px solid $primary_default",
  backgroundColor: "$on_primary_high",
  width: "$8",
  height: "$8",
  borderRadius: "$0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "none",
  outline: "none",
  cursor: "pointer",
  "&:focus": {
    boxShadow: "none",
    outline: "none"
  },
  '&[data-state="checked"]': {
    backgroundColor: "$primary_default"
  }
});
var CheckboxIndicator = styled(CheckboxPrimitive.Indicator, {
  color: "$on_primary_high"
});
var Checkbox = {
  Root: CheckboxRoot,
  Indicator: CheckboxIndicator
};

// src/Label/Label.tsx
import * as LabelPrimitive from "@radix-ui/react-label";
var Label3 = styled(LabelPrimitive.Root, {
  fontFamily: "$sans",
  fontSize: "$md",
  color: "$on_primary_high"
});

// src/RadioGroup/RadioGroup.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
var RadioGroupRoot = styled(RadioGroupPrimitive.Root, {
  display: "flex",
  alignItems: "center"
});
var RadioGroupItem = styled(RadioGroupPrimitive.Item, {
  bg: "$on_primary_high",
  width: "$8",
  height: "$8",
  border: "1px solid $primary_default",
  cursor: "pointer",
  borderRadius: "$round",
  "&:focus": {
    boxShadow: "none",
    outline: "none"
  },
  '&[data-state="checked"]': {
    borderWidth: "$space$2",
    p: "$1"
  }
});
var RadioGroupIndicator = styled(RadioGroupPrimitive.Indicator, {
  bg: "$primary_default"
});
var RadioGroup = {
  Root: RadioGroupRoot,
  Item: RadioGroupItem,
  Indicator: RadioGroupIndicator
};

// src/Toast/Toast.tsx
import React11 from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { CrossIcon as CrossIcon2 } from "@100mslive/react-icons";
var getToastVariant = (base) => {
  return {
    borderLeftColor: base,
    borderLeft: 0,
    "&:before": {
      position: "absolute",
      top: "-1px",
      left: "-$4",
      width: "$8",
      borderLeft: `solid $space$px ${base}`,
      borderTop: `solid $space$px ${base}`,
      borderBottom: `solid $space$px ${base}`,
      borderTopLeftRadius: "$3",
      borderBottomLeftRadius: "$3",
      bg: base,
      content: " ",
      height: "100%",
      zIndex: 10
    },
    "@sm": {
      "&:before": {
        content: "none"
      },
      border: `solid $space$px ${base}`
    }
  };
};
var ToastRoot = styled(ToastPrimitives.Root, __spreadProps(__spreadValues({
  r: "$3",
  bg: "$surface_default",
  p: "$10",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  fontFamily: "$sans",
  border: "solid $space$px $border_bright",
  overflow: "hidden"
}, toastAnimation), {
  "@sm": {
    p: "$8 $11"
  },
  variants: {
    variant: {
      standard: getToastVariant("$secondary_default"),
      warning: getToastVariant("$alert_warning"),
      error: getToastVariant("$alert_error_default"),
      success: getToastVariant("$alert_success")
    }
  },
  defaultVariants: {
    variant: "standard"
  }
}));
var ToastTitle = styled(ToastPrimitives.Title, {
  fontSize: "$md",
  color: "$on_surface_high",
  fontWeight: "$semiBold",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
});
var ToastDescription = styled(ToastPrimitives.Description, {
  color: "$on_surface_medium"
});
var ToastClose = styled(ToastPrimitives.Close, {});
var ToastAction = styled(ToastPrimitives.Action, {
  cursor: "pointer",
  background: "none"
});
var ToastViewport = styled(ToastPrimitives.Viewport, {
  position: "fixed",
  bottom: 0,
  left: 0,
  display: "flex",
  flexDirection: "column",
  padding: "$8",
  gap: 10,
  width: 390,
  "@sm": {
    width: "100%",
    padding: "$6"
  },
  maxWidth: "100vw",
  margin: 0,
  listStyle: "none",
  zIndex: 1e3
});
var DefaultClose = ({ css: css2 }) => {
  return /* @__PURE__ */ React11.createElement(ToastClose, { css: css2, asChild: true }, /* @__PURE__ */ React11.createElement(IconButton, null, /* @__PURE__ */ React11.createElement(CrossIcon2, null)));
};
var HMSToast = (_a7) => {
  var _b7 = _a7, {
    title,
    description,
    isClosable = true,
    icon,
    action,
    inlineAction = false
  } = _b7, props = __objRest(_b7, [
    "title",
    "description",
    "isClosable",
    "icon",
    "action",
    "inlineAction"
  ]);
  return /* @__PURE__ */ React11.createElement(React11.Fragment, null, /* @__PURE__ */ React11.createElement(ToastRoot, __spreadValues({}, props), /* @__PURE__ */ React11.createElement(ToastTitle, null, /* @__PURE__ */ React11.createElement(Flex, { align: "center", css: { gap: "$4", flex: "1 1 0", minWidth: 0 } }, icon ? /* @__PURE__ */ React11.createElement(Box, { css: { w: "$10", h: "$10", alignSelf: "start", mt: "$2" } }, icon) : null, /* @__PURE__ */ React11.createElement(Text, { variant: "sub1", css: { c: "inherit", wordBreak: "break-word" } }, title)), isClosable ? /* @__PURE__ */ React11.createElement(DefaultClose, null) : null, !isClosable && inlineAction && action ? /* @__PURE__ */ React11.createElement(ToastAction, { altText: `${title}Action` }, action) : null), description ? /* @__PURE__ */ React11.createElement(ToastDescription, null, /* @__PURE__ */ React11.createElement(Text, { variant: "body1", css: { fontWeight: "$regular", c: "$on_surface_medium" } }, description)) : null, !inlineAction && action ? /* @__PURE__ */ React11.createElement(ToastAction, { altText: `${title}Action`, css: { mt: "$10" } }, action) : null));
};
var Toast = {
  Provider: ToastPrimitives.Provider,
  Root: ToastRoot,
  Title: ToastTitle,
  Description: ToastDescription,
  Close: DefaultClose,
  Action: ToastAction,
  Viewport: ToastViewport,
  HMSToast
};

// src/Accordion/Accordion.tsx
import React12 from "react";
import * as BaseAccordion from "@radix-ui/react-accordion";
import { ChevronUpIcon } from "@100mslive/react-icons";
var StyledAccordion = styled(BaseAccordion.Root, {});
var StyledItem = styled(BaseAccordion.Item, {
  marginTop: "$px",
  display: "block",
  "&:first-child": {
    marginTop: 0,
    borderTopLeftRadius: "$0",
    borderTopRightRadius: "$0"
  },
  "&:last-child": {
    borderBottomLeftRadius: "$4",
    borderBottomRightRadius: "$4"
  }
});
var StyledHeader = styled(BaseAccordion.Header, {
  all: "unset",
  display: "flex",
  fontFamily: "$sans"
});
var StyledTrigger3 = styled(BaseAccordion.Trigger, {
  all: "unset",
  fontFamily: "$sans",
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "$md",
  lineHeight: "$md",
  color: "$on_surface_high"
});
var StyledContent3 = styled(BaseAccordion.Content, {
  display: "contents",
  fontSize: "$md",
  fontFamily: "$sans",
  color: "$on_surface_medium",
  '&[data-state="open"]': {
    animation: `${slideDown("--radix-accordion-content-height")} 300ms cubic-bezier(0.87, 0, 0.13, 1) forwards`
  },
  '&[data-state="closed"]': {
    animation: `${slideUp("--radix-accordion-content-height")} 300ms cubic-bezier(0.87, 0, 0.13, 1) forwards`
  }
});
var StyledChevron = styled(ChevronUpIcon, {
  color: "$on_primary_high",
  transition: "transform 300ms cubic-bezier(0.87, 0, 0.13, 1)",
  "[data-state=closed] &": { transform: "rotate(180deg)" }
});
var AccordionRoot = StyledAccordion;
var AccordionItem = StyledItem;
var AccordionHeader = React12.forwardRef((_a7, forwardedRef) => {
  var _b7 = _a7, { children, iconStyles, css: css2 } = _b7, props = __objRest(_b7, ["children", "iconStyles", "css"]);
  return /* @__PURE__ */ React12.createElement(StyledHeader, { css: css2 }, /* @__PURE__ */ React12.createElement(StyledTrigger3, __spreadProps(__spreadValues({}, props), { ref: forwardedRef }), children, /* @__PURE__ */ React12.createElement(StyledChevron, { "aria-hidden": true, css: iconStyles })));
});
var AccordionContent = React12.forwardRef(
  (_a7, forwardedRef) => {
    var _b7 = _a7, { children, contentStyles } = _b7, props = __objRest(_b7, ["children", "contentStyles"]);
    return /* @__PURE__ */ React12.createElement(StyledContent3, __spreadProps(__spreadValues({}, props), { ref: forwardedRef }), /* @__PURE__ */ React12.createElement(Box, { css: contentStyles }, children));
  }
);

// src/Accordion/index.ts
var Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Content: AccordionContent,
  Header: AccordionHeader
};

// src/Fieldset/Fieldset.tsx
var StyledFieldset = styled("fieldset", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: "none",
  backgroundColor: "$transparent"
});
var Fieldset = StyledFieldset;

// src/Footer/Footer.tsx
var Root19 = styled(Flex, {
  justifyContent: "space-between",
  alignItems: "center",
  py: "$4",
  position: "relative",
  height: "100%",
  "@md": { flexWrap: "wrap", gap: "$4" }
});
var Left = styled(Flex, {
  alignItems: "center",
  position: "absolute",
  left: "$10",
  gap: "$8",
  "@md": {
    position: "unset",
    justifyContent: "center",
    w: "100%"
  }
});
var Center = styled(Flex, {
  w: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: "$8"
});
var Right = styled(Flex, {
  alignItems: "center",
  position: "absolute",
  right: "$10",
  gap: "$8",
  "@md": {
    display: "none"
  }
});
var Footer = {
  Root: Root19,
  Left,
  Center,
  Right
};

// src/ReactSelect/index.ts
var ReactSelect_exports = {};
__export(ReactSelect_exports, {
  Select: () => Select2
});

// src/ReactSelect/ReactSelect.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
var StyledRoot = styled(SelectPrimitive.Root, {});
var StyledTrigger4 = styled(SelectPrimitive.SelectTrigger, {
  all: "unset",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "$2",
  padding: "$8",
  lineHeight: "$px",
  gap: "$8",
  backgroundColor: "$secondary_default",
  color: "$on_primary_high",
  fontSize: "$8",
  cursor: "pointer",
  r: "$1"
});
var StyledContent4 = styled(SelectPrimitive.Content, {
  overflow: "hidden",
  backgroundColor: "$surface_bright",
  r: "$1",
  h: "$80"
});
var StyledViewport = styled(SelectPrimitive.Viewport, {
  padding: "$3"
});
var StyledItem2 = styled(SelectPrimitive.Item, {
  all: "unset",
  fontSize: "$7",
  r: "$1",
  display: "flex",
  p: "$4 $8",
  w: "$52",
  position: "relative",
  userSelect: "none",
  cursor: "pointer"
});
var StyledLabel = styled(SelectPrimitive.Label, {
  p: "$4 $8",
  lineHeight: "$10",
  color: "$on_primary_high"
});
var StyledSeparator = styled(SelectPrimitive.Separator, {
  height: "$px",
  backgroundColor: "$on_primary_high",
  margin: "$4",
  opacity: 0.4
});
var StyledItemIndicator = styled(SelectPrimitive.ItemIndicator, {
  position: "absolute",
  right: "$8",
  width: "$8",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "$on_primary_high"
});
var scrollButtonStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: "$4",
  backgroundColor: "$surface_bright",
  color: "$on_primary_high",
  cursor: "default"
};
var StyledScrollUpButton = styled(SelectPrimitive.ScrollUpButton, scrollButtonStyles);
var StyledScrollDownButton = styled(SelectPrimitive.ScrollDownButton, scrollButtonStyles);
var Select2 = {
  Root: StyledRoot,
  Trigger: StyledTrigger4,
  Content: StyledContent4,
  Viewport: StyledViewport,
  Item: StyledItem2,
  Label: StyledLabel,
  Separator: StyledSeparator,
  ItemIndicator: StyledItemIndicator,
  ScrollUpButton: StyledScrollUpButton,
  ScrollDownButton: StyledScrollDownButton,
  Value: SelectPrimitive.Value,
  Icon: SelectPrimitive.Icon,
  ItemText: SelectPrimitive.ItemText,
  Group: SelectPrimitive.Group
};

// src/Tabs/Tabs.tsx
import * as TabsPrimitive from "@radix-ui/react-tabs";
var StyledTabsRoot = styled(TabsPrimitive.Root, {
  display: "flex"
});
var StyledList = styled(TabsPrimitive.List, {
  flexShrink: 0,
  display: "flex"
});
var StyledTrigger5 = styled(TabsPrimitive.Trigger, {
  all: "unset",
  fontFamily: "$sans",
  p: "$8",
  display: "flex",
  alignItems: "center",
  fontSize: "$sm",
  lineHeight: "$sm",
  color: "$on_surface_high",
  userSelect: "none",
  cursor: "pointer",
  '&[data-state="active"]': {
    bg: "$surface_brighter",
    r: "$1"
  }
});
var StyledContent5 = styled(TabsPrimitive.Content, {
  flex: "1 1 0",
  padding: "$10",
  outline: "none"
});
var Tabs = {
  Root: StyledTabsRoot,
  List: StyledList,
  Content: StyledContent5,
  Trigger: StyledTrigger5
};

// src/QRCode/QRCode.tsx
import React13 from "react";
import { QRCodeSVG } from "qrcode.react";
var QRCode = (props) => {
  return /* @__PURE__ */ React13.createElement(QRCodeSVG, __spreadValues({ style: { width: "100%", height: "100%" } }, props));
};

// src/Link/Link.tsx
import React14 from "react";
import * as icons from "@100mslive/react-icons";
var LinkComponent = styled("a", {
  textDecoration: "none",
  display: "flex",
  alignItems: "center",
  gap: "$5",
  variants: {
    color: {
      highEmp: {
        color: "$on_surface_high",
        "&:hover": {
          color: "$on_surface_medium"
        }
      },
      primary: {
        color: "$primary_default",
        "&:hover": {
          color: "$primary_bright"
        }
      }
    }
  }
});
var Link = (_a7) => {
  var _b7 = _a7, { iconSide = "left", icon, color = "primary", children } = _b7, rest = __objRest(_b7, ["iconSide", "icon", "color", "children"]);
  const Icon2 = icon ? icons[icon] : React14.Fragment;
  const renderedIcon = icon ? /* @__PURE__ */ React14.createElement(Flex, { as: "span" }, /* @__PURE__ */ React14.createElement(Icon2, { height: 13.33, width: 13.33 }), " ") : null;
  return /* @__PURE__ */ React14.createElement(LinkComponent, __spreadProps(__spreadValues({}, rest), { color }), iconSide === "left" && renderedIcon, /* @__PURE__ */ React14.createElement(Text, { as: "span", variant: "body2", css: { color: "inherit" } }, children), iconSide === "right" && renderedIcon);
};

// src/Collapsible/Collapsible.tsx
import { Content as Content8, Root as Root22, Trigger as Trigger7 } from "@radix-ui/react-collapsible";
var CollapsibleRoot = styled(Root22, {});
var CollapsibleTrigger = styled(Trigger7, {
  cursor: "pointer",
  appearance: "none !important",
  "&:focus": {
    outline: "none"
  },
  "&:focus-visible": {
    boxShadow: "0 0 0 3px $colors$primary_default"
  }
});
var CollapsibleContent = styled(Content8, {
  w: "$80",
  r: "$1",
  overflowY: "auto",
  '&[data-state="open"]': {
    animation: `${slideDown("--radix-collapsible-content-height")} 300ms cubic-bezier(0.87, 0, 0.13, 1) forwards`
  },
  '&[data-state="closed"]': {
    animation: `${slideUp("--radix-collapsible-content-height")} 300ms cubic-bezier(0.87, 0, 0.13, 1) forwards`
  }
});
var Collapsible = {
  Root: CollapsibleRoot,
  Trigger: CollapsibleTrigger,
  Content: CollapsibleContent
};

// src/Prebuilt/App.tsx
import React61, { Suspense as Suspense2, useEffect as useEffect29, useRef as useRef10 } from "react";
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes, useParams as useParams5 } from "react-router-dom";
import {
  HMSReactiveStore,
  HMSRoomProvider,
  selectIsConnectedToRoom as selectIsConnectedToRoom6,
  useHMSActions as useHMSActions20,
  useHMSStore as useHMSStore30
} from "@100mslive/react-sdk";

// src/Prebuilt/components/AppData/AppData.jsx
import React15, { useEffect as useEffect3 } from "react";
import {
  HMSRoomState,
  selectAvailableRoleNames,
  selectFullAppData,
  selectHLSState,
  selectIsConnectedToRoom,
  selectLocalPeerRoleName,
  selectRolesMap,
  selectRoomState,
  selectRTMPState,
  useHMSActions as useHMSActions3,
  useHMSStore as useHMSStore4,
  useRecordingStreaming
} from "@100mslive/react-sdk";

// src/Prebuilt/components/init/initUtils.js
function canPublishAV(role) {
  const params = role == null ? void 0 : role.publishParams;
  if (params == null ? void 0 : params.allowed) {
    return params.allowed.includes("video") || params.allowed.includes("audio");
  }
  return false;
}
var normalizeAppPolicyConfig = (roleNames, rolesMap, appPolicyConfig = {}) => {
  const newConfig = Object.assign({}, appPolicyConfig);
  roleNames.forEach((roleName) => {
    var _a7;
    if (!newConfig[roleName]) {
      newConfig[roleName] = {};
    }
    const subscribedRoles = ((_a7 = rolesMap[roleName].subscribeParams) == null ? void 0 : _a7.subscribeToRoles) || [];
    const isNotSubscribingOrSubscribingToSelf = subscribedRoles.length === 0 || subscribedRoles.length === 1 && subscribedRoles[0] === roleName;
    if (!newConfig[roleName].center) {
      const publishingRoleNames = roleNames.filter(
        (roleName2) => canPublishAV(rolesMap[roleName2]) && subscribedRoles.includes(roleName2)
      );
      if (isNotSubscribingOrSubscribingToSelf) {
        newConfig[roleName].center = [roleName];
      } else {
        newConfig[roleName].center = publishingRoleNames.filter((rName) => rName !== roleName);
      }
    }
    if (!newConfig[roleName].sidepane) {
      if (isNotSubscribingOrSubscribingToSelf) {
        newConfig[roleName].sidepane = [];
      } else {
        newConfig[roleName].sidepane = canPublishAV(rolesMap[roleName]) ? [roleName] : [];
      }
    }
    if (!newConfig[roleName].selfRoleChangeTo) {
      newConfig[roleName].selfRoleChangeTo = roleNames;
    }
    if (!newConfig[roleName].remoteRoleChangeFor) {
      newConfig[roleName].remoteRoleChangeFor = roleNames;
    }
  });
  return newConfig;
};

// src/Prebuilt/components/hooks/useUserPreferences.jsx
import { useState as useState3 } from "react";
import { useLocalStorage } from "react-use";
var UserPreferencesKeys = {
  PREVIEW: "preview",
  NOTIFICATIONS: "notifications",
  UI_SETTINGS: "uiSettings",
  RTMP_URLS: "rtmpUrls"
};
var defaultPreviewPreference = {
  name: "",
  isAudioMuted: false,
  isVideoMuted: false
};
var useUserPreferences = (key, defaultPreference) => {
  const [localStorageValue, setStorageValue] = useLocalStorage(key, defaultPreference);
  const [preference, setPreference] = useState3(localStorageValue || defaultPreference);
  const changePreference = (value) => {
    setPreference(value);
    setStorageValue(value);
  };
  return [preference, changePreference];
};

// src/Prebuilt/components/AppData/useSidepane.js
import { useCallback as useCallback3 } from "react";
import { selectAppData, useHMSActions, useHMSStore as useHMSStore2, useHMSVanillaStore } from "@100mslive/react-sdk";

// src/Prebuilt/common/constants.js
import { parsedUserAgent } from "@100mslive/react-sdk";
var DEFAULT_WAITING_VIEWER_ROLE = "waiting-room";
var QUERY_PARAM_PREVIEW_AS_ROLE = "preview_as_role";
var UI_MODE_GRID = "grid";
var MAX_TOASTS = 5;
var RTMP_RECORD_RESOLUTION_MIN = 480;
var RTMP_RECORD_RESOLUTION_MAX = 1280;
var RTMP_RECORD_DEFAULT_RESOLUTION = {
  width: 1280,
  height: 720
};
var EMOJI_REACTION_TYPE = "EMOJI_REACTION";
var CHAT_SELECTOR = {
  PEER_ID: "peer_id",
  ROLE: "role"
};
var APP_DATA = {
  uiSettings: "uiSettings",
  chatOpen: "chatOpen",
  chatSelector: "chatSelector",
  chatDraft: "chatDraft",
  appConfig: "appConfig",
  sidePane: "sidePane",
  hlsStats: "hlsStats",
  waitingViewerRole: "waitingViewerRole",
  subscribedNotifications: "subscribedNotifications",
  logo: "logo",
  tokenEndpoint: "tokenEndpoint",
  appLayout: "appLayout",
  hlsStarted: "hlsStarted",
  rtmpStarted: "rtmpStarted",
  recordingStarted: "recordingStarted",
  embedConfig: "embedConfig",
  pinnedTrackId: "pinnedTrackId",
  dropdownList: "dropdownList",
  authToken: "authToken",
  pdfConfig: "pdfConfig",
  minimiseInset: "minimiseInset",
  activeScreensharePeerId: "activeScreensharePeerId",
  disableNotificiations: "disableNotificiations"
};
var UI_SETTINGS = {
  isAudioOnly: "isAudioOnly",
  maxTileCount: "maxTileCount",
  uiViewMode: "uiViewMode",
  showStatsOnTiles: "showStatsOnTiles",
  enableAmbientMusic: "enableAmbientMusic",
  mirrorLocalVideo: "mirrorLocalVideo"
};
var SIDE_PANE_OPTIONS = {
  PARTICIPANTS: "Participants",
  CHAT: "Chat",
  STREAMING: "STREAMING"
};
var SUBSCRIBED_NOTIFICATIONS = {
  PEER_JOINED: "PEER_JOINED",
  PEER_LEFT: "PEER_LEFT",
  METADATA_UPDATED: "METADATA_UPDATED",
  NEW_MESSAGE: "NEW_MESSAGE",
  ERROR: "ERROR"
};
var REMOTE_STOP_SCREENSHARE_TYPE = "REMOTE_STOP_SCREENSHARE";
var _a, _b;
var isChrome = ((_b = (_a = parsedUserAgent.getBrowser()) == null ? void 0 : _a.name) == null ? void 0 : _b.toLowerCase()) === "chrome";
var _a2, _b2;
var isFirefox = ((_b2 = (_a2 = parsedUserAgent.getBrowser()) == null ? void 0 : _a2.name) == null ? void 0 : _b2.toLowerCase()) === "firefox";
var _a3, _b3;
var isSafari = ((_b3 = (_a3 = parsedUserAgent.getBrowser()) == null ? void 0 : _a3.name) == null ? void 0 : _b3.toLowerCase()) === "safari";
var _a4, _b4;
var isIOS = ((_b4 = (_a4 = parsedUserAgent.getOS()) == null ? void 0 : _a4.name) == null ? void 0 : _b4.toLowerCase()) === "ios";
var _a5, _b5;
var isMacOS = ((_b5 = (_a5 = parsedUserAgent.getOS()) == null ? void 0 : _a5.name) == null ? void 0 : _b5.toLowerCase()) === "mac os";
var _a6, _b6;
var isAndroid = ((_b6 = (_a6 = parsedUserAgent.getOS()) == null ? void 0 : _a6.name) == null ? void 0 : _b6.toLowerCase()) === "android";
var isIPadOS = false;
var SESSION_STORE_KEY = {
  TRANSCRIPTION_STATE: "transcriptionState",
  PINNED_MESSAGE: "pinnedMessage",
  SPOTLIGHT: "spotlight"
};
var ROLE_CHANGE_DECLINED = "role_change_declined";

// src/Prebuilt/components/AppData/useSidepane.js
var useIsSidepaneTypeOpen = (sidepaneType) => {
  if (!sidepaneType) {
    throw Error("Pass one of the side pane options");
  }
  return useHMSStore2(selectAppData(APP_DATA.sidePane)) === sidepaneType;
};
var useSidepaneState = () => {
  const sidePane = useHMSStore2(selectAppData(APP_DATA.sidePane));
  return sidePane;
};
var useSidepaneToggle = (sidepaneType) => {
  const hmsActions = useHMSActions();
  const vanillaStore = useHMSVanillaStore();
  const toggleSidepane = useCallback3(() => {
    const isOpen = vanillaStore.getState(selectAppData(APP_DATA.sidePane)) === sidepaneType;
    hmsActions.setAppData(APP_DATA.sidePane, !isOpen ? sidepaneType : "");
  }, [vanillaStore, hmsActions, sidepaneType]);
  return toggleSidepane;
};
var useSidepaneReset = () => {
  const hmsActions = useHMSActions();
  const resetSidepane = useCallback3(() => {
    hmsActions.setAppData(APP_DATA.sidePane, "");
  }, [hmsActions]);
  return resetSidepane;
};

// src/Prebuilt/components/AppData/useUISettings.js
import { useCallback as useCallback4 } from "react";
import {
  selectAppData as selectAppData2,
  selectAppDataByPath,
  selectAudioTrackByPeerID,
  selectSessionStore,
  selectTrackByID,
  selectVideoTrackByPeerID,
  useHMSActions as useHMSActions2,
  useHMSStore as useHMSStore3,
  useHMSVanillaStore as useHMSVanillaStore2
} from "@100mslive/react-sdk";
var useUISettings = (uiSettingKey) => {
  const uiSettings = useHMSStore3(selectAppDataByPath(APP_DATA.uiSettings, uiSettingKey));
  return uiSettings;
};
var useSetUiSettings = (uiSettingKey) => {
  const value = useUISettings(uiSettingKey);
  const setValue = useSetAppData({
    key1: APP_DATA.uiSettings,
    key2: uiSettingKey
  });
  return [value, setValue];
};
var useWaitingViewerRole = () => {
  return useHMSStore3(selectAppData2(APP_DATA.waitingViewerRole));
};
var useTokenEndpoint = () => {
  return useHMSStore3(selectAppData2(APP_DATA.tokenEndpoint));
};
var useAuthToken = () => {
  return useHMSStore3(selectAppData2(APP_DATA.authToken));
};
var useUrlToEmbed = () => {
  var _a7;
  return (_a7 = useHMSStore3(selectAppData2(APP_DATA.embedConfig))) == null ? void 0 : _a7.url;
};
var usePDFAnnotator = () => {
  var _a7;
  return (_a7 = useHMSStore3(selectAppData2(APP_DATA.pdfConfig))) == null ? void 0 : _a7.state;
};
var usePinnedTrack = () => {
  var _a7, _b7;
  const pinnedTrackId = useHMSStore3(selectAppData2(APP_DATA.pinnedTrackId));
  const spotlightPeerId = useHMSStore3(selectSessionStore(SESSION_STORE_KEY.SPOTLIGHT));
  const spotlightVideoTrackId = (_a7 = useHMSStore3(selectVideoTrackByPeerID(spotlightPeerId))) == null ? void 0 : _a7.id;
  const spotlightAudioTrackId = (_b7 = useHMSStore3(selectAudioTrackByPeerID(spotlightPeerId))) == null ? void 0 : _b7.id;
  return useHMSStore3(selectTrackByID(pinnedTrackId || spotlightVideoTrackId || spotlightAudioTrackId));
};
var useSubscribedNotifications = (notificationKey) => {
  const notificationPreference = useHMSStore3(selectAppDataByPath(APP_DATA.subscribedNotifications, notificationKey));
  return notificationPreference;
};
var useIsNotificationDisabled = () => {
  const notificationPreference = useHMSStore3(selectAppDataByPath(APP_DATA.disableNotificiations));
  return notificationPreference;
};
var useSetSubscribedNotifications = (notificationKey) => {
  const value = useSubscribedNotifications(notificationKey);
  const setValue = useSetAppData({
    key1: APP_DATA.subscribedNotifications,
    key2: notificationKey
  });
  return [value, setValue];
};
var useSubscribeChatSelector = (chatSelectorKey) => {
  const chatSelectorPreference = useHMSStore3(selectAppDataByPath(APP_DATA.chatSelector, chatSelectorKey));
  return chatSelectorPreference;
};
var useSetSubscribedChatSelector = (chatSelectorKey) => {
  const value = useSubscribeChatSelector(chatSelectorKey);
  const setValue = useSetAppData({
    key1: APP_DATA.chatSelector,
    key2: chatSelectorKey
  });
  return [value, setValue];
};
var useSetAppDataByKey = (appDataKey) => {
  const value = useHMSStore3(selectAppData2(appDataKey));
  const actions = useHMSActions2();
  const setValue = useCallback4(
    (value2) => {
      actions.setAppData(appDataKey, value2);
    },
    [actions, appDataKey]
  );
  return [value, setValue];
};
var useSetAppData = ({ key1, key2 }) => {
  const actions = useHMSActions2();
  const store = useHMSVanillaStore2();
  const [, setPreferences] = useUserPreferences(UserPreferencesKeys.UI_SETTINGS);
  const setValue = useCallback4(
    (value) => {
      if (!key1) {
        return;
      }
      actions.setAppData(
        key1,
        key2 ? {
          [key2]: value
        } : value,
        true
      );
      const appData = store.getState(selectAppData2());
      setPreferences(__spreadProps(__spreadValues({}, appData.uiSettings), {
        subscribedNotifications: appData.subscribedNotifications
      }));
    },
    [actions, key1, key2, store, setPreferences]
  );
  return setValue;
};

// src/Prebuilt/components/AppData/AppData.jsx
var getAppDetails = (appDetails) => {
  try {
    return !appDetails ? {} : JSON.parse(appDetails);
  } catch (error) {
    return {};
  }
};
var initialAppData = {
  [APP_DATA.uiSettings]: {
    [UI_SETTINGS.isAudioOnly]: false,
    [UI_SETTINGS.maxTileCount]: 9,
    [UI_SETTINGS.showStatsOnTiles]: false,
    [UI_SETTINGS.enableAmbientMusic]: false,
    [UI_SETTINGS.uiViewMode]: UI_MODE_GRID,
    [UI_SETTINGS.mirrorLocalVideo]: true
  },
  [APP_DATA.subscribedNotifications]: {
    PEER_JOINED: false,
    PEER_LEFT: false,
    NEW_MESSAGE: true,
    ERROR: true,
    METADATA_UPDATED: true
  },
  [APP_DATA.chatOpen]: false,
  [APP_DATA.chatSelector]: {
    [CHAT_SELECTOR.ROLE]: "",
    [CHAT_SELECTOR.PEER_ID]: ""
  },
  [APP_DATA.chatDraft]: "",
  [APP_DATA.sidePane]: "",
  [APP_DATA.hlsStarted]: false,
  [APP_DATA.rtmpStarted]: false,
  [APP_DATA.recordingStarted]: false,
  [APP_DATA.waitingViewerRole]: DEFAULT_WAITING_VIEWER_ROLE,
  [APP_DATA.dropdownList]: [],
  [APP_DATA.authToken]: "",
  [APP_DATA.minimiseInset]: false,
  [APP_DATA.activeScreensharePeerId]: "",
  [APP_DATA.disableNotificiations]: false
};
var AppData = React15.memo(({ appDetails, tokenEndpoint }) => {
  const hmsActions = useHMSActions3();
  const isConnected = useHMSStore4(selectIsConnectedToRoom);
  const sidePane = useSidepaneState();
  const resetSidePane = useSidepaneReset();
  const [preferences = {}] = useUserPreferences(UserPreferencesKeys.UI_SETTINGS);
  const roleNames = useHMSStore4(selectAvailableRoleNames);
  const rolesMap = useHMSStore4(selectRolesMap);
  const localPeerRole = useHMSStore4(selectLocalPeerRoleName);
  const appData = useHMSStore4(selectFullAppData);
  useEffect3(() => {
    if (!isConnected && sidePane && sidePane !== SIDE_PANE_OPTIONS.PARTICIPANTS) {
      resetSidePane();
    }
  }, [isConnected, sidePane, resetSidePane]);
  useEffect3(() => {
    hmsActions.initAppData(__spreadValues(__spreadValues({}, initialAppData), appData));
    hmsActions.setFrameworkInfo({
      type: "react-web",
      isPrebuilt: true,
      version: React15.version
    });
  }, [hmsActions]);
  useEffect3(() => {
    const uiSettings = preferences || {};
    const updatedSettings = __spreadProps(__spreadValues({}, uiSettings), {
      [UI_SETTINGS.uiViewMode]: uiSettings.uiViewMode || UI_MODE_GRID
    });
    hmsActions.setAppData(APP_DATA.uiSettings, updatedSettings, true);
  }, [preferences, hmsActions]);
  useEffect3(() => {
    const appData2 = {
      [APP_DATA.tokenEndpoint]: tokenEndpoint,
      [APP_DATA.appConfig]: getAppDetails(appDetails)
    };
    for (const key in appData2) {
      hmsActions.setAppData([key], appData2[key]);
    }
  }, [appDetails, tokenEndpoint, hmsActions]);
  useEffect3(() => {
    if (!preferences.subscribedNotifications) {
      return;
    }
    hmsActions.setAppData(APP_DATA.subscribedNotifications, preferences.subscribedNotifications, true);
  }, [preferences.subscribedNotifications, hmsActions]);
  useEffect3(() => {
    if (localPeerRole) {
      const config2 = normalizeAppPolicyConfig(roleNames, rolesMap);
      hmsActions.setAppData(APP_DATA.appLayout, config2[localPeerRole]);
    }
  }, [roleNames, rolesMap, localPeerRole, hmsActions]);
  return /* @__PURE__ */ React15.createElement(ResetStreamingStart, null);
});
var ResetStreamingStart = () => {
  const { isHLSRunning, isRTMPRunning, isBrowserRecordingOn } = useRecordingStreaming();
  const hlsError = useHMSStore4(selectHLSState).error;
  const rtmpError = useHMSStore4(selectRTMPState).error;
  const roomState = useHMSStore4(selectRoomState);
  const [hlsStarted, setHLSStarted] = useSetAppDataByKey(APP_DATA.hlsStarted);
  const [recordingStarted, setRecordingStarted] = useSetAppDataByKey(APP_DATA.recordingStarted);
  const [rtmpStarted, setRTMPStarted] = useSetAppDataByKey(APP_DATA.rtmpStarted);
  const toggleStreaming = useSidepaneToggle(SIDE_PANE_OPTIONS.STREAMING);
  const isStreamingOpen = useIsSidepaneTypeOpen(SIDE_PANE_OPTIONS.STREAMING);
  useEffect3(() => {
    if (isBrowserRecordingOn && recordingStarted) {
      setRecordingStarted(false);
    }
  }, [isBrowserRecordingOn, recordingStarted, setRecordingStarted]);
  useEffect3(() => {
    if (roomState === HMSRoomState.Disconnected) {
      setHLSStarted(false);
      setRecordingStarted(false);
      setRTMPStarted(false);
    }
  }, [roomState, setHLSStarted, setRTMPStarted, setRecordingStarted]);
  useEffect3(() => {
    if (isHLSRunning || hlsError) {
      if (hlsStarted) {
        setHLSStarted(false);
        if (isStreamingOpen) {
          toggleStreaming();
        }
      }
    }
  }, [isHLSRunning, hlsStarted, setHLSStarted, hlsError, isStreamingOpen, toggleStreaming]);
  useEffect3(() => {
    if (isRTMPRunning || rtmpError || isBrowserRecordingOn) {
      if (rtmpStarted) {
        setRTMPStarted(false);
        if (isStreamingOpen) {
          toggleStreaming();
        }
      }
    }
  }, [isRTMPRunning, setRTMPStarted, rtmpStarted, rtmpError, isBrowserRecordingOn, isStreamingOpen, toggleStreaming]);
  return null;
};

// src/Prebuilt/components/AuthToken.jsx
import React18, { useEffect as useEffect4, useState as useState4 } from "react";
import { useHMSActions as useHMSActions4 } from "@100mslive/react-sdk";

// src/Prebuilt/AppContext.tsx
import React16, { useContext } from "react";
var HMSPrebuiltContext = React16.createContext({
  roomCode: "",
  userName: "",
  userId: "",
  endpoints: {},
  onLeave: void 0
});
HMSPrebuiltContext.displayName = "HMSPrebuiltContext";
var useHMSPrebuiltContext = () => {
  const context = useContext(HMSPrebuiltContext);
  if (!context) {
    throw Error("Make sure HMSPrebuiltContext.Provider is present at the top level of your application");
  }
  return context;
};

// src/Prebuilt/primitives/DialogContent.jsx
import React17, { useRef as useRef3 } from "react";
import { CheckIcon, CloudUploadIcon, CrossIcon as CrossIcon3 } from "@100mslive/react-icons";
var DialogContent = (_a7) => {
  var _b7 = _a7, { Icon: Icon2, title, closeable = true, children, css: css2, iconCSS = {} } = _b7, props = __objRest(_b7, ["Icon", "title", "closeable", "children", "css", "iconCSS"]);
  return /* @__PURE__ */ React17.createElement(Dialog.Portal, null, /* @__PURE__ */ React17.createElement(Dialog.Overlay, null), /* @__PURE__ */ React17.createElement(Dialog.Content, __spreadValues({ css: __spreadValues({ width: "min(600px, 100%)" }, css2) }, props), /* @__PURE__ */ React17.createElement(Dialog.Title, null, /* @__PURE__ */ React17.createElement(Flex, { justify: "between" }, /* @__PURE__ */ React17.createElement(Flex, { align: "center", css: { mb: "$1" } }, Icon2 ? /* @__PURE__ */ React17.createElement(Box, { css: __spreadValues({ mr: "$2", color: "$on_primary_high" }, iconCSS) }, /* @__PURE__ */ React17.createElement(Icon2, null)) : null, /* @__PURE__ */ React17.createElement(Text, { variant: "h6", inline: true }, title)), closeable && /* @__PURE__ */ React17.createElement(Dialog.DefaultClose, { "data-testid": "dialoge_cross_icon" }))), /* @__PURE__ */ React17.createElement(HorizontalDivider, { css: { mt: "0.8rem" } }), /* @__PURE__ */ React17.createElement(Box, null, children)));
};
var ErrorDialog = (_a7) => {
  var _b7 = _a7, { open = true, onOpenChange, title, children } = _b7, props = __objRest(_b7, ["open", "onOpenChange", "title", "children"]);
  return /* @__PURE__ */ React17.createElement(Dialog.Root, { open, onOpenChange }, /* @__PURE__ */ React17.createElement(
    DialogContent,
    __spreadValues({
      Icon: CrossIcon3,
      title,
      onInteractOutside: (e) => e.preventDefault(),
      onEscapeKeyDown: (e) => e.preventDefault(),
      onPointerDownOutside: (e) => e.preventDefault(),
      closeable: false,
      iconCSS: { color: "$alert_error_default" }
    }, props),
    /* @__PURE__ */ React17.createElement(Box, { css: { mt: "$lg" } }, children)
  ));
};
var RequestDialog = ({ open = true, onOpenChange, title, body, actionText = "Accept", onAction, Icon: Icon2 }) => /* @__PURE__ */ React17.createElement(Dialog.Root, { open, onOpenChange }, /* @__PURE__ */ React17.createElement(Dialog.Portal, null, /* @__PURE__ */ React17.createElement(Dialog.Overlay, null), /* @__PURE__ */ React17.createElement(Dialog.Content, { css: { width: "min(400px,80%)", p: "$10" } }, /* @__PURE__ */ React17.createElement(Dialog.Title, { css: { p: 0, display: "flex", flexDirection: "row", gap: "$md" } }, Icon2 ? Icon2 : null, /* @__PURE__ */ React17.createElement(Text, { variant: "h6" }, title)), typeof body === "string" ? /* @__PURE__ */ React17.createElement(
  Text,
  {
    variant: "md",
    css: {
      fontWeight: 400,
      mt: "$4",
      mb: "$10",
      c: "$on_surface_medium"
    }
  },
  body
) : /* @__PURE__ */ React17.createElement(Box, { css: { mt: "$4", mb: "$10" } }, body), /* @__PURE__ */ React17.createElement(Flex, { justify: "center", align: "center", css: { width: "100%", gap: "$md" } }, /* @__PURE__ */ React17.createElement(Box, { css: { width: "50%" } }, /* @__PURE__ */ React17.createElement(Dialog.Close, { css: { width: "100%" } }, /* @__PURE__ */ React17.createElement(Button, { variant: "standard", outlined: true, css: { width: "100%" } }, "Cancel"))), /* @__PURE__ */ React17.createElement(Box, { css: { width: "50%" } }, /* @__PURE__ */ React17.createElement(Button, { variant: "primary", css: { width: "100%" }, onClick: onAction }, actionText))))));
var DialogRow = ({ children, breakSm = false, css: css2, justify = "between" }) => {
  let finalCSS = {
    margin: "$10 0",
    w: "100%"
  };
  if (breakSm) {
    finalCSS["@sm"] = {
      flexDirection: "column",
      alignItems: "flex-start"
    };
  }
  if (css2) {
    finalCSS = Object.assign(finalCSS, css2);
  }
  return /* @__PURE__ */ React17.createElement(Flex, { align: "center", justify, css: finalCSS }, children);
};
var DialogCol = (_a7) => {
  var _b7 = _a7, { children, breakSm = false, css: css2, align = "center", justify = "between" } = _b7, props = __objRest(_b7, ["children", "breakSm", "css", "align", "justify"]);
  let finalCSS = {
    margin: "$10 0",
    w: "100%"
  };
  if (breakSm) {
    finalCSS["@sm"] = {
      alignItems: "flex-start"
    };
  }
  if (css2) {
    finalCSS = Object.assign(finalCSS, css2);
  }
  return /* @__PURE__ */ React17.createElement(Flex, __spreadValues({ direction: "column", align, justify, css: finalCSS }, props), children);
};
var DialogSelect = (_a7) => {
  var _b7 = _a7, { title, options, keyField, labelField, selected, onChange } = _b7, props = __objRest(_b7, ["title", "options", "keyField", "labelField", "selected", "onChange"]);
  return /* @__PURE__ */ React17.createElement(DialogRow, { breakSm: true }, /* @__PURE__ */ React17.createElement(Label3, null, title), /* @__PURE__ */ React17.createElement(Select.Root, __spreadValues({ "data-testid": `dialog_select_${title}`, css: { width: "70%", "@sm": { width: "100%" } } }, props), /* @__PURE__ */ React17.createElement(Select.DefaultDownIcon, null), /* @__PURE__ */ React17.createElement(Select.Select, { onChange: (e) => onChange(e.target.value), value: selected, css: { width: "100%" } }, options.map((option) => {
    const id = keyField ? option[keyField] : option;
    const label = labelField ? option[labelField] : option;
    return /* @__PURE__ */ React17.createElement("option", { value: id, key: id }, label);
  }))));
};
var DialogInputFile = (_a7) => {
  var _b7 = _a7, { value, onChange, placeholder, disabled, type } = _b7, props = __objRest(_b7, ["value", "onChange", "placeholder", "disabled", "type"]);
  const inputRef = useRef3();
  return /* @__PURE__ */ React17.createElement(
    DialogCol,
    {
      breakSm: true,
      onClick: () => {
        var _a8;
        return (_a8 = inputRef.current) == null ? void 0 : _a8.click();
      },
      css: {
        justifyContent: "center",
        position: "relative",
        cursor: "pointer",
        py: "$12",
        border: "1px dashed $border_bright",
        r: "$1",
        height: "max(140px, 30%)",
        alignItems: "center",
        m: "$6 0"
      },
      gap: "8"
    },
    /* @__PURE__ */ React17.createElement(
      IconButton,
      {
        variant: "standard",
        css: {
          border: "none",
          background: "none",
          "&:hover": {
            border: "none",
            background: "none",
            bg: "$transparent !important"
          }
        }
      },
      /* @__PURE__ */ React17.createElement(
        CloudUploadIcon,
        {
          style: {
            width: "3rem",
            height: "3rem"
          }
        }
      )
    ),
    /* @__PURE__ */ React17.createElement(Flex, { direction: "row" }, /* @__PURE__ */ React17.createElement(
      Input,
      __spreadValues({
        ref: inputRef,
        css: { width: "70%", "@sm": { width: "100%" } },
        value,
        onChange: (e) => onChange(e.target),
        placeholder,
        disabled,
        type,
        hidden: true
      }, props)
    ), /* @__PURE__ */ React17.createElement(
      IconButton,
      {
        variant: "standard",
        css: {
          background: "none",
          border: "none",
          textDecoration: "underline",
          "&:hover": {
            background: "none !important",
            border: "none !important"
          }
        }
      },
      /* @__PURE__ */ React17.createElement(Text, { variant: "md" }, placeholder)
    ))
  );
};

// src/Prebuilt/components/AuthToken.jsx
var AuthToken = React18.memo(({ authTokenByRoomCodeEndpoint, defaultAuthToken }) => {
  const hmsActions = useHMSActions4();
  const tokenEndpoint = useTokenEndpoint();
  const { roomCode, userId } = useHMSPrebuiltContext();
  const [error, setError] = useState4({ title: "", body: "" });
  let authToken = defaultAuthToken;
  const [, setAuthTokenInAppData] = useSetAppDataByKey(APP_DATA.authToken);
  useEffect4(() => {
    if (authToken) {
      setAuthTokenInAppData(authToken);
      return;
    }
    if (!tokenEndpoint && !roomCode) {
      return;
    }
    hmsActions.getAuthTokenByRoomCode({ roomCode, userId }, { endpoint: authTokenByRoomCodeEndpoint }).then((token) => setAuthTokenInAppData(token)).catch((error2) => setError(convertError(error2)));
  }, [hmsActions, tokenEndpoint, authToken, authTokenByRoomCodeEndpoint, setAuthTokenInAppData, roomCode, userId]);
  if (error.title) {
    return /* @__PURE__ */ React18.createElement(ErrorDialog, { title: error.title }, error.body);
  }
  return null;
});
var convertError = (error) => {
  console.error("[error]", { error });
  if (error.action === "GET_TOKEN" && error.code === 403) {
    return {
      title: "Room code is disabled",
      body: ErrorWithSupportLink("Room code corresponding to this link is no more active.")
    };
  } else if (error.action === "GET_TOKEN" && error.code === 404) {
    return {
      title: "Room code does not exist",
      body: ErrorWithSupportLink("We could not find a room code corresponding to this link.")
    };
  } else if (error.action === "GET_TOKEN" && error.code === 2003) {
    return {
      title: "Endpoint is not reachable",
      body: ErrorWithSupportLink(`Endpoint is not reachable. ${error.description}.`)
    };
  } else if (error.response && error.response.status === 404) {
    return {
      title: "Room does not exist",
      body: ErrorWithSupportLink("We could not find a room corresponding to this link.")
    };
  } else if (error.response && error.response.status === 403) {
    return {
      title: "Accessing room using this link format is disabled",
      body: ErrorWithSupportLink("You can re-enable this from the developer section in Dashboard.")
    };
  } else {
    console.error("Token API Error", error);
    return {
      title: "Error fetching token",
      body: ErrorWithSupportLink(
        "An error occurred while fetching the app token. Please look into logs for more details."
      )
    };
  }
};
var Link2 = styled("a", {
  color: "#2f80e1"
});
var ErrorWithSupportLink = (errorMessage) => /* @__PURE__ */ React18.createElement("div", null, errorMessage, " If you think this is a mistake on our side, please create", " ", /* @__PURE__ */ React18.createElement(Link2, { target: "_blank", href: "https://github.com/100mslive/100ms-web/issues", rel: "noreferrer" }, "an issue"), " ", "or reach out over", " ", /* @__PURE__ */ React18.createElement(Link2, { target: "_blank", href: "https://discord.com/invite/kGdmszyzq2", rel: "noreferrer" }, "Discord"), ".");
var AuthToken_default = AuthToken;

// src/Prebuilt/components/ErrorBoundary.jsx
import React19, { Component } from "react";
import { CopyIcon as CopyIcon2 } from "@100mslive/react-icons";
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, isErrorCopied: false };
  }
  componentDidCatch(error, errorInfo) {
    console.error(`react error boundary - ${error.message}`, error, errorInfo);
    this.setState({
      error: error == null ? void 0 : error.message,
      errorInfo
    });
  }
  render() {
    if (this.state.errorInfo) {
      return /* @__PURE__ */ React19.createElement(
        Flex,
        {
          align: "center",
          justify: "center",
          css: {
            size: "100%",
            height: "100vh",
            width: "100%",
            color: "$on_primary_high",
            backgroundColor: "$background_default"
          }
        },
        /* @__PURE__ */ React19.createElement(Box, { css: { position: "relative", overflow: "hidden", r: "$3", height: "100%", width: "100%" } }, /* @__PURE__ */ React19.createElement(
          Flex,
          {
            direction: "column",
            css: {
              position: "absolute",
              size: "100%",
              top: "33.33%",
              left: 0
            }
          },
          /* @__PURE__ */ React19.createElement("div", { style: { margin: "1.5rem", width: "100%" } }, /* @__PURE__ */ React19.createElement(Text, null, "Something went wrong"), /* @__PURE__ */ React19.createElement(Text, null, "Message: $", this.state.error), /* @__PURE__ */ React19.createElement("br", null), ErrorWithSupportLink(`Please reload to see if it works.`)),
          /* @__PURE__ */ React19.createElement(Flex, null, /* @__PURE__ */ React19.createElement(Tooltip, { title: "Reload page" }, /* @__PURE__ */ React19.createElement(
            Button,
            {
              onClick: () => {
                window.location.reload();
              },
              css: { mx: "$8" },
              "data-testid": "join_again_btn"
            },
            "Reload"
          )), /* @__PURE__ */ React19.createElement(Tooltip, { title: "Copy error details to clipboard" }, /* @__PURE__ */ React19.createElement(
            Button,
            {
              onClick: () => {
                const { error, errorInfo } = this.state;
                navigator.clipboard.writeText(
                  JSON.stringify({
                    error,
                    errorInfo
                  })
                );
                this.setState({ isErrorCopied: true });
              },
              css: { mx: "$8" },
              "data-testid": "join_again_btn"
            },
            /* @__PURE__ */ React19.createElement(CopyIcon2, null),
            " ",
            this.state.isErrorCopied ? "Copied" : "Copy Details"
          ))),
          /* @__PURE__ */ React19.createElement("details", { style: { whiteSpace: "pre-wrap", margin: "1.5rem" } }, /* @__PURE__ */ React19.createElement(Text, null, this.state.error && this.state.error.toString()), /* @__PURE__ */ React19.createElement("br", null), /* @__PURE__ */ React19.createElement(Text, null, JSON.stringify(this.state.errorInfo)))
        ))
      );
    }
    return this.props.children;
  }
};

// src/Prebuilt/components/FullPageProgress.jsx
import React20 from "react";
var FullPageProgress = ({ loaderColor = "$primary_default", text = "", css: css2 = {} }) => /* @__PURE__ */ React20.createElement(Flex, { direction: "column", justify: "center", align: "center", css: __spreadValues({ size: "100%", color: loaderColor }, css2) }, /* @__PURE__ */ React20.createElement(Loading, { color: "currentColor", size: 100 }), text ? /* @__PURE__ */ React20.createElement(Text, { css: { mt: "$10", color: "$on_surface_high" } }, text) : null);
var FullPageProgress_default = FullPageProgress;

// src/Prebuilt/components/init/Init.jsx
import React21, { useEffect as useEffect6 } from "react";

// src/Prebuilt/services/FeatureFlags.jsx
import { useEffect as useEffect5 } from "react";
import { selectRoomID, useHMSStore as useHMSStore5 } from "@100mslive/react-sdk";
var FeatureFlags = class {
  static init(roomId) {
    if (!window.HMS) {
      window.HMS = {};
    }
    window.HMS.OPTIMISE_HLS_LATENCY = false;
    window.HMS.ALWAYS_REQUEST_PERMISSIONS = false;
    window.HMS.SHOW_NS = define_process_env_default.REACT_APP_ENV !== "prod";
    this.enableTranscription = define_process_env_default.REACT_APP_TRANSCRIPTION_ROOM_ID === roomId;
  }
  static showNS() {
    return window.HMS.SHOW_NS;
  }
  static optimiseHLSLatency() {
    return window.HMS.OPTIMISE_HLS_LATENCY;
  }
  static alwaysRequestPermissions() {
    return window.HMS.ALWAYS_REQUEST_PERMISSIONS;
  }
};
__publicField(FeatureFlags, "enableTranscription", define_process_env_default.REACT_APP_ENABLE_TRANSCRIPTION === "true");
__publicField(FeatureFlags, "enableStatsForNerds", define_process_env_default.REACT_APP_ENABLE_STATS_FOR_NERDS === "true");
__publicField(FeatureFlags, "enableWhiteboard", define_process_env_default.REACT_APP_ENABLE_WHITEBOARD && define_process_env_default.REACT_APP_PUSHER_APP_KEY && define_process_env_default.REACT_APP_PUSHER_AUTHENDPOINT);
function FeatureFlagsInit() {
  const roomId = useHMSStore5(selectRoomID);
  useEffect5(() => {
    if (roomId) {
      FeatureFlags.init(roomId);
    }
  }, [roomId]);
  return null;
}

// src/Prebuilt/components/init/Init.jsx
var Init = () => {
  const { toggleTheme } = useTheme();
  useEffect6(() => {
    window.toggleUiTheme = toggleTheme;
  }, [toggleTheme]);
  useEffect6(() => {
    function resetHeight() {
      var _a7;
      document.body.style.height = `${((_a7 = window.visualViewport) == null ? void 0 : _a7.height) || window.innerHeight}px`;
    }
    window.addEventListener("resize", resetHeight);
    resetHeight();
    return () => {
      window.removeEventListener("resize", resetHeight);
    };
  }, []);
  return /* @__PURE__ */ React21.createElement(FeatureFlagsInit, null);
};

// src/Prebuilt/components/Input/KeyboardInputManager.js
import { useEffect as useEffect7 } from "react";
import {
  selectAppData as selectAppData3,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  useHMSActions as useHMSActions5,
  useHMSVanillaStore as useHMSVanillaStore3
} from "@100mslive/react-sdk";
var isEvenListenersAttached = false;
var _actions, _store, _toggleAudio, _toggleVideo, _hideSidepane, _toggleStatsForNerds, _toggleHlsStats, _keyDownHandler, _bind, _unbind;
var KeyboardInputManager = class {
  constructor(store, actions) {
    __privateAdd(this, _actions, void 0);
    __privateAdd(this, _store, void 0);
    __privateAdd(this, _toggleAudio, () => __async(this, null, function* () {
      const enabled = __privateGet(this, _store).getState(selectIsLocalAudioEnabled);
      yield __privateGet(this, _actions).setLocalAudioEnabled(!enabled);
    }));
    __privateAdd(this, _toggleVideo, () => __async(this, null, function* () {
      const enabled = __privateGet(this, _store).getState(selectIsLocalVideoEnabled);
      yield __privateGet(this, _actions).setLocalVideoEnabled(!enabled);
    }));
    __privateAdd(this, _hideSidepane, () => {
      if (__privateGet(this, _store).getState(selectAppData3(APP_DATA.sidePane))) {
        __privateGet(this, _actions).setAppData(APP_DATA.sidePane, "");
      }
    });
    __privateAdd(this, _toggleStatsForNerds, () => {
      const uiSettings = __privateGet(this, _store).getState(selectAppData3(APP_DATA.uiSettings));
      const statsEnabled = uiSettings.showStatsOnTiles;
      __privateGet(this, _actions).setAppData(APP_DATA.uiSettings, __spreadProps(__spreadValues({}, uiSettings), {
        showStatsOnTiles: !statsEnabled
      }));
    });
    __privateAdd(this, _toggleHlsStats, () => {
      __privateGet(this, _actions).setAppData(APP_DATA.hlsStats, !__privateGet(this, _store).getState(selectAppData3(APP_DATA.hlsStats)));
    });
    // eslint-disable-next-line complexity
    __privateAdd(this, _keyDownHandler, (e) => __async(this, null, function* () {
      const CONTROL_KEY = isMacOS ? e.metaKey : e.ctrlKey;
      const D_KEY = e.key === "d" || e.key === "D";
      const E_KEY = e.key === "e" || e.key === "E";
      const SNF_KEY = e.key === "]" || e.key === "}";
      const SHORTCUT_TOGGLE_AUDIO = CONTROL_KEY && D_KEY;
      const SHORTCUT_TOGGLE_VIDEO = CONTROL_KEY && E_KEY;
      const SHORTCUT_SIDEPANE_CLOSE = e.key === "Escape";
      const SHORTCUT_STATS_FOR_NERDS = CONTROL_KEY && SNF_KEY;
      if (SHORTCUT_TOGGLE_AUDIO) {
        e.preventDefault();
        yield __privateGet(this, _toggleAudio).call(this);
      } else if (SHORTCUT_TOGGLE_VIDEO) {
        e.preventDefault();
        yield __privateGet(this, _toggleVideo).call(this);
      } else if (SHORTCUT_SIDEPANE_CLOSE) {
        __privateGet(this, _hideSidepane).call(this);
      } else if (SHORTCUT_STATS_FOR_NERDS) {
        __privateGet(this, _toggleHlsStats).call(this);
        __privateGet(this, _toggleStatsForNerds).call(this);
      }
    }));
    __privateAdd(this, _bind, () => {
      document.addEventListener("keydown", __privateGet(this, _keyDownHandler), false);
    });
    __privateAdd(this, _unbind, () => {
      document.removeEventListener("keydown", __privateGet(this, _keyDownHandler), false);
    });
    __publicField(this, "bindAllShortcuts", () => {
      if (!isEvenListenersAttached) {
        __privateGet(this, _bind).call(this);
        isEvenListenersAttached = true;
      }
    });
    __publicField(this, "unbindAllShortcuts", () => {
      if (isEvenListenersAttached) {
        __privateGet(this, _unbind).call(this);
        isEvenListenersAttached = false;
      }
    });
    __privateSet(this, _actions, actions);
    __privateSet(this, _store, store);
  }
};
_actions = new WeakMap();
_store = new WeakMap();
_toggleAudio = new WeakMap();
_toggleVideo = new WeakMap();
_hideSidepane = new WeakMap();
_toggleStatsForNerds = new WeakMap();
_toggleHlsStats = new WeakMap();
_keyDownHandler = new WeakMap();
_bind = new WeakMap();
_unbind = new WeakMap();
var KeyboardHandler = () => {
  const store = useHMSVanillaStore3();
  const actions = useHMSActions5();
  useEffect7(() => {
    const keyboardManager = new KeyboardInputManager(store, actions);
    keyboardManager.bindAllShortcuts();
    return keyboardManager.unbindAllShortcuts;
  }, [actions, store]);
  return null;
};

// src/Prebuilt/components/Notifications/Notifications.jsx
import React30, { useCallback as useCallback8, useEffect as useEffect16 } from "react";
import { useNavigate as useNavigate2, useParams as useParams2 } from "react-router-dom";
import {
  HMSNotificationTypes as HMSNotificationTypes7,
  HMSRoomState as HMSRoomState2,
  selectHasPeerHandRaised,
  selectRoomState as selectRoomState2,
  useCustomEvent,
  useHMSNotifications as useHMSNotifications7,
  useHMSStore as useHMSStore7,
  useHMSVanillaStore as useHMSVanillaStore4
} from "@100mslive/react-sdk";

// src/Prebuilt/provider/roomLayoutProvider/index.tsx
import React22 from "react";
import merge from "lodash.merge";

// src/Prebuilt/provider/roomLayoutProvider/hooks/useFetchRoomLayout.ts
import { useCallback as useCallback5, useEffect as useEffect8, useRef as useRef4, useState as useState5 } from "react";

// src/Prebuilt/provider/roomLayoutProvider/constants/index.ts
import { JoinForm_JoinBtnType } from "@100mslive/types-prebuilt/elements/join_form";
var defaultLayout = {
  id: "",
  role_id: "",
  template_id: "",
  app_id: "",
  typography: {
    font_family: "Inter"
  },
  themes: [],
  options: {},
  screens: {
    preview: {
      default: {
        elements: {
          preview_header: {
            title: "Get Started",
            sub_title: "Setup your audio and video before joining"
          },
          join_form: {
            join_btn_type: JoinForm_JoinBtnType.JOIN_BTN_TYPE_JOIN_ONLY,
            join_btn_label: "Join Now",
            go_live_btn_label: "Go Live"
          }
        }
      }
    },
    conferencing: {
      default: {
        elements: {
          chat: {},
          participant_list: {},
          video_tile_layout: {
            grid: {
              enable_local_tile_inset: true,
              prominent_roles: [],
              enable_spotlighting_peer: true
            }
          },
          emoji_reactions: {}
        }
      }
    },
    leave: {}
  }
};

// src/Prebuilt/provider/roomLayoutProvider/hooks/useFetchRoomLayout.ts
var fetchWithRetry = (..._0) => __async(void 0, [..._0], function* (url = "", options = {}) {
  const MAX_RETRIES = 4;
  let error = Error("something went wrong");
  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      return yield fetch(url, options);
    } catch (err) {
      error = err;
    }
  }
  console.error("Fetch failed after max retries", { url, options });
  throw error;
});
var useFetchRoomLayout = ({
  endpoint = "",
  authToken = ""
}) => {
  const [layout, setLayout] = useState5(void 0);
  const layoutResp = useRef4();
  const isFetchInProgress = useRef4(false);
  const updateRoomLayoutForRole = useCallback5((role) => {
    var _a7;
    if (!layoutResp.current) {
      return;
    }
    const [layout2] = (((_a7 = layoutResp.current) == null ? void 0 : _a7.data) || []).filter((layout3) => layout3.role === role);
    if (layout2) {
      setLayout(layout2);
    }
  }, []);
  useEffect8(() => {
    (() => __async(void 0, null, function* () {
      var _a7, _b7;
      if (isFetchInProgress.current || !authToken) {
        return;
      }
      isFetchInProgress.current = true;
      try {
        const resp = yield fetchWithRetry(endpoint || "https://api.100ms.live/v2/layouts/ui", {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        });
        layoutResp.current = yield resp.json();
      } catch (e) {
        console.error("[Room Layout API]: Failed to fetch / process room layout. Resorting to default layout.", e);
        layoutResp.current = {
          data: [defaultLayout]
        };
      }
      let layoutForRole = (_b7 = (_a7 = layoutResp.current) == null ? void 0 : _a7.data) == null ? void 0 : _b7[0];
      if (!layoutForRole) {
        console.error(
          "[Room Layout API]: Unable to figure out room layout from API response. Resorting to default layout."
        );
        layoutForRole = defaultLayout;
      }
      const layout2 = layoutForRole;
      setLayout(layout2);
      isFetchInProgress.current = false;
    }))();
  }, [authToken, endpoint]);
  return { layout, updateRoomLayoutForRole };
};

// src/Prebuilt/provider/roomLayoutProvider/index.tsx
var RoomLayoutContext = React22.createContext(void 0);
var RoomLayoutProvider = ({
  children,
  roomLayoutEndpoint,
  overrideLayout
}) => {
  const authToken = useAuthToken();
  const { layout, updateRoomLayoutForRole } = useFetchRoomLayout({ authToken, endpoint: roomLayoutEndpoint });
  const mergedLayout = authToken && layout ? merge(layout, overrideLayout) : layout;
  return /* @__PURE__ */ React22.createElement(RoomLayoutContext.Provider, { value: { layout: mergedLayout, updateRoomLayoutForRole } }, children);
};
var useRoomLayout = () => {
  var _a7;
  return (_a7 = React22.useContext(RoomLayoutContext)) == null ? void 0 : _a7.layout;
};
var useUpdateRoomLayout = () => {
  var _a7;
  return (_a7 = React22.useContext(RoomLayoutContext)) == null ? void 0 : _a7.updateRoomLayoutForRole;
};

// src/Prebuilt/components/Toast/ToastConfig.jsx
import React23, { useCallback as useCallback6 } from "react";
import { selectPeerByID, useHMSActions as useHMSActions6, useHMSStore as useHMSStore6 } from "@100mslive/react-sdk";
import {
  ChatUnreadIcon,
  ConnectivityIcon,
  HandIcon,
  PeopleAddIcon,
  PeopleRemoveIcon,
  PoorConnectivityIcon
} from "@100mslive/react-icons";
var ChatAction = React23.forwardRef((_, ref) => {
  const toggleChat = useSidepaneToggle(SIDE_PANE_OPTIONS.CHAT);
  const isChatOpen = useIsSidepaneTypeOpen(SIDE_PANE_OPTIONS.CHAT);
  if (isChatOpen) {
    return null;
  }
  return /* @__PURE__ */ React23.createElement(Button, { outlined: true, as: "div", variant: "standard", css: { w: "max-content" }, onClick: toggleChat, ref }, "Open Chat");
});
var HandRaiseAction = React23.forwardRef(({ id = "", isSingleHandRaise = true }, ref) => {
  var _a7, _b7, _c;
  const hmsActions = useHMSActions6();
  const toggleSidepane = useSidepaneToggle(SIDE_PANE_OPTIONS.PARTICIPANTS);
  const isParticipantsOpen = useIsSidepaneTypeOpen(SIDE_PANE_OPTIONS.PARTICIPANTS);
  const peer = useHMSStore6(selectPeerByID(id));
  const layout = useRoomLayout();
  const {
    bring_to_stage_label,
    on_stage_role,
    off_stage_roles = []
  } = ((_c = (_b7 = (_a7 = layout == null ? void 0 : layout.screens) == null ? void 0 : _a7.conferencing) == null ? void 0 : _b7.default) == null ? void 0 : _c.elements.on_stage_exp) || {};
  const onClickHandler = useCallback6(() => {
    if (isSingleHandRaise) {
      hmsActions.changeRoleOfPeer(id, on_stage_role);
    } else {
      !isParticipantsOpen && toggleSidepane();
    }
  }, [hmsActions, id, isParticipantsOpen, isSingleHandRaise, on_stage_role, toggleSidepane]);
  if (isSingleHandRaise && (!peer || !off_stage_roles.includes(peer.roleName))) {
    return null;
  }
  return /* @__PURE__ */ React23.createElement(Button, { outlined: true, as: "div", variant: "standard", css: { w: "max-content" }, onClick: onClickHandler, ref }, isSingleHandRaise ? bring_to_stage_label : "View");
});
var ToastConfig = {
  PEER_LIST: {
    single: function(notification) {
      var _a7, _b7;
      if (notification.data.length === 1) {
        return {
          title: `${(_a7 = notification.data[0]) == null ? void 0 : _a7.name} joined`,
          icon: /* @__PURE__ */ React23.createElement(PeopleAddIcon, null)
        };
      }
      return {
        title: `${(_b7 = notification.data[notification.data.length - 1]) == null ? void 0 : _b7.name} and ${notification.data.length - 1} others joined`,
        icon: /* @__PURE__ */ React23.createElement(PeopleAddIcon, null)
      };
    },
    multiple: (notifications) => {
      return {
        title: `${notifications[0].data.name} and ${notifications.length - 1} others joined`,
        icon: /* @__PURE__ */ React23.createElement(PeopleAddIcon, null)
      };
    }
  },
  PEER_JOINED: {
    single: function(notification) {
      var _a7;
      return {
        title: `${(_a7 = notification.data) == null ? void 0 : _a7.name} joined`,
        icon: /* @__PURE__ */ React23.createElement(PeopleAddIcon, null)
      };
    },
    multiple: function(notifications) {
      return {
        title: `${notifications[notifications.length - 1].data.name} and ${notifications.length - 1} others joined`,
        icon: /* @__PURE__ */ React23.createElement(PeopleAddIcon, null)
      };
    }
  },
  PEER_LEFT: {
    single: function(notification) {
      var _a7;
      return {
        title: `${(_a7 = notification.data) == null ? void 0 : _a7.name} left`,
        icon: /* @__PURE__ */ React23.createElement(PeopleRemoveIcon, null)
      };
    },
    multiple: function(notifications) {
      return {
        title: `${notifications[notifications.length - 1].data.name} and ${notifications.length - 1} others left`,
        icon: /* @__PURE__ */ React23.createElement(PeopleRemoveIcon, null)
      };
    }
  },
  RAISE_HAND: {
    single: (notification) => {
      var _a7, _b7;
      return {
        title: `${(_a7 = notification.data) == null ? void 0 : _a7.name} raised hand`,
        icon: /* @__PURE__ */ React23.createElement(HandIcon, null),
        action: /* @__PURE__ */ React23.createElement(HandRaiseAction, { id: (_b7 = notification.data) == null ? void 0 : _b7.id })
      };
    },
    multiple: (notifications) => {
      var _a7;
      const count = new Set(notifications.map((notification) => {
        var _a8;
        return (_a8 = notification.data) == null ? void 0 : _a8.id;
      })).size;
      return {
        title: `${(_a7 = notifications[notifications.length - 1].data) == null ? void 0 : _a7.name} ${count > 1 ? `${count} and others` : ""} raised hand`,
        icon: /* @__PURE__ */ React23.createElement(HandIcon, null),
        action: /* @__PURE__ */ React23.createElement(HandRaiseAction, { isSingleHandRaise: false })
      };
    }
  },
  NEW_MESSAGE: {
    single: (notification) => {
      var _a7;
      return {
        title: `New message from ${(_a7 = notification.data) == null ? void 0 : _a7.senderName}`,
        icon: /* @__PURE__ */ React23.createElement(ChatUnreadIcon, null),
        action: /* @__PURE__ */ React23.createElement(ChatAction, null)
      };
    },
    multiple: (notifications) => {
      return {
        title: `${notifications.length} new messages`,
        icon: /* @__PURE__ */ React23.createElement(ChatUnreadIcon, null),
        action: /* @__PURE__ */ React23.createElement(ChatAction, null)
      };
    }
  },
  RECONNECTED: {
    single: () => {
      return {
        title: `You are now connected`,
        icon: /* @__PURE__ */ React23.createElement(ConnectivityIcon, null),
        variant: "success",
        duration: 3e3
      };
    }
  },
  RECONNECTING: {
    single: (message) => {
      return {
        title: `You are offline for now. while we try to reconnect, please check
        your internet connection. ${message}.
      `,
        icon: /* @__PURE__ */ React23.createElement(PoorConnectivityIcon, null),
        variant: "warning",
        duration: 3e4
      };
    }
  }
};

// src/Prebuilt/components/Toast/ToastManager.js
import { v4 } from "uuid";
var ToastManager = {
  toasts: /* @__PURE__ */ new Map(),
  listeners: /* @__PURE__ */ new Map(),
  addToast(toast) {
    const id = toast.id ? toast.id : v4();
    this.toasts.set(id, __spreadProps(__spreadValues({}, toast), { id }));
    this.onChange();
    return id;
  },
  clearAllToast() {
    this.toasts.clear();
    this.onChange();
  },
  removeToast(id) {
    this.toasts.delete(id);
    this.onChange();
  },
  replaceToast(id, toast) {
    if (this.isActive(id)) {
      this.toasts.set(id, __spreadValues(__spreadValues({}, this.toasts.get(id)), toast));
      this.onChange();
      return id;
    } else {
      return this.addToast(toast);
    }
  },
  addListener(cb) {
    this.listeners.set(cb, cb);
  },
  removeListener(cb) {
    this.listeners.delete(cb);
  },
  isActive(id) {
    return this.toasts.has(id);
  },
  onChange() {
    const toasts = Array.from(this.toasts.values());
    this.listeners.forEach((listener) => listener(toasts));
  }
};

// src/Prebuilt/components/Toast/ToastBatcher.js
var ToastBatcher = {
  toastsType: /* @__PURE__ */ new Map(),
  showToastInternal({ notification, duration, type }) {
    let notificationType = type;
    if (!type) {
      notificationType = notification.type;
    }
    const toastType = this.toastsType.has(notificationType);
    if (toastType) {
      let { notifications } = this.toastsType.get(notificationType);
      const { id } = this.toastsType.get(notificationType);
      notifications.push(notification);
      const toastObject = ToastConfig[notificationType].multiple(notifications);
      const toastId = ToastManager.replaceToast(id, __spreadProps(__spreadValues({}, toastObject), {
        duration
      }));
      this.toastsType.set(notificationType, {
        id: toastId,
        notifications,
        duration
      });
    } else {
      const toastObject = ToastConfig[notificationType].single(notification);
      const toastId = ToastManager.addToast(__spreadProps(__spreadValues({}, toastObject), {
        duration
      }));
      let notifications = [];
      notifications.push(notification);
      this.toastsType.set(notificationType, {
        id: toastId,
        notifications: [...notifications],
        duration
      });
    }
  },
  showToast({ notification, duration = 3e3, type }) {
    try {
      this.showToastInternal({ notification, duration, type });
    } catch (err) {
      console.debug("Notifications", err);
    }
  },
  syncUItoast(toastsDisplaying) {
    for (const [toastType, toastInfo] of this.toastsType.entries()) {
      if (!toastsDisplaying.find((toast) => toast.id === toastInfo.id)) {
        this.toastsType.delete(toastType);
      }
    }
  }
};
ToastManager.addListener(ToastBatcher.syncUItoast.bind(ToastBatcher));

// src/Prebuilt/components/Notifications/AutoplayBlockedModal.jsx
import React24 from "react";
import { useAutoplayError } from "@100mslive/react-sdk";
function AutoplayBlockedModal() {
  const { error, resetError, unblockAudio } = useAutoplayError();
  return /* @__PURE__ */ React24.createElement(
    Dialog.Root,
    {
      open: !!error,
      onOpenChange: (value) => {
        if (!value) {
          unblockAudio();
        }
        resetError();
      }
    },
    /* @__PURE__ */ React24.createElement(DialogContent, { title: "Permission Error", closeable: false }, /* @__PURE__ */ React24.createElement(DialogRow, null, /* @__PURE__ */ React24.createElement(Text, { variant: "md" }, "The browser wants us to get a confirmation for playing the Audio. Please allow audio to proceed.")), /* @__PURE__ */ React24.createElement(DialogRow, { justify: "end" }, /* @__PURE__ */ React24.createElement(
      Button,
      {
        variant: "primary",
        onClick: () => {
          unblockAudio();
          resetError();
        }
      },
      "Allow Audio"
    )))
  );
}

// src/Prebuilt/components/Notifications/InitErrorModal.jsx
import React25, { useEffect as useEffect9, useState as useState6 } from "react";
var InitErrorModal = ({ notification }) => {
  const [showModal, setShowModal] = useState6(false);
  const [info, setInfo] = useState6({ title: "Init Error", description: "" });
  useEffect9(() => {
    const data = notification == null ? void 0 : notification.data;
    if (!data || data.action !== "INIT") {
      return;
    }
    let description;
    let title;
    if (data.description.includes("role is invalid")) {
      description = "This role does not exist for the given room. Try again with a valid role.";
      title = "Invalid Role";
    } else if (data.description.includes("room is not active")) {
      title = "Room is disabled";
      description = "This room is disabled and cannot be joined. To enable the room, use the 100ms dashboard or the API.";
    } else {
      description = data.description;
      title = "Init Error";
    }
    setInfo({ title, description });
    setShowModal(true);
  }, [notification]);
  return /* @__PURE__ */ React25.createElement(ErrorDialog, { open: showModal, onOpenChange: setShowModal, title: info.title }, /* @__PURE__ */ React25.createElement(Text, { variant: "sm", css: { wordBreak: "break-word" } }, info.description, " ", /* @__PURE__ */ React25.createElement("br", null), "Current URL - ", window.location.href));
};

// src/Prebuilt/components/Notifications/PeerNotifications.jsx
import { useEffect as useEffect10 } from "react";
import { HMSNotificationTypes, useHMSNotifications } from "@100mslive/react-sdk";
var notificationTypes = [
  HMSNotificationTypes.PEER_LIST,
  HMSNotificationTypes.PEER_JOINED,
  HMSNotificationTypes.PEER_LEFT
];
var PeerNotifications = () => {
  const notification = useHMSNotifications(notificationTypes);
  const isPeerJoinSubscribed = useSubscribedNotifications(SUBSCRIBED_NOTIFICATIONS.PEER_JOINED);
  const isPeerLeftSubscribed = useSubscribedNotifications(SUBSCRIBED_NOTIFICATIONS.PEER_LEFT);
  useEffect10(() => {
    if (!(notification == null ? void 0 : notification.data)) {
      return;
    }
    console.debug(`[${notification.type}]`, notification);
    switch (notification.type) {
      case HMSNotificationTypes.PEER_LIST:
        if (!isPeerJoinSubscribed || notification.data.length === 0) {
          return;
        }
        break;
      case HMSNotificationTypes.PEER_JOINED:
        if (!isPeerJoinSubscribed) {
          return;
        }
        break;
      case HMSNotificationTypes.PEER_LEFT:
        if (!isPeerLeftSubscribed) {
          return;
        }
        break;
      default:
        return;
    }
    ToastBatcher.showToast({ notification });
  }, [notification, isPeerJoinSubscribed, isPeerLeftSubscribed]);
  return null;
};

// src/Prebuilt/components/Notifications/PermissionErrorModal.jsx
import React26, { useEffect as useEffect11, useState as useState7 } from "react";
import { useMedia } from "react-use";
import { HMSNotificationTypes as HMSNotificationTypes2, useHMSNotifications as useHMSNotifications2 } from "@100mslive/react-sdk";

// src/Prebuilt/images/android-perm-1.png
var android_perm_1_default = "data:image/png;base64,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";

// src/Prebuilt/images/ios-perm-0.png
var ios_perm_0_default = "data:image/png;base64,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";

// src/Prebuilt/components/Notifications/PermissionErrorModal.jsx
function PermissionErrorModal() {
  const notification = useHMSNotifications2(HMSNotificationTypes2.ERROR);
  const [deviceType, setDeviceType] = useState7("");
  const [isSystemError, setIsSystemError] = useState7(false);
  const isMobile = useMedia(config.media.md);
  useEffect11(() => {
    var _a7, _b7, _c, _d, _e;
    if (!notification || ((_a7 = notification.data) == null ? void 0 : _a7.code) !== 3001 && ((_b7 = notification.data) == null ? void 0 : _b7.code) !== 3011 || ((_c = notification.data) == null ? void 0 : _c.code) === 3001 && ((_d = notification.data) == null ? void 0 : _d.message.includes("screen"))) {
      return;
    }
    console.error(`[${notification.type}]`, notification);
    const errorMessage = (_e = notification.data) == null ? void 0 : _e.message;
    const hasAudio = errorMessage.includes("audio");
    const hasVideo = errorMessage.includes("video");
    const hasScreen = errorMessage.includes("screen");
    if (hasAudio && hasVideo) {
      setDeviceType("camera and microphone");
    } else if (hasAudio) {
      setDeviceType("microphone");
    } else if (hasVideo) {
      setDeviceType("camera");
    } else if (hasScreen) {
      setDeviceType("screen");
    }
    setIsSystemError(notification.data.code === 3011);
  }, [notification]);
  return deviceType ? /* @__PURE__ */ React26.createElement(Dialog.Root, { open: !!deviceType }, /* @__PURE__ */ React26.createElement(Dialog.Portal, null, /* @__PURE__ */ React26.createElement(Dialog.Overlay, null), /* @__PURE__ */ React26.createElement(Dialog.Content, { css: { w: "min(380px, 90%)", p: "$8" } }, /* @__PURE__ */ React26.createElement(
    Dialog.Title,
    {
      css: {
        borderBottom: "1px solid $border_default"
      }
    },
    isMobile && isIOS ? /* @__PURE__ */ React26.createElement("img", { style: { maxWidth: "100%", maxHeight: "100%" }, src: ios_perm_0_default, alt: "iOS Permission flow" }) : null,
    isMobile && isAndroid ? /* @__PURE__ */ React26.createElement(
      "img",
      {
        src: android_perm_1_default,
        style: { maxWidth: "100%", maxHeight: "100%" },
        alt: "Android Permission flow "
      }
    ) : null,
    /* @__PURE__ */ React26.createElement(Text, { variant: "h6" }, "We can't access your ", deviceType)
  ), /* @__PURE__ */ React26.createElement(Text, { variant: "sm", css: { pt: "$4", pb: "$10", color: "$on_surface_medium" } }, isMobile && isIOS ? 'Enable permissions by reloading this page and clicking "Allow" on the pop-up, or change settings from the address bar.' : null, isMobile && isAndroid ? `To allow other users to see and hear you, click the blocked camera icon in your browser's address bar.` : null, !isMobile ? `Access to ${deviceType} is required. ` : null, isSystemError && !isMobile ? `Enable permissions for ${deviceType}${deviceType === "screen" ? "share" : ""} from sytem settings` : null, !isSystemError && !isMobile ? `Enable permissions for ${deviceType}${deviceType === "screen" ? "share" : ""} from address bar or browser settings.` : null), isMobile && isIOS ? /* @__PURE__ */ React26.createElement(React26.Fragment, null, /* @__PURE__ */ React26.createElement(Button, { onClick: () => window.location.reload(), css: { w: "100%", mb: "$6" } }, "Reload"), /* @__PURE__ */ React26.createElement(Button, { outlined: true, variant: "standard", onClick: () => setDeviceType(""), css: { w: "100%" } }, "Continue anyway")) : null, isMobile && isAndroid ? /* @__PURE__ */ React26.createElement(React26.Fragment, null, /* @__PURE__ */ React26.createElement(Button, { onClick: () => setDeviceType(""), css: { w: "100%", mb: "$6" } }, "I've allowed access"), /* @__PURE__ */ React26.createElement(Button, { outlined: true, variant: "standard", onClick: () => setDeviceType(""), css: { w: "100%" } }, "Continue anyway")) : null, !isMobile ? /* @__PURE__ */ React26.createElement(Flex, { justify: "end", css: { w: "100%" } }, /* @__PURE__ */ React26.createElement(Button, { outlined: true, variant: "standard", onClick: () => setDeviceType("") }, "Dismiss")) : null))) : null;
}

// src/Prebuilt/components/Notifications/ReconnectNotifications.jsx
import React27, { useEffect as useEffect12, useState as useState8 } from "react";
import { HMSNotificationTypes as HMSNotificationTypes3, useHMSNotifications as useHMSNotifications3 } from "@100mslive/react-sdk";
var notificationTypes2 = [
  HMSNotificationTypes3.RECONNECTED,
  HMSNotificationTypes3.RECONNECTING,
  HMSNotificationTypes3.ERROR
];
var notificationId = null;
var ReconnectNotifications = () => {
  const notification = useHMSNotifications3(notificationTypes2);
  const [open, setOpen] = useState8(false);
  useEffect12(() => {
    var _a7;
    if ((notification == null ? void 0 : notification.type) === HMSNotificationTypes3.ERROR && ((_a7 = notification == null ? void 0 : notification.data) == null ? void 0 : _a7.isTerminal)) {
      setOpen(false);
    } else if ((notification == null ? void 0 : notification.type) === HMSNotificationTypes3.RECONNECTED) {
      notificationId = ToastManager.replaceToast(notificationId, ToastConfig.RECONNECTED.single());
      setOpen(false);
    } else if ((notification == null ? void 0 : notification.type) === HMSNotificationTypes3.RECONNECTING) {
      notificationId = ToastManager.replaceToast(
        notificationId,
        ToastConfig.RECONNECTING.single(notification.data.message)
      );
    }
  }, [notification]);
  if (!open)
    return null;
  return /* @__PURE__ */ React27.createElement(Dialog.Root, { open, modal: true }, /* @__PURE__ */ React27.createElement(Dialog.Portal, { container: document.getElementById("conferencing") }, /* @__PURE__ */ React27.createElement(Dialog.Overlay, null), /* @__PURE__ */ React27.createElement(
    Dialog.Content,
    {
      css: {
        width: "fit-content",
        maxWidth: "80%",
        p: "$4 $8",
        position: "relative",
        top: "unset",
        bottom: "$9",
        transform: "translate(-50%, -100%)",
        animation: "none !important"
      }
    },
    /* @__PURE__ */ React27.createElement(Flex, { align: "center" }, /* @__PURE__ */ React27.createElement("div", { style: { display: "inline", margin: "0.25rem" } }, /* @__PURE__ */ React27.createElement(Loading, { size: 16 })), /* @__PURE__ */ React27.createElement(Text, { css: { fontSize: "$space$8", color: "$on_surface_high" } }, "You lost your network connection. Trying to reconnect."))
  )));
};

// src/Prebuilt/components/Notifications/TrackBulkUnmuteModal.jsx
import React28, { useEffect as useEffect13, useState as useState9 } from "react";
import { HMSNotificationTypes as HMSNotificationTypes4, useHMSActions as useHMSActions7, useHMSNotifications as useHMSNotifications4 } from "@100mslive/react-sdk";
import { MicOnIcon } from "@100mslive/react-icons";
var TrackBulkUnmuteModal = () => {
  const hmsActions = useHMSActions7();
  const [muteNotification, setMuteNotification] = useState9(null);
  const notification = useHMSNotifications4([
    HMSNotificationTypes4.CHANGE_MULTI_TRACK_STATE_REQUEST,
    HMSNotificationTypes4.ROOM_ENDED,
    HMSNotificationTypes4.REMOVED_FROM_ROOM
  ]);
  useEffect13(() => {
    switch (notification == null ? void 0 : notification.type) {
      case HMSNotificationTypes4.REMOVED_FROM_ROOM:
      case HMSNotificationTypes4.ROOM_ENDED:
        setMuteNotification(null);
        break;
      case HMSNotificationTypes4.CHANGE_MULTI_TRACK_STATE_REQUEST:
        if (notification == null ? void 0 : notification.data.enabled) {
          setMuteNotification(notification.data);
        }
        break;
      default:
        return;
    }
  }, [notification]);
  if (!muteNotification) {
    return null;
  }
  const { requestedBy: peer, tracks, enabled } = muteNotification;
  return /* @__PURE__ */ React28.createElement(
    RequestDialog,
    {
      title: "Track Unmute Request",
      body: `${peer == null ? void 0 : peer.name} has requested you to unmute your tracks.`,
      onOpenChange: (value) => !value && setMuteNotification(null),
      onAction: () => {
        tracks.forEach((track) => {
          hmsActions.setEnabledTrack(track.id, enabled);
        });
        setMuteNotification(null);
      },
      Icon: MicOnIcon
    }
  );
};

// src/Prebuilt/components/Notifications/TrackNotifications.jsx
import { useEffect as useEffect14 } from "react";
import { HMSNotificationTypes as HMSNotificationTypes5, useHMSNotifications as useHMSNotifications5 } from "@100mslive/react-sdk";
var notificationTypes3 = [
  HMSNotificationTypes5.TRACK_ADDED,
  HMSNotificationTypes5.TRACK_REMOVED,
  HMSNotificationTypes5.TRACK_MUTED,
  HMSNotificationTypes5.TRACK_UNMUTED
];
var TrackNotifications = () => {
  const notification = useHMSNotifications5(notificationTypes3);
  useEffect14(() => {
    if (notification) {
      console.debug(`[${notification.type}]`, notification);
    }
  }, [notification]);
  return null;
};

// src/Prebuilt/components/Notifications/TrackUnmuteModal.jsx
import React29, { useEffect as useEffect15, useState as useState10 } from "react";
import { HMSNotificationTypes as HMSNotificationTypes6, useHMSActions as useHMSActions8, useHMSNotifications as useHMSNotifications6 } from "@100mslive/react-sdk";
import { MicOnIcon as MicOnIcon2 } from "@100mslive/react-icons";
var TrackUnmuteModal = () => {
  const hmsActions = useHMSActions8();
  const notification = useHMSNotifications6([
    HMSNotificationTypes6.CHANGE_TRACK_STATE_REQUEST,
    HMSNotificationTypes6.ROOM_ENDED,
    HMSNotificationTypes6.REMOVED_FROM_ROOM
  ]);
  const [muteNotification, setMuteNotification] = useState10(null);
  useEffect15(() => {
    switch (notification == null ? void 0 : notification.type) {
      case HMSNotificationTypes6.REMOVED_FROM_ROOM:
      case HMSNotificationTypes6.ROOM_ENDED:
        setMuteNotification(null);
        break;
      case HMSNotificationTypes6.CHANGE_TRACK_STATE_REQUEST:
        if (notification == null ? void 0 : notification.data.enabled) {
          setMuteNotification(notification.data);
        }
        break;
      default:
        return;
    }
  }, [notification]);
  if (!muteNotification) {
    return null;
  }
  const { requestedBy: peer, track, enabled } = muteNotification;
  return /* @__PURE__ */ React29.createElement(
    RequestDialog,
    {
      title: "Track Unmute Request",
      onOpenChange: (value) => !value && setMuteNotification(null),
      body: `${peer == null ? void 0 : peer.name} has requested you to unmute your ${track == null ? void 0 : track.source} ${track == null ? void 0 : track.type}.`,
      onAction: () => {
        hmsActions.setEnabledTrack(track.id, enabled);
        setMuteNotification(null);
      },
      Icon: MicOnIcon2
    }
  );
};

// src/Prebuilt/components/hooks/useRedirectToLeave.tsx
import { useCallback as useCallback7 } from "react";
import { useNavigate, useParams } from "react-router-dom";

// src/Prebuilt/components/PIP/PIPManager.js
import * as workerTimers from "worker-timers";

// src/Prebuilt/components/PIP/pipUtils.js
var CANVAS_FILL_COLOR;
var CANVAS_STROKE_COLOR;
function setPIPCanvasColors() {
  if (!CANVAS_FILL_COLOR) {
    CANVAS_FILL_COLOR = window.getComputedStyle(document.documentElement).getPropertyValue("--hms-ui-colors-surface_bright");
  }
  if (!CANVAS_STROKE_COLOR) {
    CANVAS_STROKE_COLOR = window.getComputedStyle(document.documentElement).getPropertyValue("--hms-ui-colors-border_bright");
  }
}
function resetPIPCanvasColors() {
  CANVAS_FILL_COLOR = "";
  CANVAS_STROKE_COLOR = "";
}
function drawVideoElementsOnCanvas(videoElements, canvas) {
  let videoTiles = videoElements.filter((videoElement) => videoElement.srcObject !== null);
  const ctx = canvas.getContext("2d");
  setPIPCanvasColors();
  ctx.fillStyle = CANVAS_FILL_COLOR;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (videoTiles.length === 0) {
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return;
  }
  fillGridTiles(videoTiles.slice(0, 4), ctx, canvas);
}
function dummyChangeInCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  setPIPCanvasColors();
  ctx.fillStyle = CANVAS_FILL_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}
function fillGridTiles(videoElements, ctx, canvas) {
  const offset = 8;
  canvas.width = 480;
  canvas.height = 320;
  ctx.fillStyle = CANVAS_FILL_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (videoElements.length > 0) {
    ctx.strokeStyle = CANVAS_STROKE_COLOR;
    ctx.lineWidth = offset / 2;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }
  if (videoElements.length === 1) {
    const video = videoElements[0];
    const { width, height } = getRenderDimensions(video, canvas.width - offset, canvas.height - offset);
    const xOffset = (canvas.width - width) / 2;
    const yOffset = (canvas.height - height) / 2;
    ctx.drawImage(video, xOffset, yOffset, width, height);
  }
  if (videoElements.length === 2) {
    videoElements.forEach((video, index) => {
      const { width, height } = getRenderDimensions(
        video,
        canvas.width / 2 - offset,
        // This will be the max available width for each tile
        canvas.height - offset
      );
      const xOffset = (canvas.width / 2 - width) / 2 + canvas.width / 2 * index;
      const yOffset = (canvas.height - height) / 2;
      ctx.drawImage(video, xOffset, yOffset, width, height);
    });
    const path = new Path2D();
    path.moveTo(canvas.width / 2, 0);
    path.lineTo(canvas.width / 2, canvas.height);
    ctx.stroke(path);
  }
  if (videoElements.length === 3) {
    videoElements.forEach((video, index) => {
      const { width, height } = getRenderDimensions(video, canvas.width / 2 - offset, canvas.height / 2 - offset);
      const xOffset = index < 2 ? (canvas.width / 2 - width) / 2 + canvas.width / 2 * index : canvas.width / 2 - width / 2;
      const yOffset = (index < 2 ? 0 : canvas.height / 2) + (canvas.height / 2 - height) / 2;
      ctx.drawImage(video, xOffset, yOffset, width, height);
    });
    const path = new Path2D();
    path.moveTo(canvas.width / 2, 0);
    path.lineTo(canvas.width / 2, canvas.height / 2);
    ctx.stroke(path);
    path.moveTo(0, canvas.height / 2);
    path.lineTo(canvas.width, canvas.height / 2);
    ctx.stroke(path);
  }
  if (videoElements.length === 4) {
    videoElements.forEach((video, index) => {
      const { width, height } = getRenderDimensions(video, canvas.width / 2 - offset, canvas.height / 2 - offset);
      const xOffset = (canvas.width / 2 - width) / 2 + canvas.width / 2 * (index % 2);
      const yOffset = (index < 2 ? 0 : canvas.height / 2) + (canvas.height / 2 - height) / 2;
      ctx.drawImage(video, xOffset, yOffset, width, height);
    });
    const path = new Path2D();
    path.moveTo(canvas.width / 2, 0);
    path.lineTo(canvas.width / 2, canvas.height);
    ctx.stroke(path);
    path.moveTo(0, canvas.height / 2);
    path.lineTo(canvas.width, canvas.height / 2);
    ctx.stroke(path);
  }
}
function getRenderDimensions(video, width, height) {
  let finalWidth = video.videoWidth / video.videoHeight * height;
  let finalHeight = height;
  if (finalWidth > width) {
    finalWidth = width;
    finalHeight = video.videoHeight / video.videoWidth * width;
  }
  return { width: finalWidth, height: finalHeight };
}

// src/Prebuilt/components/PIP/PIPManager.js
var MAX_NUMBER_OF_TILES_IN_PIP = 4;
var DEFAULT_FPS = 30;
var DEFAULT_CANVAS_WIDTH = 480;
var DEFAULT_CANVAS_HEIGHT = 320;
var LEAVE_EVENT_NAME = "leavepictureinpicture";
var PIPStates = {
  starting: "starting",
  started: "started",
  stopping: "stopping",
  stopped: "stopped"
};
var PipManager = class {
  constructor() {
    __publicField(this, "listeners", /* @__PURE__ */ new Set());
    __publicField(this, "stop", () => __async(this, null, function* () {
      var _a7;
      if (this.state === PIPStates.stopped) {
        return;
      }
      this.state = PIPStates.stopping;
      (_a7 = this.pipVideo) == null ? void 0 : _a7.removeEventListener(LEAVE_EVENT_NAME, this.stop);
      if (this.timeoutRef) {
        workerTimers.clearTimeout(this.timeoutRef);
        this.timeoutRef = null;
      }
      if (this.isOn()) {
        this.exitPIP();
      }
      yield this.detachOldAttachNewTracks(this.tracksToShow, []);
      this.onStateChange(false);
      this.callListeners(false);
      this.reset();
      this.state = PIPStates.stopped;
    }));
    // ------- Private function --------------
    /**
     * @private {boolean} on - whether pip is on/off
     */
    __publicField(this, "callListeners", (on) => {
      this.listeners.forEach((listener) => listener == null ? void 0 : listener(on));
    });
    this.reset();
  }
  listenToStateChange(cb) {
    this.listeners.add(cb);
  }
  /**
   * @private
   */
  reset() {
    console.debug("resetting PIP state");
    resetPIPCanvasColors();
    this.canvas = null;
    this.pipVideo = null;
    this.timeoutRef = null;
    this.hmsActions = null;
    this.videoElements = [];
    this.tracksToShow = [];
    this.onStateChange = () => {
    };
    this.state = PIPStates.stopped;
  }
  /**
   * check if PIP is supported in this browser env
   */
  isSupported() {
    return !!document.pictureInPictureEnabled && !isIOS && !(isMacOS && isSafari);
  }
  /**
   * check if pip is currently turned on
   */
  isOn() {
    return !!document.pictureInPictureElement;
  }
  /**
   * request browser to start pip and start the render loop updating the pip
   * video element with peer tracks.
   * @param hmsActions
   * @param onStateChangeFn {function(bool):void} callback called to notify change in pip state
   */
  start(hmsActions, onStateChangeFn) {
    return __async(this, null, function* () {
      if (!this.isSupported()) {
        throw new Error("pip is not supported on this browser");
      }
      console.debug("starting PIP, current state", this.state);
      if (this.state === PIPStates.started) {
        yield this.stop();
      } else if (this.state === PIPStates.starting) {
        return;
      }
      this.state = PIPStates.starting;
      try {
        yield this.init(hmsActions, onStateChangeFn);
        this.pipVideo.addEventListener(LEAVE_EVENT_NAME, this.stop);
        this.renderLoop();
        if (!this.isOn()) {
          yield this.requestPIP();
        }
        console.debug("pip started");
        this.state = PIPStates.started;
        this.onStateChange(true);
        this.callListeners(true);
      } catch (err) {
        console.error("error in request pip", err);
        this.state = PIPStates.stopped;
      }
    });
  }
  /**
   * @param peers {Array} All Remote Peers present in call.
   * @param tracksMap {Object} map of track id to track
   * */
  updatePeersAndTracks(peers, tracksMap) {
    return __async(this, null, function* () {
      if (!this.canvas) {
        return;
      }
      const newTracksToShowUnordered = this.pickTracksToShow(peers, tracksMap);
      const currentTracksShowing = this.tracksToShow;
      this.tracksToShow = this.orderNewTracksToShow(newTracksToShowUnordered, currentTracksShowing);
      try {
        yield this.detachOldAttachNewTracks(currentTracksShowing, this.tracksToShow, tracksMap);
      } catch (error) {
        console.error("error in detaching/attaching tracks", error);
      }
    });
  }
  /**
   * @private
   */
  init(hmsActions, onStateChangeFn) {
    return __async(this, null, function* () {
      yield this.initMediaElements();
      this.hmsActions = hmsActions;
      this.onStateChange = onStateChangeFn;
    });
  }
  initMediaElements() {
    return __async(this, null, function* () {
      if (!this.canvas) {
        const { canvas, pipVideo } = this.initializeCanvasAndVideoElement();
        this.canvas = canvas;
        this.pipVideo = pipVideo;
        this.videoElements = this.initializeVideoElements();
        const videoPlayPromise = this.pipVideo.play();
        dummyChangeInCanvas(this.canvas);
        yield videoPlayPromise;
      }
    });
  }
  initializeCanvasAndVideoElement() {
    const canvas = document.createElement("canvas");
    canvas.width = DEFAULT_CANVAS_WIDTH;
    canvas.height = DEFAULT_CANVAS_HEIGHT;
    const pipVideo = document.createElement("video");
    pipVideo.width = DEFAULT_CANVAS_WIDTH;
    pipVideo.height = DEFAULT_CANVAS_HEIGHT;
    pipVideo.muted = true;
    pipVideo.srcObject = canvas.captureStream();
    return { canvas, pipVideo };
  }
  initializeVideoElements() {
    let videoElements = [];
    for (let i = 0; i < MAX_NUMBER_OF_TILES_IN_PIP; i++) {
      const videoElement = document.createElement("video");
      videoElement.autoplay = true;
      videoElement.playsinline = true;
      videoElements.push(videoElement);
    }
    return videoElements;
  }
  /**
   * render loop is responsible for rendering the video elements on canvas/pip.
   * in each loop current video elements are stitched and painted on canvas
   */
  renderLoop() {
    const delay = 1e3 / DEFAULT_FPS;
    this.timeoutRef = workerTimers.setTimeout(() => {
      if (this.state === PIPStates.stopping || this.state === PIPStates.stopped) {
        return;
      }
      if (this.state === PIPStates.started) {
        drawVideoElementsOnCanvas(this.videoElements, this.canvas);
      }
      this.renderLoop();
    }, delay);
  }
  requestPIP() {
    return __async(this, null, function* () {
      try {
        if (this.isOn()) {
          this.exitPIP();
        }
        yield this.pipVideo.requestPictureInPicture();
      } catch (error) {
        console.error("error in requestpip", error, "state", this.state);
        throw error;
      }
    });
  }
  exitPIP() {
    document.exitPictureInPicture();
  }
  /**
   * Logic - pick only enabled video tracks
   * @param peers {Array<any>}
   * @param tracksMap {Record<string, any>}
   */
  pickTracksToShow(peers, tracksMap) {
    var _a7;
    const tracksToShow = [];
    for (const peer of peers) {
      if (tracksToShow.length === MAX_NUMBER_OF_TILES_IN_PIP) {
        break;
      } else if (peer.videoTrack && ((_a7 = tracksMap[peer.videoTrack]) == null ? void 0 : _a7.enabled)) {
        tracksToShow.push(peer.videoTrack);
      }
    }
    return tracksToShow;
  }
  /**
   * there has to be a smart reordering of new tracks based on currently showing
   * ones to reduce unnecessary displacement. If someone was showing up both
   * earlier and now, it's a better UX to keep their position same instead
   * of letting it change.
   * The returned array is a shuffled version of newTracks with position of
   * tracks present in the old tracks intact.
   * eg. old = [1,5,9,3], new = [3,8,2,9], result = [8,2,9,3]
   * @param oldTracks {Array}
   * @param newTracks {Array}
   * @return {Array}
   */
  orderNewTracksToShow(newTracks, oldTracks) {
    const betterNewTracks = [];
    const leftOvers = [];
    newTracks.forEach((track) => {
      const oldPosition = oldTracks.indexOf(track);
      if (oldPosition !== -1 && oldPosition < newTracks.length) {
        betterNewTracks[oldPosition] = track;
      } else {
        leftOvers.push(track);
      }
    });
    for (let i = 0; i < newTracks.length; i++) {
      if (!betterNewTracks[i]) {
        betterNewTracks[i] = leftOvers.shift();
      }
    }
    return betterNewTracks;
  }
  /**
   * call detach for tracks which no longer need to be shown and attach for
   * new ones which are to be shown now.
   * Note: oldTracks and newTracks are not necessarily of same length
   * @param oldTracks {Array<String>}
   * @param newTracks {Array<String>}
   * @param tracksMap {Record<String, any>}
   */
  // eslint-disable-next-line complexity
  detachOldAttachNewTracks(oldTracks, newTracks, tracksMap = null) {
    return __async(this, null, function* () {
      const numTracks = Math.max(oldTracks.length, newTracks.length);
      for (let i = 0; i < numTracks; i++) {
        if (oldTracks[i] === newTracks[i]) {
          continue;
        } else if (oldTracks[i]) {
          if (!tracksMap || tracksMap[oldTracks[i]]) {
            yield this.hmsActions.detachVideo(oldTracks[i], this.videoElements[i]);
          }
          if (this.videoElements[i]) {
            this.videoElements[i].srcObject = null;
          }
        }
        if (newTracks[i]) {
          yield this.hmsActions.attachVideo(newTracks[i], this.videoElements[i]);
        }
      }
    });
  }
};
var PictureInPicture = new PipManager();

// src/Prebuilt/provider/roomLayoutProvider/hooks/useRoomLayoutScreen.ts
function useRoomLayoutScreen({
  screen
}) {
  var _a7;
  const roomLayout = useRoomLayout();
  const screenProps = (_a7 = roomLayout == null ? void 0 : roomLayout.screens) == null ? void 0 : _a7[screen];
  return screenProps;
}
function useRoomLayoutPreviewScreen() {
  var _a7;
  const screenProps = useRoomLayoutScreen({ screen: "preview" });
  const isPreviewScreenEnabled = !!screenProps;
  let elements;
  let screenType;
  if (isPreviewScreenEnabled) {
    screenType = Object.keys(screenProps)[0];
    elements = (_a7 = screenProps[screenType]) == null ? void 0 : _a7.elements;
  }
  return {
    isPreviewScreenEnabled,
    elements,
    screenType
  };
}
function useRoomLayoutConferencingScreen() {
  var _a7, _b7;
  const screenProps = useRoomLayoutScreen({ screen: "conferencing" }) || {};
  const screenType = Object.keys(screenProps)[0];
  const elements = (_a7 = screenProps[screenType]) == null ? void 0 : _a7.elements;
  const hideSections = ((_b7 = screenProps[screenType]) == null ? void 0 : _b7.hideSections) || [];
  return {
    hideSections,
    elements,
    screenType
  };
}
function useRoomLayoutLeaveScreen() {
  const screenProps = useRoomLayoutScreen({ screen: "leave" });
  const isLeaveScreenEnabled = !!screenProps;
  return {
    isLeaveScreenEnabled
  };
}

// src/Prebuilt/components/hooks/useRedirectToLeave.tsx
var useRedirectToLeave = () => {
  const { isLeaveScreenEnabled } = useRoomLayoutLeaveScreen();
  const { onLeave } = useHMSPrebuiltContext();
  const params = useParams();
  const navigate = useNavigate();
  const redirect = useCallback7(
    (timeout = 0) => {
      setTimeout(() => {
        const prefix = isLeaveScreenEnabled ? "/leave/" : "/";
        if (params.role) {
          navigate(prefix + params.roomId + "/" + params.role);
        } else {
          navigate(prefix + params.roomId);
        }
        PictureInPicture.stop().catch(() => console.error("stopping pip"));
        ToastManager.clearAllToast();
        onLeave == null ? void 0 : onLeave();
      }, timeout);
    },
    [isLeaveScreenEnabled, navigate, onLeave, params.role, params.roomId]
  );
  return { redirectToLeave: redirect };
};

// src/Prebuilt/common/utils.js
var getMetadata = (metadataString) => {
  try {
    return !metadataString ? {} : JSON.parse(metadataString);
  } catch (error) {
    return {};
  }
};
var isScreenshareSupported = () => {
  return typeof navigator.mediaDevices.getDisplayMedia !== "undefined";
};
var getFormattedCount = (num) => {
  const formatter = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 2 });
  const formattedNum = formatter.format(num);
  return formattedNum;
};
var formatTime = (timeInSeconds) => {
  timeInSeconds = Math.floor(timeInSeconds / 1e3);
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor(timeInSeconds % 3600 / 60);
  const seconds = timeInSeconds % 60;
  const hour = hours !== 0 ? `${hours < 10 ? "0" : ""}${hours}:` : "";
  return `${hour}${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};
var getAttributeBoxSize = (width, height) => {
  if (!width || !height) {
    return "";
  }
  return width < 180 || height < 180 ? "small" : "medium";
};

// src/Prebuilt/components/Notifications/Notifications.jsx
function Notifications() {
  const notification = useHMSNotifications7();
  const navigate = useNavigate2();
  const params = useParams2();
  const subscribedNotifications = useSubscribedNotifications() || {};
  const roomState = useHMSStore7(selectRoomState2);
  const updateRoomLayoutForRole = useUpdateRoomLayout();
  const isNotificationDisabled = useIsNotificationDisabled();
  const { redirectToLeave } = useRedirectToLeave();
  const vanillaStore = useHMSVanillaStore4();
  const handleRoleChangeDenied = useCallback8((request) => {
    ToastManager.addToast({
      title: `${request.peerName} denied your request to join the ${request.role.name} role`,
      variant: "error"
    });
  }, []);
  useCustomEvent({ type: ROLE_CHANGE_DECLINED, onEvent: handleRoleChangeDenied });
  useEffect16(() => {
    var _a7, _b7, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
    if (!notification || isNotificationDisabled) {
      return;
    }
    switch (notification.type) {
      case HMSNotificationTypes7.HAND_RAISE_CHANGED: {
        if (roomState !== HMSRoomState2.Connected || notification.data.isLocal) {
          return;
        }
        const hasPeerHandRaised = vanillaStore.getState(selectHasPeerHandRaised(notification.data.id));
        if (hasPeerHandRaised) {
          ToastBatcher.showToast({ notification, type: "RAISE_HAND" });
        }
        break;
      }
      case HMSNotificationTypes7.METADATA_UPDATED:
        if (roomState !== HMSRoomState2.Connected) {
          return;
        }
        const metadata = getMetadata((_a7 = notification.data) == null ? void 0 : _a7.metadata);
        if (!(metadata == null ? void 0 : metadata.isHandRaised) || notification.data.isLocal)
          return;
        console.debug("Metadata updated", notification.data);
        if (!subscribedNotifications.METADATA_UPDATED)
          return;
        ToastBatcher.showToast({ notification, type: "RAISE_HAND" });
        break;
      case HMSNotificationTypes7.NAME_UPDATED:
        console.log(notification.data.id + " changed their name to " + notification.data.name);
        break;
      case HMSNotificationTypes7.ERROR:
        if (((_b7 = notification.data) == null ? void 0 : _b7.isTerminal) && ((_c = notification.data) == null ? void 0 : _c.action) !== "INIT") {
          if ([500, 6008].includes((_d = notification.data) == null ? void 0 : _d.code)) {
            ToastManager.addToast({
              title: `Error: ${(_e = notification.data) == null ? void 0 : _e.message}`
            });
          } else {
            const toastId = ToastManager.addToast({
              title: ((_f = notification.data) == null ? void 0 : _f.message) || "We couldn\u2019t reconnect you. When you\u2019re back online, try joining the room.",
              inlineAction: true,
              action: /* @__PURE__ */ React30.createElement(
                Button,
                {
                  onClick: () => {
                    ToastManager.removeToast(toastId);
                    navigate(`/${params.roomId}${params.role ? `/${params.role}` : ""}`);
                  }
                },
                "Rejoin"
              ),
              close: false
            });
          }
          redirectToLeave(1e3);
          return;
        }
        if (((_g = notification.data) == null ? void 0 : _g.code) === 3008 || ((_h = notification.data) == null ? void 0 : _h.code) === 3001 || ((_i = notification.data) == null ? void 0 : _i.code) === 3011) {
          return;
        }
        if (((_j = notification.data) == null ? void 0 : _j.action) === "INIT") {
          return;
        }
        if (!subscribedNotifications.ERROR)
          return;
        ToastManager.addToast({
          title: `Error: ${(_k = notification.data) == null ? void 0 : _k.message} - ${(_l = notification.data) == null ? void 0 : _l.description}`
        });
        break;
      case HMSNotificationTypes7.ROLE_UPDATED: {
        if ((_m = notification.data) == null ? void 0 : _m.isLocal) {
          ToastManager.addToast({
            title: `You are now a ${notification.data.roleName}`
          });
          updateRoomLayoutForRole(notification.data.roleName);
        }
        break;
      }
      case HMSNotificationTypes7.CHANGE_TRACK_STATE_REQUEST:
        const track = (_n = notification.data) == null ? void 0 : _n.track;
        if (!notification.data.enabled) {
          ToastManager.addToast({
            title: `Your ${track.source} ${track.type} was muted by
                ${(_o = notification.data.requestedBy) == null ? void 0 : _o.name}.`
          });
        }
        break;
      case HMSNotificationTypes7.REMOVED_FROM_ROOM:
      case HMSNotificationTypes7.ROOM_ENDED:
        ToastManager.addToast({
          title: `${notification.message}. 
              ${notification.data.reason && `Reason: ${notification.data.reason}`}`
        });
        redirectToLeave(1e3);
        break;
      case HMSNotificationTypes7.DEVICE_CHANGE_UPDATE:
        ToastManager.addToast({
          title: notification.message
        });
        break;
      default:
        break;
    }
  }, [notification, subscribedNotifications.ERROR, subscribedNotifications.METADATA_UPDATED]);
  if (isNotificationDisabled) {
    return null;
  }
  return /* @__PURE__ */ React30.createElement(React30.Fragment, null, /* @__PURE__ */ React30.createElement(TrackUnmuteModal, null), /* @__PURE__ */ React30.createElement(TrackBulkUnmuteModal, null), /* @__PURE__ */ React30.createElement(TrackNotifications, null), roomState === HMSRoomState2.Connected ? /* @__PURE__ */ React30.createElement(PeerNotifications, null) : null, /* @__PURE__ */ React30.createElement(ReconnectNotifications, null), /* @__PURE__ */ React30.createElement(AutoplayBlockedModal, null), /* @__PURE__ */ React30.createElement(PermissionErrorModal, null), /* @__PURE__ */ React30.createElement(InitErrorModal, { notification }));
}

// src/Prebuilt/components/PostLeave.jsx
import React37 from "react";
import { useNavigate as useNavigate3, useParams as useParams3 } from "react-router-dom";
import { ExitIcon } from "@100mslive/react-icons";

// src/Prebuilt/components/Header/Header.tsx
import React36 from "react";
import { useMedia as useMedia3 } from "react-use";
import { HMSRoomState as HMSRoomState4, selectRoomState as selectRoomState4, useHMSStore as useHMSStore16 } from "@100mslive/react-sdk";

// src/Prebuilt/components/Header/HeaderComponents.jsx
import React31, { useEffect as useEffect17, useState as useState11 } from "react";
import { selectDominantSpeaker, useHMSStore as useHMSStore8 } from "@100mslive/react-sdk";
import { VolumeOneIcon } from "@100mslive/react-icons";
var SpeakerTag = () => {
  const dominantSpeaker = useHMSStore8(selectDominantSpeaker);
  return dominantSpeaker && dominantSpeaker.name && /* @__PURE__ */ React31.createElement(
    Flex,
    {
      align: "center",
      justify: "center",
      css: { flex: "1 1 0", color: "$on_primary_high", "@md": { display: "none" } }
    },
    /* @__PURE__ */ React31.createElement(VerticalDivider, { css: { ml: "$8" } }),
    /* @__PURE__ */ React31.createElement(VolumeOneIcon, null),
    /* @__PURE__ */ React31.createElement(Text, { variant: "md", css: __spreadProps(__spreadValues({}, textEllipsis(200)), { ml: "$2" }), title: dominantSpeaker.name }, dominantSpeaker.name)
  );
};
var LogoImg = styled("img", {
  maxHeight: "$14",
  w: "auto",
  objectFit: "contain",
  "@md": {
    maxHeight: "$12"
  }
});
var Logo = () => {
  var _a7;
  const roomLayout = useRoomLayout();
  const logo = (_a7 = roomLayout == null ? void 0 : roomLayout.logo) == null ? void 0 : _a7.url;
  const [hideImage, setHideImage] = useState11(false);
  useEffect17(() => {
    if (hideImage) {
      setHideImage(false);
    }
  }, [logo]);
  return logo && !hideImage ? /* @__PURE__ */ React31.createElement(
    LogoImg,
    {
      src: logo,
      alt: "Brand Logo",
      onError: (e) => {
        e.target.onerror = null;
        setHideImage(true);
      }
    }
  ) : null;
};

// src/Prebuilt/components/Header/StreamActions.tsx
import React34, { useCallback as useCallback13, useEffect as useEffect19, useRef as useRef5, useState as useState13 } from "react";
import { useMedia as useMedia2 } from "react-use";
import {
  HMSRoomState as HMSRoomState3,
  selectHLSState as selectHLSState2,
  selectIsConnectedToRoom as selectIsConnectedToRoom2,
  selectPermissions,
  selectRoomState as selectRoomState3,
  useHMSActions as useHMSActions12,
  useHMSStore as useHMSStore14,
  useRecordingStreaming as useRecordingStreaming2
} from "@100mslive/react-sdk";
import { AlertTriangleIcon, CrossIcon as CrossIcon4, RecordIcon } from "@100mslive/react-icons";

// src/Sheet/Sheet.tsx
import React32 from "react";
import * as DialogPrimitive2 from "@radix-ui/react-dialog";
var SheetRoot = styled(DialogPrimitive2.Root, {
  minHeight: "240px",
  maxWidth: "100%"
});
var SheetTrigger = styled(DialogPrimitive2.Trigger, {
  appearance: "none !important"
  // Needed for safari it shows white overlay
});
var StyledOverlay = styled(Dialog.Overlay, {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  '&[data-state="open"]': {
    animation: `${sheetFadeIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
  },
  '&[data-state="closed"]': {
    animation: `${sheetFadeOut} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
  }
});
var StyledContent6 = styled(DialogPrimitive2.Content, {
  color: "$on_surface_medium",
  backgroundColor: "$surface_default",
  borderTopLeftRadius: "$3",
  borderTopRightRadius: "$3",
  boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  position: "fixed",
  zIndex: 1001,
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  maxHeight: "96%",
  // Among other things, prevents text alignment inconsistencies when dialog can't be centered in the viewport evenly.
  // Affects animated and non-animated dialogs alike.
  willChange: "transform",
  "&:focus": {
    outline: "none"
  },
  "@allowMotion": {
    '&[data-state="open"]': {
      animation: `${sheetSlideIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
    },
    '&[data-state="closed"]': {
      animation: `${sheetSlideOut} 150ms cubic-bezier(0.22, 1, 0.36, 1)`
    }
  },
  variants: {
    side: {
      top: {
        $$transformValue: "translate3d(0,-100%,0)",
        bottom: "auto"
      },
      right: {
        $$transformValue: "translate3d(100%,0,0)",
        right: 0
      },
      bottom: {
        $$transformValue: "translate3d(0,100%,0)",
        bottom: 0,
        top: "auto"
      },
      left: {
        $$transformValue: "translate3d(-100%,0,0)",
        left: 0
      }
    }
  },
  defaultVariants: {
    side: "bottom"
  }
});
var SheetContent = React32.forwardRef(
  (_a7, forwardedRef) => {
    var _b7 = _a7, { children } = _b7, props = __objRest(_b7, ["children"]);
    return /* @__PURE__ */ React32.createElement(Dialog.Portal, null, /* @__PURE__ */ React32.createElement(StyledOverlay, null), /* @__PURE__ */ React32.createElement(StyledContent6, __spreadProps(__spreadValues({}, props), { ref: forwardedRef }), children));
  }
);
var SheetClose = Dialog.Close;
var SheetTitle = styled(DialogPrimitive2.Title, {
  margin: 0
});
var SheetDescription = Dialog.Description;
var SheetDefaultCloseIcon = Dialog.DefaultClose;
var Sheet = {
  Root: SheetRoot,
  Trigger: SheetTrigger,
  Content: SheetContent,
  Description: SheetDescription,
  Title: SheetTitle,
  Close: SheetClose,
  DefaultClose: SheetDefaultCloseIcon
};

// src/Prebuilt/components/Header/AdditionalRoomState.jsx
import React33, { useState as useState12 } from "react";
import { selectLocalPeerID as selectLocalPeerID3, selectPeerSharingVideoPlaylist, useHMSStore as useHMSStore13, useScreenShare } from "@100mslive/react-sdk";
import {
  AudioPlayerIcon,
  ChevronDownIcon as ChevronDownIcon2,
  ChevronUpIcon as ChevronUpIcon2,
  MusicIcon,
  PencilDrawIcon,
  ShareScreenIcon,
  VideoPlayerIcon
} from "@100mslive/react-icons";

// src/Prebuilt/plugins/whiteboard/useWhiteboardMetadata.js
import { useCallback as useCallback10, useEffect as useEffect18, useMemo as useMemo2 } from "react";
import { selectLocalPeerID as selectLocalPeerID2, selectPeerByCondition, useHMSStore as useHMSStore10 } from "@100mslive/react-sdk";

// src/Prebuilt/components/hooks/useMetadata.jsx
import { useCallback as useCallback9 } from "react";
import {
  selectHasPeerHandRaised as selectHasPeerHandRaised2,
  selectLocalPeerID,
  selectPeerMetadata,
  useHMSActions as useHMSActions9,
  useHMSStore as useHMSStore9,
  useHMSVanillaStore as useHMSVanillaStore5
} from "@100mslive/react-sdk";
var useMyMetadata = () => {
  const hmsActions = useHMSActions9();
  const localPeerId = useHMSStore9(selectLocalPeerID);
  const vanillaStore = useHMSVanillaStore5();
  const metaData = useHMSStore9(selectPeerMetadata(localPeerId));
  const isHandRaised = useHMSStore9(selectHasPeerHandRaised2(localPeerId));
  const update = (updatedFields) => __async(void 0, null, function* () {
    try {
      const currentMetadata = vanillaStore.getState(selectPeerMetadata(localPeerId));
      yield hmsActions.changeMetadata(Object.assign(currentMetadata, updatedFields));
      return true;
    } catch (error) {
      console.error("failed to update metadata ", updatedFields);
    }
  });
  const toggleHandRaise = useCallback9(() => __async(void 0, null, function* () {
    if (isHandRaised) {
      yield hmsActions.lowerLocalPeerHand();
    } else {
      yield hmsActions.raiseLocalPeerHand();
      yield update({ isBRBOn: false });
    }
  }), [isHandRaised]);
  const toggleBRB = useCallback9(() => __async(void 0, null, function* () {
    const newValue = !(metaData == null ? void 0 : metaData.isBRBOn);
    yield update({ isBRBOn: newValue });
    if (newValue) {
      yield hmsActions.lowerLocalPeerHand();
    }
  }), [metaData == null ? void 0 : metaData.isBRBOn]);
  const setPrevRole = (role) => __async(void 0, null, function* () {
    yield update({
      prevRole: role
    });
  });
  return {
    isHandRaised,
    isBRBOn: !!(metaData == null ? void 0 : metaData.isBRBOn),
    metaData,
    updateMetaData: update,
    toggleHandRaise,
    toggleBRB,
    setPrevRole
  };
};

// src/Prebuilt/plugins/whiteboard/useWhiteboardMetadata.js
var isWhiteboardOwner = (peer) => {
  return !!getMetadata(peer == null ? void 0 : peer.metadata).whiteboardOwner;
};
var useWhiteboardMetadata = () => {
  const localPeerID = useHMSStore10(selectLocalPeerID2);
  const { updateMetaData } = useMyMetadata();
  const whiteboardOwner = useHMSStore10(selectPeerByCondition(isWhiteboardOwner));
  const amIWhiteboardOwner = useMemo2(() => localPeerID === (whiteboardOwner == null ? void 0 : whiteboardOwner.id), [localPeerID, whiteboardOwner]);
  const toggleWhiteboard = useCallback10(() => __async(void 0, null, function* () {
    if (!define_process_env_default.REACT_APP_PUSHER_APP_KEY) {
      console.error("Cannot start whiteboard - Pusher Key unavailable");
    }
    try {
      if (!whiteboardOwner || amIWhiteboardOwner) {
        yield updateMetaData({ whiteboardOwner: !whiteboardOwner });
      } else {
        console.warn("Cannot toggle whiteboard as it was shared by another peer");
      }
    } catch (error) {
      console.error("failed to toggle whiteboard to ", !whiteboardOwner, error);
    }
  }), [whiteboardOwner, updateMetaData, amIWhiteboardOwner]);
  useEffect18(() => {
    window.toggleWhiteboard = toggleWhiteboard;
  }, [toggleWhiteboard]);
  return {
    /** is whiteboard enabled for the room */
    whiteboardEnabled: FeatureFlags.enableWhiteboard,
    /** owner of the active whiteboard, can also be used to check if whiteboard is active */
    whiteboardOwner,
    amIWhiteboardOwner,
    toggleWhiteboard
  };
};

// src/Prebuilt/components/hooks/usePlaylistMusic.js
import { useCallback as useCallback11 } from "react";
import {
  selectAudioPlaylist,
  selectAudioPlaylistTrackByPeerID,
  selectPeerSharingAudioPlaylist,
  useHMSActions as useHMSActions10,
  useHMSStore as useHMSStore11
} from "@100mslive/react-sdk";
var usePlaylistMusic = () => {
  const peer = useHMSStore11(selectPeerSharingAudioPlaylist);
  const track = useHMSStore11(selectAudioPlaylistTrackByPeerID(peer == null ? void 0 : peer.id));
  const selection = useHMSStore11(selectAudioPlaylist.selectedItem);
  const hmsActions = useHMSActions10();
  const play = useCallback11(
    (selectedId) => __async(void 0, null, function* () {
      yield hmsActions.audioPlaylist.play(selectedId);
    }),
    [hmsActions]
  );
  const pause = useCallback11(() => {
    hmsActions.audioPlaylist.pause();
  }, [hmsActions]);
  const setVolume = useCallback11(
    (value) => {
      hmsActions.setVolume(value, track == null ? void 0 : track.id);
    },
    [hmsActions, track]
  );
  return { selection, peer, track, play, pause, setVolume };
};

// src/Prebuilt/components/hooks/useScreenshareAudio.js
import { useCallback as useCallback12 } from "react";
import {
  selectPeerSharingAudio,
  selectScreenShareAudioByPeerID,
  useHMSActions as useHMSActions11,
  useHMSStore as useHMSStore12
} from "@100mslive/react-sdk";
var useScreenshareAudio = () => {
  const hmsActions = useHMSActions11();
  const peer = useHMSStore12(selectPeerSharingAudio);
  const track = useHMSStore12(selectScreenShareAudioByPeerID(peer == null ? void 0 : peer.id));
  const handleMute = useCallback12(() => {
    if (!peer.isLocal) {
      hmsActions.setVolume(!track.volume ? 100 : 0, track.id);
    } else {
      hmsActions.setEnabledTrack(track.id, !track.enabled).catch(console.error);
    }
  }, [peer, hmsActions, track]);
  let muted = void 0;
  if (peer && track) {
    muted = peer.isLocal ? !track.enabled : track.volume === 0;
  }
  return { peer, track, muted, onToggle: handleMute };
};

// src/Prebuilt/components/Header/AdditionalRoomState.jsx
var getRecordingText = ({ isBrowserRecordingOn, isServerRecordingOn, isHLSRecordingOn }, delimiter = ", ") => {
  if (!isBrowserRecordingOn && !isServerRecordingOn && !isHLSRecordingOn) {
    return "";
  }
  const title = [];
  if (isBrowserRecordingOn) {
    title.push("Browser");
  }
  if (isServerRecordingOn) {
    title.push("Server");
  }
  if (isHLSRecordingOn) {
    title.push("HLS");
  }
  return title.join(delimiter);
};
var AdditionalRoomState = () => {
  var _a7, _b7, _c, _d, _e;
  const playlist = usePlaylistMusic();
  const isAudioOnly = useUISettings(UI_SETTINGS.isAudioOnly);
  const screenshareAudio = useScreenshareAudio();
  const [open, setOpen] = useState12(false);
  const isPlaylistInactive = [
    !playlist.peer || !playlist.track,
    !((_a7 = playlist.peer) == null ? void 0 : _a7.isLocal) && !((_b7 = playlist.track) == null ? void 0 : _b7.enabled),
    ((_c = playlist.peer) == null ? void 0 : _c.isLocal) && !playlist.selection
  ].some(Boolean);
  const isAudioshareInactive = [
    !screenshareAudio.peer || !screenshareAudio.track,
    !((_d = screenshareAudio.peer) == null ? void 0 : _d.isLocal) && !((_e = screenshareAudio.track) == null ? void 0 : _e.enabled)
  ].some(Boolean);
  const peerSharingPlaylist = useHMSStore13(selectPeerSharingVideoPlaylist);
  const localPeerID = useHMSStore13(selectLocalPeerID3);
  const isVideoPlayListPlaying = !!(peerSharingPlaylist == null ? void 0 : peerSharingPlaylist.id);
  const { screenSharingPeerName, screenSharingPeerId, screenShareVideoTrackId } = useScreenShare();
  const isVideoScreenSharingOn = !!screenShareVideoTrackId;
  const { whiteboardOwner, amIWhiteboardOwner, toggleWhiteboard } = useWhiteboardMetadata();
  const shouldShowScreenShareState = isAudioOnly && isVideoScreenSharingOn;
  const shouldShowVideoState = isAudioOnly && isVideoPlayListPlaying;
  if (isPlaylistInactive && isAudioshareInactive && !shouldShowScreenShareState && !shouldShowVideoState && !whiteboardOwner) {
    return null;
  }
  return /* @__PURE__ */ React33.createElement(Dropdown.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React33.createElement(Dropdown.Trigger, { asChild: true }, /* @__PURE__ */ React33.createElement(
    Flex,
    {
      align: "center",
      css: {
        color: "$on_primary_high",
        borderRadius: "$1",
        border: "1px solid $on_surface_low",
        padding: "$4",
        "@sm": { display: "none" }
      },
      "data-testid": "record_status_dropdown"
    },
    !isAudioshareInactive && /* @__PURE__ */ React33.createElement(Tooltip, { title: "Screenshare Audio" }, /* @__PURE__ */ React33.createElement(Flex, { align: "center", css: { color: "$on_primary_high", mx: "$2" } }, /* @__PURE__ */ React33.createElement(MusicIcon, { width: 24, height: 24 }))),
    shouldShowScreenShareState && /* @__PURE__ */ React33.createElement(Tooltip, { title: "Screenshare" }, /* @__PURE__ */ React33.createElement(Flex, { align: "center", css: { color: "$on_primary_high", mx: "$2" } }, /* @__PURE__ */ React33.createElement(ShareScreenIcon, { width: 24, height: 24 }))),
    shouldShowVideoState && /* @__PURE__ */ React33.createElement(Tooltip, { title: "video playlist" }, /* @__PURE__ */ React33.createElement(Flex, { align: "center", css: { color: "$on_primary_high", mx: "$2" } }, /* @__PURE__ */ React33.createElement(VideoPlayerIcon, { width: 24, height: 24 }))),
    !isPlaylistInactive && /* @__PURE__ */ React33.createElement(Tooltip, { title: "Playlist Music" }, /* @__PURE__ */ React33.createElement(Flex, { align: "center", css: { color: "$on_primary_high", mx: "$2" } }, /* @__PURE__ */ React33.createElement(AudioPlayerIcon, { width: 24, height: 24 }))),
    whiteboardOwner && /* @__PURE__ */ React33.createElement(Tooltip, { title: "Whiteboard" }, /* @__PURE__ */ React33.createElement(Flex, { align: "center", css: { color: "$on_primary_high", mx: "$2" } }, /* @__PURE__ */ React33.createElement(PencilDrawIcon, { width: 24, height: 24 }))),
    /* @__PURE__ */ React33.createElement(Box, { css: { "@lg": { display: "none" }, color: "$on_surface_low" } }, open ? /* @__PURE__ */ React33.createElement(ChevronUpIcon2, null) : /* @__PURE__ */ React33.createElement(ChevronDownIcon2, null))
  )), /* @__PURE__ */ React33.createElement(Dropdown.Content, { sideOffset: 5, align: "end", css: { w: "$60" } }, !isPlaylistInactive && /* @__PURE__ */ React33.createElement(Dropdown.Item, { css: { color: "$on_primary_high" } }, /* @__PURE__ */ React33.createElement(AudioPlayerIcon, { width: 24, height: 24 }), /* @__PURE__ */ React33.createElement(Text, { variant: "sm", css: { ml: "$2", flex: "1 1 0" } }, "Playlist is playing"), playlist.peer.isLocal ? /* @__PURE__ */ React33.createElement(
    Text,
    {
      variant: "sm",
      css: { color: "$alert_error_default", cursor: "pointer", ml: "$2" },
      onClick: (e) => {
        e.preventDefault();
        playlist.selection.playing ? playlist.pause() : playlist.play(playlist.selection.id);
      }
    },
    playlist.selection.playing ? "Pause" : "Play"
  ) : /* @__PURE__ */ React33.createElement(
    Text,
    {
      variant: "sm",
      css: { color: "$alert_error_default", ml: "$2", cursor: "pointer" },
      onClick: (e) => {
        e.preventDefault();
        playlist.setVolume(!playlist.track.volume ? 100 : 0);
      }
    },
    playlist.track.volume === 0 ? "Unmute" : "Mute"
  )), !isAudioshareInactive && /* @__PURE__ */ React33.createElement(Dropdown.Item, { css: { color: "$on_primary_high" } }, /* @__PURE__ */ React33.createElement(MusicIcon, { width: 24, height: 24 }), /* @__PURE__ */ React33.createElement(Text, { variant: "sm", css: { ml: "$2", flex: "1 1 0" } }, "Music is playing"), /* @__PURE__ */ React33.createElement(
    Text,
    {
      variant: "sm",
      css: { color: "$alert_error_default", ml: "$2", cursor: "pointer" },
      onClick: (e) => {
        e.preventDefault();
        screenshareAudio.onToggle();
      }
    },
    screenshareAudio.muted ? "Unmute" : "Mute"
  )), shouldShowScreenShareState && /* @__PURE__ */ React33.createElement(Dropdown.Item, { css: { color: "$on_primary_high" } }, /* @__PURE__ */ React33.createElement(ShareScreenIcon, { width: 24, height: 24 }), /* @__PURE__ */ React33.createElement(Text, { variant: "sm", css: { ml: "$2", flex: "1 1 0" } }, `Shared by: ${screenSharingPeerId === localPeerID ? "You" : screenSharingPeerName}`)), shouldShowVideoState && /* @__PURE__ */ React33.createElement(Dropdown.Item, { css: { color: "$on_primary_high" } }, /* @__PURE__ */ React33.createElement(VideoPlayerIcon, { width: 24, height: 24 }), /* @__PURE__ */ React33.createElement(Text, { variant: "sm", css: { ml: "$2", flex: "1 1 0" } }, `Shared by: ${peerSharingPlaylist.id === localPeerID ? "You" : peerSharingPlaylist.name}`)), whiteboardOwner && /* @__PURE__ */ React33.createElement(Dropdown.Item, { css: { color: "$on_primary_high" } }, /* @__PURE__ */ React33.createElement(PencilDrawIcon, { width: 24, height: 24 }), /* @__PURE__ */ React33.createElement(Text, { variant: "sm", css: { ml: "$2", flex: "1 1 0" } }, "Whiteboard Owner - ", whiteboardOwner.name, amIWhiteboardOwner && "(You)"), amIWhiteboardOwner && /* @__PURE__ */ React33.createElement(
    Text,
    {
      variant: "sm",
      css: { color: "$alert_error_default", ml: "$2", cursor: "pointer" },
      onClick: (e) => {
        e.preventDefault();
        toggleWhiteboard();
      }
    },
    "Stop"
  ))));
};

// src/Prebuilt/components/Header/StreamActions.tsx
var LiveStatus = () => {
  var _a7;
  const { isHLSRunning, isRTMPRunning } = useRecordingStreaming2();
  const hlsState = useHMSStore14(selectHLSState2);
  const isMobile = useMedia2(config.media.md);
  const intervalRef = useRef5(null);
  const [liveTime, setLiveTime] = useState13(0);
  const startTimer = useCallback13(() => {
    intervalRef.current = setInterval(() => {
      var _a8;
      if ((hlsState == null ? void 0 : hlsState.running) && ((_a8 = hlsState == null ? void 0 : hlsState.variants[0]) == null ? void 0 : _a8.startedAt)) {
        setLiveTime(Date.now() - hlsState.variants[0].startedAt.getTime());
      }
    }, 1e3);
  }, [hlsState == null ? void 0 : hlsState.running, hlsState == null ? void 0 : hlsState.variants]);
  useEffect19(() => {
    if (hlsState == null ? void 0 : hlsState.running) {
      startTimer();
    }
    if (!(hlsState == null ? void 0 : hlsState.running) && intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hlsState.running, isMobile, startTimer]);
  if (!isHLSRunning && !isRTMPRunning) {
    return null;
  }
  return /* @__PURE__ */ React34.createElement(
    Flex,
    {
      align: "center",
      gap: "1",
      css: {
        border: "1px solid $border_default",
        padding: "$4 $6 $4 $6",
        borderRadius: "$1"
      }
    },
    /* @__PURE__ */ React34.createElement(Box, { css: { w: "$4", h: "$4", r: "$round", bg: "$alert_error_default", mr: "$2" } }),
    /* @__PURE__ */ React34.createElement(Flex, { align: "center", gap: "2" }, /* @__PURE__ */ React34.createElement(Text, { variant: !isMobile ? "button" : "body2" }, "LIVE"), /* @__PURE__ */ React34.createElement(Text, { variant: "caption" }, ((_a7 = hlsState == null ? void 0 : hlsState.variants) == null ? void 0 : _a7.length) > 0 ? formatTime(liveTime) : ""))
  );
};
var RecordingStatus = () => {
  const { isBrowserRecordingOn, isServerRecordingOn, isHLSRecordingOn, isRecordingOn } = useRecordingStreaming2();
  const permissions = useHMSStore14(selectPermissions);
  const isMobile = useMedia2(config.media.md);
  if (!isRecordingOn || // if only browser recording is enabled, stop recording is shown
  // so no need to show this as it duplicates
  [permissions == null ? void 0 : permissions.browserRecording, !isServerRecordingOn, !isHLSRecordingOn, isBrowserRecordingOn].every(
    (value) => !!value
  )) {
    if (!(isMobile && isRecordingOn))
      return null;
  }
  return /* @__PURE__ */ React34.createElement(
    Tooltip,
    {
      boxCss: { zIndex: 1 },
      title: getRecordingText({
        isBrowserRecordingOn,
        isServerRecordingOn,
        isHLSRecordingOn
      })
    },
    /* @__PURE__ */ React34.createElement(
      Flex,
      {
        css: {
          color: "$alert_error_default",
          alignItems: "center"
        }
      },
      /* @__PURE__ */ React34.createElement(RecordIcon, { width: 24, height: 24 })
    )
  );
};
var StartRecording = () => {
  const permissions = useHMSStore14(selectPermissions);
  const [open, setOpen] = useState13(false);
  const [recordingStarted, setRecordingState] = useSetAppDataByKey(APP_DATA.recordingStarted);
  const { isBrowserRecordingOn, isStreamingOn, isHLSRunning } = useRecordingStreaming2();
  const hmsActions = useHMSActions12();
  if (!(permissions == null ? void 0 : permissions.browserRecording) || isHLSRunning) {
    return null;
  }
  if (isBrowserRecordingOn) {
    return /* @__PURE__ */ React34.createElement(Popover2.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React34.createElement(Popover2.Trigger, { asChild: true }, /* @__PURE__ */ React34.createElement(Button, { variant: "danger", "data-testid": "stop_recording", icon: true, outlined: true, onClick: () => setOpen(true) }, /* @__PURE__ */ React34.createElement(RecordIcon, null), /* @__PURE__ */ React34.createElement(Text, { as: "span", css: { "@md": { display: "none" }, color: "currentColor" } }, "Stop Recording"))), /* @__PURE__ */ React34.createElement(Popover2.Portal, null, /* @__PURE__ */ React34.createElement(Popover2.Content, { align: "end", sideOffset: 8, css: { w: "$64" } }, /* @__PURE__ */ React34.createElement(Text, { variant: "body1", css: { color: "$on_surface_medium" } }, "Are you sure you want to end the recording?"), /* @__PURE__ */ React34.createElement(
      Button,
      {
        "data-testid": "stop_recording_confirm",
        variant: "danger",
        icon: true,
        css: { ml: "auto" },
        onClick: () => __async(void 0, null, function* () {
          try {
            yield hmsActions.stopRTMPAndRecording();
          } catch (error) {
            const err = error;
            ToastManager.addToast({
              title: err.message,
              variant: "error"
            });
          }
          setOpen(false);
        })
      },
      "Stop"
    ))));
  }
  return /* @__PURE__ */ React34.createElement(
    Button,
    {
      "data-testid": "start_recording",
      variant: "standard",
      icon: true,
      disabled: recordingStarted || isStreamingOn,
      onClick: () => __async(void 0, null, function* () {
        try {
          setRecordingState(true);
          yield hmsActions.startRTMPOrRecording({
            record: true
          });
        } catch (error) {
          const err = error;
          if (err.message.includes("stream already running")) {
            ToastManager.addToast({
              title: "Recording already running",
              variant: "error"
            });
          } else {
            ToastManager.addToast({
              title: err.message,
              variant: "error"
            });
          }
          setRecordingState(false);
        }
      })
    },
    recordingStarted ? /* @__PURE__ */ React34.createElement(Loading, { size: 24, color: "currentColor" }) : /* @__PURE__ */ React34.createElement(RecordIcon, null),
    /* @__PURE__ */ React34.createElement(Text, { as: "span", css: { "@md": { display: "none" }, color: "currentColor" } }, recordingStarted ? "Starting" : "Start", " Recording")
  );
};
var StreamActions = () => {
  const isConnected = useHMSStore14(selectIsConnectedToRoom2);
  const isMobile = useMedia2(config.media.md);
  const roomState = useHMSStore14(selectRoomState3);
  return /* @__PURE__ */ React34.createElement(Flex, { align: "center", css: { gap: "$4" } }, /* @__PURE__ */ React34.createElement(AdditionalRoomState, null), !isMobile && /* @__PURE__ */ React34.createElement(Flex, { align: "center", css: { gap: "$4" } }, /* @__PURE__ */ React34.createElement(RecordingStatus, null), roomState !== HMSRoomState3.Preview ? /* @__PURE__ */ React34.createElement(LiveStatus, null) : null), isConnected && !isMobile ? /* @__PURE__ */ React34.createElement(StartRecording, null) : null);
};
var StopRecordingInSheet = ({
  onStopRecording,
  onClose
}) => {
  return /* @__PURE__ */ React34.createElement(Sheet.Root, { open: true }, /* @__PURE__ */ React34.createElement(Sheet.Content, null, /* @__PURE__ */ React34.createElement(Sheet.Title, { css: { p: "$10" } }, /* @__PURE__ */ React34.createElement(Flex, { direction: "row", justify: "between", css: { w: "100%", c: "$alert_error_default" } }, /* @__PURE__ */ React34.createElement(Flex, { justify: "start", align: "center", gap: "3" }, /* @__PURE__ */ React34.createElement(AlertTriangleIcon, null), /* @__PURE__ */ React34.createElement(Text, { variant: "h5", css: { c: "$alert_error_default" } }, "Stop Recording")), /* @__PURE__ */ React34.createElement(Sheet.Close, { css: { color: "white" }, onClick: onClose }, /* @__PURE__ */ React34.createElement(CrossIcon4, null)))), /* @__PURE__ */ React34.createElement(HorizontalDivider, null), /* @__PURE__ */ React34.createElement(Box, { as: "div", css: { p: "$10", overflowY: "scroll", maxHeight: "70vh" } }, /* @__PURE__ */ React34.createElement(Text, { variant: "caption", css: { c: "$on_surface_medium", pb: "$8" } }, "Are you sure you want to stop recording? You can\u2019t undo this action."), /* @__PURE__ */ React34.createElement(
    Button,
    {
      variant: "danger",
      css: { width: "100%" },
      type: "submit",
      "data-testid": "popup_change_btn",
      onClick: onStopRecording
    },
    "Stop"
  ))));
};

// src/Prebuilt/components/Header/common.jsx
import React35 from "react";
import {
  DeviceType,
  selectIsLocalVideoEnabled as selectIsLocalVideoEnabled2,
  selectLocalVideoTrackID,
  selectVideoTrackByID,
  useDevices,
  useHMSActions as useHMSActions13,
  useHMSStore as useHMSStore15
} from "@100mslive/react-sdk";
import { CameraFlipIcon, CheckIcon as CheckIcon2, CrossIcon as CrossIcon5, SpeakerIcon } from "@100mslive/react-icons";
var CamaraFlipActions = () => {
  const actions = useHMSActions13();
  const { allDevices } = useDevices();
  const { videoInput } = allDevices;
  const isVideoOn = useHMSStore15(selectIsLocalVideoEnabled2);
  const videoTrackId = useHMSStore15(selectLocalVideoTrackID);
  const localVideoTrack = useHMSStore15(selectVideoTrackByID(videoTrackId));
  if (!videoInput || !(videoInput == null ? void 0 : videoInput.length) || !(localVideoTrack == null ? void 0 : localVideoTrack.facingMode)) {
    return null;
  }
  return /* @__PURE__ */ React35.createElement(Box, null, /* @__PURE__ */ React35.createElement(
    IconButton_default,
    {
      disabled: !isVideoOn,
      onClick: () => __async(void 0, null, function* () {
        try {
          yield actions.switchCamera();
        } catch (e) {
          ToastManager.addToast({
            title: `Error while flipping camera ${e.message || ""}`,
            variant: "error"
          });
        }
      })
    },
    /* @__PURE__ */ React35.createElement(CameraFlipIcon, null)
  ));
};
var AudioOutputActions = () => {
  var _a7;
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices();
  const { audioOutput } = allDevices;
  const shouldShowAudioOutput = "setSinkId" in HTMLMediaElement.prototype;
  const audioOutputFiltered = (_a7 = audioOutput == null ? void 0 : audioOutput.filter((item) => !!item.label)) != null ? _a7 : [];
  if (!shouldShowAudioOutput || !(audioOutputFiltered == null ? void 0 : audioOutputFiltered.length) > 0) {
    return null;
  }
  return /* @__PURE__ */ React35.createElement(
    AudioOutputSelectionSheet,
    {
      outputDevices: audioOutput,
      outputSelected: selectedDeviceIDs.outputDevices,
      onChange: (deviceId) => __async(void 0, null, function* () {
        try {
          yield updateDevice({
            deviceId,
            deviceType: DeviceType.audioOutput
          });
        } catch (e) {
          ToastManager.addToast({
            title: `Error while changing audio output ${e.message || ""}`,
            variant: "error"
          });
        }
      })
    },
    /* @__PURE__ */ React35.createElement(Box, null, /* @__PURE__ */ React35.createElement(IconButton_default, null, /* @__PURE__ */ React35.createElement(SpeakerIcon, null)))
  );
};
var AudioOutputSelectionSheet = ({ outputDevices, outputSelected, onChange, children }) => {
  return /* @__PURE__ */ React35.createElement(Sheet.Root, null, /* @__PURE__ */ React35.createElement(Sheet.Trigger, { asChild: true }, children), /* @__PURE__ */ React35.createElement(Sheet.Content, null, /* @__PURE__ */ React35.createElement(Sheet.Title, { css: { py: "$10", px: "$8", alignItems: "center" } }, /* @__PURE__ */ React35.createElement(Flex, { direction: "row", justify: "between", css: { w: "100%" } }, /* @__PURE__ */ React35.createElement(Text, { variant: "h6", css: { display: "flex" } }, "Audio Output"), /* @__PURE__ */ React35.createElement(Sheet.Close, null, /* @__PURE__ */ React35.createElement(IconButton_default, { as: "div", "data-testid": "dialog_cross_icon" }, /* @__PURE__ */ React35.createElement(CrossIcon5, null))))), /* @__PURE__ */ React35.createElement(HorizontalDivider, null), /* @__PURE__ */ React35.createElement(
    Flex,
    {
      direction: "column",
      css: {
        px: "$8",
        maxHeight: "80vh",
        overflowY: "scroll"
      }
    },
    outputDevices.map((audioDevice) => {
      return /* @__PURE__ */ React35.createElement(
        SelectWithLabel,
        {
          key: audioDevice.deviceId,
          label: audioDevice.label,
          id: audioDevice.deviceId,
          checked: audioDevice.deviceId === outputSelected,
          onChange: () => onChange(audioDevice.deviceId)
        }
      );
    })
  )));
};
var SelectWithLabel = ({ label, icon = /* @__PURE__ */ React35.createElement(React35.Fragment, null), checked, id, onChange }) => {
  return /* @__PURE__ */ React35.createElement(
    Flex,
    {
      align: "center",
      css: {
        my: "$2",
        py: "$8",
        w: "100%",
        borderBottom: "1px solid $border_default"
      },
      onClick: onChange
    },
    /* @__PURE__ */ React35.createElement(
      Label3,
      {
        htmlFor: id,
        css: {
          fontSize: "$md",
          fontWeight: "$semiBold",
          color: checked ? "$on_surface_high" : "$on_surface_low",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "$8",
          flex: "1 1 0"
        }
      },
      icon,
      label
    ),
    checked && /* @__PURE__ */ React35.createElement(CheckIcon2, { width: 24, height: 24 })
  );
};

// src/Prebuilt/components/Header/Header.tsx
var Header2 = () => {
  const roomState = useHMSStore16(selectRoomState4);
  const isMobile = useMedia3(config.media.md);
  if (roomState !== HMSRoomState4.Connected) {
    return /* @__PURE__ */ React36.createElement(React36.Fragment, null);
  }
  return /* @__PURE__ */ React36.createElement(Flex, { justify: "between", align: "center", css: { position: "relative", height: "100%" } }, /* @__PURE__ */ React36.createElement(Flex, { align: "center", gap: "2", css: { position: "absolute", left: "$10" } }, /* @__PURE__ */ React36.createElement(Logo, null), /* @__PURE__ */ React36.createElement(SpeakerTag, null), isMobile && /* @__PURE__ */ React36.createElement(Flex, { align: "center", css: { gap: "$4" } }, /* @__PURE__ */ React36.createElement(LiveStatus, null), /* @__PURE__ */ React36.createElement(RecordingStatus, null))), /* @__PURE__ */ React36.createElement(
    Flex,
    {
      align: "center",
      css: {
        position: "absolute",
        right: "$10",
        gap: "$4"
      }
    },
    /* @__PURE__ */ React36.createElement(StreamActions, null),
    isMobile ? /* @__PURE__ */ React36.createElement(React36.Fragment, null, /* @__PURE__ */ React36.createElement(CamaraFlipActions, null), /* @__PURE__ */ React36.createElement(AudioOutputActions, null)) : null
  ));
};

// src/Prebuilt/components/PostLeave.jsx
var PostLeave = () => {
  const navigate = useNavigate3();
  const { isPreviewScreenEnabled } = useRoomLayoutPreviewScreen();
  const { roomCode } = useHMSPrebuiltContext();
  const { roomId, role } = useParams3();
  const [previewPreference] = useUserPreferences(UserPreferencesKeys.PREVIEW, defaultPreviewPreference);
  return /* @__PURE__ */ React37.createElement(Flex, { direction: "column", css: { size: "100%" } }, /* @__PURE__ */ React37.createElement(Box, { css: { h: "$18", "@md": { h: "$17" } }, "data-testid": "header" }, /* @__PURE__ */ React37.createElement(Header2, null)), /* @__PURE__ */ React37.createElement(
    Flex,
    {
      justify: "center",
      direction: "column",
      align: "center",
      css: { bg: "$background_dim", flex: "1 1 0", position: "relative" }
    },
    /* @__PURE__ */ React37.createElement(Text, { variant: "h2", css: { fontWeight: "$semiBold" } }, "\u{1F44B}"),
    /* @__PURE__ */ React37.createElement(Text, { variant: "h4", css: { color: "$on_surface_high", fontWeight: "$semiBold", mt: "$12" } }, "You left the room"),
    /* @__PURE__ */ React37.createElement(
      Text,
      {
        variant: "body1",
        css: {
          color: "$on_surface_medium",
          mt: "$8",
          fontWeight: "$regular",
          textAlign: "center"
        }
      },
      "Have a nice day",
      previewPreference.name && /* @__PURE__ */ React37.createElement(Box, { as: "span", css: __spreadValues({}, textEllipsis(100)) }, ", ", previewPreference.name),
      "!"
    ),
    /* @__PURE__ */ React37.createElement(Flex, { css: { mt: "$14", gap: "$10", alignItems: "center" } }, /* @__PURE__ */ React37.createElement(Text, { variant: "body1", css: { color: "$on_surface_medium", fontWeight: "$regular" } }, "Left by mistake?"), /* @__PURE__ */ React37.createElement(
      Button,
      {
        onClick: () => {
          let redirectUrl = `${isPreviewScreenEnabled ? "/preview/" : "/meeting/"}${roomCode || roomId}`;
          if (role && roomId)
            redirectUrl += "/" + role;
          navigate(redirectUrl);
          ToastManager.clearAllToast();
        },
        "data-testid": "join_again_btn"
      },
      /* @__PURE__ */ React37.createElement(ExitIcon, null),
      /* @__PURE__ */ React37.createElement(Text, { css: { ml: "$3", fontWeight: "$semiBold", color: "inherit" } }, "Rejoin")
    ))
  ));
};
var PostLeave_default = PostLeave;

// src/Prebuilt/components/Preview/PreviewContainer.tsx
import React56 from "react";
import { useNavigate as useNavigate4, useParams as useParams4 } from "react-router-dom";
import { useSearchParam } from "react-use";

// src/Prebuilt/components/Preview/PreviewJoin.tsx
import React55, { Fragment as Fragment6, Suspense, useCallback as useCallback17, useEffect as useEffect24, useState as useState19 } from "react";
import { useMedia as useMedia10 } from "react-use";
import {
  HMSRoomState as HMSRoomState6,
  selectIsLocalVideoEnabled as selectIsLocalVideoEnabled5,
  selectLocalPeer,
  selectRoomState as selectRoomState6,
  selectVideoTrackByID as selectVideoTrackByID5,
  useAVToggle as useAVToggle2,
  useHMSStore as useHMSStore25,
  useParticipants,
  usePreviewJoin,
  useRecordingStreaming as useRecordingStreaming4
} from "@100mslive/react-sdk";
import { MicOffIcon as MicOffIcon4, SettingsIcon as SettingsIcon2 } from "@100mslive/react-icons";

// src/Prebuilt/components/AudioVideoToggle.jsx
import React39, { Fragment as Fragment2 } from "react";
import {
  DeviceType as DeviceType2,
  HMSRoomState as HMSRoomState5,
  selectLocalVideoTrackID as selectLocalVideoTrackID2,
  selectRoomState as selectRoomState5,
  selectVideoTrackByID as selectVideoTrackByID2,
  useAVToggle,
  useDevices as useDevices2,
  useHMSActions as useHMSActions14,
  useHMSStore as useHMSStore17
} from "@100mslive/react-sdk";
import { CameraFlipIcon as CameraFlipIcon2, MicOffIcon, MicOnIcon as MicOnIcon3, VideoOffIcon, VideoOnIcon } from "@100mslive/react-icons";

// src/Prebuilt/components/IconButtonWithOptions/IconButtonWithOptions.jsx
import React38 from "react";
import { VerticalMenuIcon } from "@100mslive/react-icons";
var IconSection = styled(IconButton_default, {
  w: "unset",
  h: "$14",
  p: "$4",
  r: "$1",
  borderTopRightRadius: 0,
  borderColor: "$border_default",
  borderBottomRightRadius: 0,
  position: "relative",
  "&:not([disabled]):focus-visible": {
    zIndex: 1
  },
  "@md": {
    mx: 0,
    borderTopRightRadius: "$1",
    borderBottomRightRadius: "$1"
  }
});
var OptionsSection = styled(IconButton_default, {
  w: "unset",
  h: "$14",
  p: "$4",
  r: "$1",
  borderTopLeftRadius: 0,
  borderColor: "$border_default",
  borderBottomLeftRadius: 0,
  borderLeftWidth: 0,
  position: "relative",
  "&:not([disabled]):focus-visible": {
    zIndex: 1
  },
  "@md": {
    display: "none"
  }
});
var IconButtonWithOptions = ({
  disabled = false,
  onDisabledClick = () => {
    return;
  },
  tooltipMessage = "",
  icon,
  options = [],
  active,
  onClick = () => {
    return;
  }
}) => {
  const bgCss = { backgroundColor: disabled ? "$surface_brighter" : active ? "$transparent" : "$secondary_dim" };
  const iconCss = { color: disabled ? "$on_surface_low" : active ? "$on_surface_high" : "$on_primary_high" };
  return /* @__PURE__ */ React38.createElement(Flex, null, /* @__PURE__ */ React38.createElement(IconSection, { css: bgCss, onClick }, /* @__PURE__ */ React38.createElement(Tooltip, { disabled: !tooltipMessage, title: tooltipMessage }, /* @__PURE__ */ React38.createElement(Flex, { align: "center", justify: "center", css: iconCss }, icon))), /* @__PURE__ */ React38.createElement(Dropdown.Root, null, /* @__PURE__ */ React38.createElement(
    Dropdown.Trigger,
    {
      asChild: true,
      onPointerDown: (e) => {
        if (disabled) {
          e.preventDefault();
          onDisabledClick();
        }
      }
    },
    /* @__PURE__ */ React38.createElement(OptionsSection, { css: bgCss }, /* @__PURE__ */ React38.createElement(Tooltip, { title: "View Options" }, /* @__PURE__ */ React38.createElement(Box, { css: iconCss }, /* @__PURE__ */ React38.createElement(VerticalMenuIcon, null))))
  ), /* @__PURE__ */ React38.createElement(
    Dropdown.Content,
    {
      sideOffset: 5,
      align: "center",
      css: {
        w: "$64",
        maxHeight: "$96",
        p: 0,
        border: "none"
      }
    },
    options.map((option, index) => /* @__PURE__ */ React38.createElement(
      Dropdown.Item,
      {
        key: option.title,
        css: {
          flexDirection: "column",
          alignItems: "flex-start",
          backgroundColor: option.active ? "$surface_bright" : "$surface_dim",
          borderTop: index === 0 ? "none" : "1px solid $border_default",
          "&:hover": {
            cursor: "pointer"
          },
          p: "0"
        }
      },
      option.content
    ))
  )));
};

// src/Prebuilt/components/AudioVideoToggle.jsx
var optionsCSS = { fontWeight: "$semiBold", color: "$on_surface_high", w: "100%", p: "$8" };
var AudioVideoToggle = ({ hideOptions = false }) => {
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices2();
  const { videoInput, audioInput } = allDevices;
  const formattedVideoInputList = videoInput == null ? void 0 : videoInput.map((videoInput2) => ({
    active: selectedDeviceIDs.videoInput === videoInput2.deviceId,
    content: /* @__PURE__ */ React39.createElement(
      Text,
      {
        variant: "sm",
        onClick: () => updateDevice({
          deviceType: DeviceType2.videoInput,
          deviceId: videoInput2.deviceId
        }),
        css: optionsCSS
      },
      videoInput2.label
    ),
    title: videoInput2.label
  }));
  const formattedAudioInputList = audioInput == null ? void 0 : audioInput.map((audioInput2) => ({
    active: selectedDeviceIDs.audioInput === audioInput2.deviceId,
    content: /* @__PURE__ */ React39.createElement(
      Text,
      {
        variant: "sm",
        onClick: () => updateDevice({
          deviceType: DeviceType2.audioInput,
          deviceId: audioInput2.deviceId
        }),
        css: optionsCSS
      },
      audioInput2.label
    ),
    title: audioInput2.label
  }));
  const { isLocalVideoEnabled, isLocalAudioEnabled, toggleAudio, toggleVideo } = useAVToggle();
  const actions = useHMSActions14();
  const videoTrackId = useHMSStore17(selectLocalVideoTrackID2);
  const localVideoTrack = useHMSStore17(selectVideoTrackByID2(videoTrackId));
  const roomState = useHMSStore17(selectRoomState5);
  const hasAudioDevices = (audioInput == null ? void 0 : audioInput.length) > 0;
  const hasVideoDevices = (videoInput == null ? void 0 : videoInput.length) > 0;
  return /* @__PURE__ */ React39.createElement(Fragment2, null, toggleAudio ? hideOptions || !hasAudioDevices ? /* @__PURE__ */ React39.createElement(Tooltip, { title: `Turn ${isLocalAudioEnabled ? "off" : "on"} audio (${isMacOS ? "\u2318" : "ctrl"} + d)` }, /* @__PURE__ */ React39.createElement(
    IconButton_default,
    {
      active: isLocalAudioEnabled,
      disabled: !toggleAudio,
      onClick: toggleAudio,
      key: "toggleAudio",
      "data-testid": "audio_btn",
      className: "__cancel-drag-event"
    },
    !isLocalAudioEnabled ? /* @__PURE__ */ React39.createElement(MicOffIcon, { "data-testid": "audio_off_btn" }) : /* @__PURE__ */ React39.createElement(MicOnIcon3, { "data-testid": "audio_on_btn" })
  )) : /* @__PURE__ */ React39.createElement(
    IconButtonWithOptions,
    {
      options: formattedAudioInputList,
      disabled: !toggleAudio,
      onDisabledClick: toggleAudio,
      tooltipMessage: `Turn ${isLocalAudioEnabled ? "off" : "on"} audio (${isMacOS ? "\u2318" : "ctrl"} + d)`,
      icon: !isLocalAudioEnabled ? /* @__PURE__ */ React39.createElement(MicOffIcon, { "data-testid": "audio_off_btn" }) : /* @__PURE__ */ React39.createElement(MicOnIcon3, { "data-testid": "audio_on_btn" }),
      active: isLocalAudioEnabled,
      onClick: toggleAudio,
      key: "toggleAudio"
    }
  ) : null, toggleVideo ? hideOptions || !hasVideoDevices ? /* @__PURE__ */ React39.createElement(Tooltip, { title: `Turn ${isLocalVideoEnabled ? "off" : "on"} video (${isMacOS ? "\u2318" : "ctrl"} + e)` }, /* @__PURE__ */ React39.createElement(
    IconButton_default,
    {
      key: "toggleVideo",
      active: isLocalVideoEnabled,
      disabled: !toggleVideo,
      onClick: toggleVideo,
      "data-testid": "video_btn",
      className: "__cancel-drag-event"
    },
    !isLocalVideoEnabled ? /* @__PURE__ */ React39.createElement(VideoOffIcon, { "data-testid": "video_off_btn" }) : /* @__PURE__ */ React39.createElement(VideoOnIcon, { "data-testid": "video_on_btn" })
  )) : /* @__PURE__ */ React39.createElement(
    IconButtonWithOptions,
    {
      disabled: !toggleVideo,
      onDisabledClick: toggleVideo,
      options: formattedVideoInputList,
      tooltipMessage: `Turn ${isLocalVideoEnabled ? "off" : "on"} video (${isMacOS ? "\u2318" : "ctrl"} + e)`,
      icon: !isLocalVideoEnabled ? /* @__PURE__ */ React39.createElement(VideoOffIcon, { "data-testid": "video_off_btn" }) : /* @__PURE__ */ React39.createElement(VideoOnIcon, { "data-testid": "video_on_btn" }),
      key: "toggleVideo",
      active: isLocalVideoEnabled,
      onClick: toggleVideo
    }
  ) : null, (localVideoTrack == null ? void 0 : localVideoTrack.facingMode) && roomState === HMSRoomState5.Preview ? /* @__PURE__ */ React39.createElement(Tooltip, { title: "Switch Camera", key: "switchCamera" }, /* @__PURE__ */ React39.createElement(
    IconButton_default,
    {
      onClick: () => __async(void 0, null, function* () {
        try {
          yield actions.switchCamera();
        } catch (e) {
          ToastManager.addToast({
            title: `Error while flipping camera ${e.message || ""}`,
            variant: "error"
          });
        }
      })
    },
    /* @__PURE__ */ React39.createElement(CameraFlipIcon2, null)
  )) : null);
};

// src/Prebuilt/components/Chip.jsx
import React40 from "react";
var Chip = ({
  icon = /* @__PURE__ */ React40.createElement(React40.Fragment, null),
  content = "",
  backgroundColor = "$surface_default",
  textColor = "$on_surface_high",
  hideIfNoContent = false
}) => {
  if (hideIfNoContent && !content) {
    return;
  }
  return /* @__PURE__ */ React40.createElement(Flex, { align: "center", css: { backgroundColor, p: "$4 $6", borderRadius: "$4" } }, icon, /* @__PURE__ */ React40.createElement(Text, { variant: "sm", css: { fontWeight: "$semiBold", color: textColor, ml: "$2" } }, content));
};
var Chip_default = Chip;

// src/Prebuilt/components/Connection/TileConnection.tsx
import React42 from "react";
import { PinIcon, SpotlightIcon } from "@100mslive/react-icons";

// src/Prebuilt/components/Connection/ConnectionIndicator.tsx
import React41 from "react";
import { selectConnectionQualityByPeerID as selectConnectionQualityByPeerID2, useHMSStore as useHMSStore18 } from "@100mslive/react-sdk";
import { PoorConnectivityIcon as PoorConnectivityIcon2 } from "@100mslive/react-icons";

// src/Prebuilt/components/Connection/connectionQualityUtils.js
var connectionTooltip = {
  0: "Reconnecting",
  1: "Very Bad Connection",
  2: "Bad Connection",
  3: "Moderate Connection",
  4: "Good Connection",
  5: "Excellent Connection"
};
connectionTooltip[-1] = "Network Unknown";
var getTooltipText = (connectionScore) => {
  return connectionTooltip[connectionScore];
};
var getColor = (position, connectionScore, defaultColor, theme2) => {
  const shouldBeColored = position <= connectionScore;
  if (!shouldBeColored) {
    return defaultColor;
  }
  if (connectionScore >= 4) {
    return theme2.colors.alert_success;
  } else if (connectionScore >= 3) {
    return theme2.colors.alert_warning;
  } else if (connectionScore >= 1) {
    return theme2.colors.alert_default;
  }
  return defaultColor;
};

// src/Prebuilt/components/Connection/ConnectionIndicator.tsx
var Wrapper = styled("span", {
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "$round",
  variants: {
    isTile: {
      true: {
        width: "unset",
        height: "unset"
      }
    }
  }
});
var ConnectionIndicator = ({
  peerId,
  isTile = false,
  hideBg = false
}) => {
  var _a7;
  const downlinkQuality = (_a7 = useHMSStore18(selectConnectionQualityByPeerID2(peerId))) == null ? void 0 : _a7.downlinkQuality;
  const { theme: theme2 } = useTheme();
  const defaultColor = theme2.colors.surface_brighter;
  if (downlinkQuality === -1 || downlinkQuality === void 0) {
    return null;
  }
  if (downlinkQuality === 0) {
    return /* @__PURE__ */ React41.createElement(Tooltip, { title: getTooltipText(downlinkQuality) }, /* @__PURE__ */ React41.createElement(Wrapper, { isTile, css: { color: "#ED4C5A" }, "data-testid": "tile_network" }, /* @__PURE__ */ React41.createElement(PoorConnectivityIcon2, null)));
  }
  const size = isTile ? 12 : 16;
  return /* @__PURE__ */ React41.createElement(Tooltip, { title: getTooltipText(downlinkQuality) }, /* @__PURE__ */ React41.createElement(Wrapper, { isTile, "data-testid": "tile_network", css: { backgroundColor: hideBg ? "" : "$surface_bright" } }, /* @__PURE__ */ React41.createElement(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 14 12",
      xmlns: "http://www.w3.org/2000/svg",
      xmlSpace: "preserve",
      style: {
        fillRule: "evenodd",
        clipRule: "evenodd",
        strokeLinejoin: "round",
        strokeMiterlimit: 2
      }
    },
    /* @__PURE__ */ React41.createElement(
      "path",
      {
        d: "M6.875 0c2.549.035 4.679.902 6.445 2.648.366.362.45.796.216 1.096-.239.306-.714.34-1.142.072a2.28 2.28 0 0 1-.341-.271C9.24.862 4.924.775 1.992 3.346c-.284.249-.594.419-.983.393-.272-.019-.49-.135-.613-.388-.125-.261-.05-.498.114-.713.073-.092.156-.177.245-.254C2.516.804 4.591.039 6.875 0Z",
        fill: getColor(4, downlinkQuality, defaultColor, theme2),
        transform: "translate(-.333)"
      }
    ),
    /* @__PURE__ */ React41.createElement(
      "path",
      {
        d: "M7.056 2.964c1.756.035 3.208.7 4.499 1.763.162.134.277.315.354.512.098.251.114.503-.075.72-.193.222-.452.259-.725.198-.293-.066-.518-.247-.738-.443a4.859 4.859 0 0 0-6.198-.26c-.166.127-.318.271-.475.409-.242.211-.513.343-.843.317-.43-.034-.679-.397-.561-.81.062-.211.181-.4.345-.546 1.265-1.162 2.733-1.836 4.417-1.86Z",
        fill: getColor(3, downlinkQuality, defaultColor, theme2),
        transform: "translate(-.333)"
      }
    ),
    /* @__PURE__ */ React41.createElement(
      "path",
      {
        d: "M7.384,6.052C8.293,6.068 9.157,6.449 9.783,7.108C10.005,7.339 10.157,7.6 10.07,7.942C9.959,8.377 9.435,8.581 9.071,8.243C7.935,7.191 6.356,7.183 5.152,8.183C4.816,8.462 4.6,8.485 4.332,8.27C4.063,8.055 3.998,7.691 4.177,7.358C4.273,7.179 4.414,7.038 4.57,6.911C5.26,6.349 6.149,6.05 7.384,6.052L7.384,6.052Z",
        fill: getColor(2, downlinkQuality, defaultColor, theme2)
      }
    ),
    /* @__PURE__ */ React41.createElement(
      "path",
      {
        d: "M8.214,9.941C8.214,10.234 8.097,10.515 7.888,10.721C7.68,10.928 7.398,11.042 7.104,11.039C6.471,11.036 5.982,10.541 5.993,9.912C6.004,9.259 6.499,8.766 7.133,8.779C7.744,8.791 8.22,9.301 8.214,9.941Z",
        fill: getColor(1, downlinkQuality, defaultColor, theme2)
      }
    )
  )));
};

// src/Prebuilt/components/Connection/TileConnection.tsx
var TileConnection = ({
  name,
  peerId,
  hideLabel,
  width,
  spotlighted,
  pinned
}) => {
  return /* @__PURE__ */ React42.createElement(Wrapper2, null, !hideLabel ? /* @__PURE__ */ React42.createElement(React42.Fragment, null, /* @__PURE__ */ React42.createElement(Flex, { align: "center" }, pinned && /* @__PURE__ */ React42.createElement(IconWrapper, null, /* @__PURE__ */ React42.createElement(PinIcon, { width: "15", height: "15" })), spotlighted && /* @__PURE__ */ React42.createElement(IconWrapper, null, /* @__PURE__ */ React42.createElement(SpotlightIcon, { width: "15", height: "15" })), /* @__PURE__ */ React42.createElement(
    Text,
    {
      css: __spreadValues({
        c: "$on_surface_high",
        verticalAlign: "baseline"
      }, width ? textEllipsis(width - 60) : {}),
      variant: "xs"
    },
    name
  )), /* @__PURE__ */ React42.createElement(ConnectionIndicator, { isTile: true, peerId, hideBg: true })) : null);
};
var IconWrapper = styled("div", { c: "$on_surface_high", ml: "$3", mt: "$1" });
var Wrapper2 = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "absolute",
  bottom: "$2",
  left: "$2",
  backgroundColor: "$background_dim",
  borderRadius: "$1",
  maxWidth: "85%",
  "& p,span": {
    p: "$2 $3"
  }
});
var TileConnection_default = TileConnection;

// src/Prebuilt/components/Settings/SettingsModal.jsx
import React48, { useCallback as useCallback15, useEffect as useEffect21, useState as useState15 } from "react";
import { useMedia as useMedia4 } from "react-use";
import { ChevronLeftIcon, CrossIcon as CrossIcon6 } from "@100mslive/react-icons";

// src/Prebuilt/components/Settings/common.js
import { GridFourIcon, NotificationsIcon, SettingsIcon } from "@100mslive/react-icons";

// src/Prebuilt/components/Settings/DeviceSettings.jsx
import React44, { Fragment as Fragment3, useEffect as useEffect20, useRef as useRef6, useState as useState14 } from "react";
import {
  DeviceType as DeviceType3,
  selectIsLocalVideoEnabled as selectIsLocalVideoEnabled3,
  selectLocalVideoTrackID as selectLocalVideoTrackID3,
  selectVideoTrackByID as selectVideoTrackByID3,
  useDevices as useDevices3,
  useHMSStore as useHMSStore19
} from "@100mslive/react-sdk";
import { MicOnIcon as MicOnIcon4, SpeakerIcon as SpeakerIcon2, VideoOnIcon as VideoOnIcon2 } from "@100mslive/react-icons";

// src/Prebuilt/primitives/DropdownTrigger.jsx
import React43 from "react";
import { ChevronDownIcon as ChevronDownIcon3, ChevronUpIcon as ChevronUpIcon3 } from "@100mslive/react-icons";
var DialogDropdownTrigger = React43.forwardRef(({ title, css: css2, open, icon, titleCSS = {} }, ref) => {
  return /* @__PURE__ */ React43.createElement(
    Dropdown.Trigger,
    {
      asChild: true,
      "data-testid": `${title}_selector`,
      css: __spreadValues({
        border: "1px solid $border_bright",
        bg: "$surface_bright",
        r: "$1",
        p: "$6 $9",
        zIndex: 10
      }, css2),
      ref
    },
    /* @__PURE__ */ React43.createElement(
      Flex,
      {
        css: {
          display: "flex",
          justifyContent: "space-between",
          color: "$on_surface_high",
          w: "100%"
        }
      },
      icon,
      /* @__PURE__ */ React43.createElement(
        Text,
        {
          css: __spreadValues(__spreadProps(__spreadValues({
            color: "inherit"
          }, textEllipsis("90%")), {
            flex: "1 1 0",
            mx: icon ? "$6" : "0"
          }), titleCSS)
        },
        title
      ),
      open ? /* @__PURE__ */ React43.createElement(ChevronUpIcon3, null) : /* @__PURE__ */ React43.createElement(ChevronDownIcon3, null)
    )
  );
});

// src/Prebuilt/components/hooks/useDropdownSelection.jsx
var useDropdownSelection = () => {
  return "$primary_dim";
};

// src/Prebuilt/components/Settings/DeviceSettings.jsx
var Settings = ({ setHide }) => {
  var _a7;
  const { allDevices, selectedDeviceIDs, updateDevice } = useDevices3();
  const { videoInput, audioInput, audioOutput } = allDevices;
  const videoTrackId = useHMSStore19(selectLocalVideoTrackID3);
  const isVideoOn = useHMSStore19(selectIsLocalVideoEnabled3);
  const shouldShowAudioOutput = "setSinkId" in HTMLMediaElement.prototype;
  const mirrorLocalVideo = useUISettings(UI_SETTINGS.mirrorLocalVideo);
  const trackSelector = selectVideoTrackByID3(videoTrackId);
  const track = useHMSStore19(trackSelector);
  const audioOutputFiltered = (_a7 = audioOutput == null ? void 0 : audioOutput.filter((item) => !!item.label)) != null ? _a7 : [];
  if (!(videoInput == null ? void 0 : videoInput.length) && !(audioInput == null ? void 0 : audioInput.length) && !(audioOutputFiltered == null ? void 0 : audioOutputFiltered.length)) {
    setHide(true);
  }
  return /* @__PURE__ */ React44.createElement(Box, { className: settingOverflow() }, (videoInput == null ? void 0 : videoInput.length) ? /* @__PURE__ */ React44.createElement(Fragment3, null, isVideoOn && /* @__PURE__ */ React44.createElement(
    StyledVideoTile.Container,
    {
      css: {
        w: "90%",
        px: "$10",
        height: "$48",
        bg: "transparent",
        m: "$10 auto"
      }
    },
    /* @__PURE__ */ React44.createElement(Video, { trackId: videoTrackId, mirror: (track == null ? void 0 : track.facingMode) !== "environment" && mirrorLocalVideo })
  ), /* @__PURE__ */ React44.createElement(
    DeviceSelector,
    {
      title: "Video",
      devices: videoInput,
      icon: /* @__PURE__ */ React44.createElement(VideoOnIcon2, null),
      selection: selectedDeviceIDs.videoInput,
      onChange: (deviceId) => updateDevice({
        deviceId,
        deviceType: DeviceType3.videoInput
      })
    }
  )) : null, (audioInput == null ? void 0 : audioInput.length) ? /* @__PURE__ */ React44.createElement(
    DeviceSelector,
    {
      title: shouldShowAudioOutput ? "Microphone" : "Audio",
      icon: /* @__PURE__ */ React44.createElement(MicOnIcon4, null),
      devices: audioInput,
      selection: selectedDeviceIDs.audioInput,
      onChange: (deviceId) => updateDevice({
        deviceId,
        deviceType: DeviceType3.audioInput
      })
    }
  ) : null, (audioOutputFiltered == null ? void 0 : audioOutputFiltered.length) && shouldShowAudioOutput ? /* @__PURE__ */ React44.createElement(
    DeviceSelector,
    {
      title: "Speaker",
      icon: /* @__PURE__ */ React44.createElement(SpeakerIcon2, null),
      devices: audioOutput,
      selection: selectedDeviceIDs.audioOutput,
      onChange: (deviceId) => updateDevice({
        deviceId,
        deviceType: DeviceType3.audioOutput
      })
    },
    /* @__PURE__ */ React44.createElement(TestAudio, { id: selectedDeviceIDs.audioOutput })
  ) : null);
};
var DeviceSelector = ({ title, devices, selection, onChange, icon, children = null }) => {
  var _a7, _b7;
  const [open, setOpen] = useState14(false);
  const selectionBg = useDropdownSelection();
  const ref = useRef6(null);
  return /* @__PURE__ */ React44.createElement(Box, { css: { mb: "$10" } }, /* @__PURE__ */ React44.createElement(Text, { css: { mb: "$4" } }, title), /* @__PURE__ */ React44.createElement(
    Flex,
    {
      align: "center",
      css: {
        gap: "$4",
        "@md": {
          flexDirection: children ? "column" : "row",
          alignItems: children ? "start" : "center"
        }
      }
    },
    /* @__PURE__ */ React44.createElement(
      Box,
      {
        css: {
          position: "relative",
          flex: "1 1 0",
          minWidth: 0,
          w: "100%",
          maxWidth: "100%",
          "@md": {
            mb: children ? "$8" : 0
          }
        }
      },
      /* @__PURE__ */ React44.createElement(Dropdown.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React44.createElement(
        DialogDropdownTrigger,
        {
          ref,
          icon,
          title: ((_a7 = devices.find(({ deviceId }) => deviceId === selection)) == null ? void 0 : _a7.label) || "Select device from list",
          open
        }
      ), /* @__PURE__ */ React44.createElement(Dropdown.Portal, null, /* @__PURE__ */ React44.createElement(Dropdown.Content, { align: "start", sideOffset: 8, css: { w: (_b7 = ref.current) == null ? void 0 : _b7.clientWidth, zIndex: 1001 } }, devices.map((device) => {
        return /* @__PURE__ */ React44.createElement(
          Dropdown.Item,
          {
            key: device.label,
            onSelect: () => onChange(device.deviceId),
            css: {
              px: "$9",
              bg: device.deviceId === selection ? selectionBg : void 0
            }
          },
          device.label
        );
      }))))
    ),
    children
  ));
};
var TEST_AUDIO_URL = "https://100ms.live/test-audio.wav";
var TestAudio = ({ id }) => {
  const audioRef = useRef6(null);
  const [playing, setPlaying] = useState14(false);
  useEffect20(() => {
    if (audioRef.current && id) {
      try {
        if (typeof audioRef.current.setSinkId !== "undefined") {
          audioRef.current.setSinkId(id);
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [id]);
  return /* @__PURE__ */ React44.createElement(React44.Fragment, null, /* @__PURE__ */ React44.createElement(
    Button,
    {
      variant: "standard",
      css: {
        flexShrink: 0,
        p: "$6 $9",
        "@md": {
          w: "100%"
        }
      },
      onClick: () => {
        var _a7;
        return (_a7 = audioRef.current) == null ? void 0 : _a7.play();
      },
      disabled: playing
    },
    /* @__PURE__ */ React44.createElement(SpeakerIcon2, null),
    "\xA0Test",
    " ",
    /* @__PURE__ */ React44.createElement(Text, { as: "span", css: { display: "none", "@md": { display: "inline" } } }, "\xA0 speaker")
  ), /* @__PURE__ */ React44.createElement("audio", { ref: audioRef, src: TEST_AUDIO_URL, onEnded: () => setPlaying(false), onPlay: () => setPlaying(true) }));
};
var DeviceSettings_default = Settings;

// src/Prebuilt/components/Settings/LayoutSettings.jsx
import React46, { useCallback as useCallback14 } from "react";
import { selectIsLocalScreenShared, selectIsLocalVideoEnabled as selectIsLocalVideoEnabled4, useHMSActions as useHMSActions15, useHMSStore as useHMSStore20 } from "@100mslive/react-sdk";

// src/Prebuilt/components/Settings/SwitchWithLabel.jsx
import React45 from "react";
var SwitchWithLabel = ({ label, icon, id, onChange, checked, hide = false }) => {
  return /* @__PURE__ */ React45.createElement(
    Flex,
    {
      align: "center",
      css: {
        my: "$2",
        py: "$8",
        w: "100%",
        borderBottom: "1px solid $border_default",
        display: hide ? "none" : "flex"
      }
    },
    /* @__PURE__ */ React45.createElement(
      Label3,
      {
        htmlFor: id,
        css: {
          fontSize: "$md",
          fontWeight: "$semiBold",
          color: checked ? "$on_surface_high" : "$on_surface_low",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "$8",
          flex: "1 1 0"
        }
      },
      icon,
      label
    ),
    /* @__PURE__ */ React45.createElement(Switch, { id, checked, onCheckedChange: onChange })
  );
};
var SwitchWithLabel_default = SwitchWithLabel;

// src/Prebuilt/components/Settings/LayoutSettings.jsx
var LayoutSettings = () => {
  const hmsActions = useHMSActions15();
  const isLocalVideoEnabled = useHMSStore20(selectIsLocalVideoEnabled4);
  const isLocalScreenShared = useHMSStore20(selectIsLocalScreenShared);
  const [{ isAudioOnly, maxTileCount, mirrorLocalVideo }, setUISettings] = useSetUiSettings();
  const toggleIsAudioOnly = useCallback14(
    (isAudioOnlyModeOn) => __async(void 0, null, function* () {
      if (isAudioOnlyModeOn) {
        isLocalVideoEnabled && (yield hmsActions.setLocalVideoEnabled(false));
        isLocalScreenShared && (yield hmsActions.setScreenShareEnabled(false));
      }
      setUISettings({ [UI_SETTINGS.isAudioOnly]: isAudioOnlyModeOn });
    }),
    [hmsActions, isLocalVideoEnabled, isLocalScreenShared, setUISettings]
  );
  return /* @__PURE__ */ React46.createElement(Box, { className: settingOverflow() }, /* @__PURE__ */ React46.createElement(SwitchWithLabel_default, { label: "Audio Only Mode", id: "audioOnlyMode", checked: isAudioOnly, onChange: toggleIsAudioOnly }), /* @__PURE__ */ React46.createElement(
    SwitchWithLabel_default,
    {
      label: "Mirror Local Video",
      id: "mirrorMode",
      checked: mirrorLocalVideo,
      onChange: (value) => {
        setUISettings({
          [UI_SETTINGS.mirrorLocalVideo]: value
        });
      }
    }
  ), /* @__PURE__ */ React46.createElement(Flex, { align: "center", css: { w: "100%", my: "$2", py: "$8", "@md": { display: "none" } } }, /* @__PURE__ */ React46.createElement(Text, { variant: "md", css: { fontWeight: "$semiBold" } }, "Tiles In View(", maxTileCount, ")"), /* @__PURE__ */ React46.createElement(Flex, { justify: "end", css: { flex: "1 1 0" } }, /* @__PURE__ */ React46.createElement(
    Slider,
    {
      step: 1,
      value: [maxTileCount],
      min: 1,
      max: 49,
      onValueChange: (e) => {
        setUISettings({ [UI_SETTINGS.maxTileCount]: e[0] });
      },
      css: { w: "70%" }
    }
  ))));
};

// src/Prebuilt/components/Settings/NotificationSettings.jsx
import React47 from "react";
import { AlertOctagonIcon, HandIcon as HandIcon2, PeopleAddIcon as PeopleAddIcon2, PeopleRemoveIcon as PeopleRemoveIcon2 } from "@100mslive/react-icons";
var NotificationItem = ({ type, label, icon, checked }) => {
  const [, setSubscribedNotifications] = useSetSubscribedNotifications(type);
  return /* @__PURE__ */ React47.createElement(
    SwitchWithLabel_default,
    {
      label,
      id: type,
      icon,
      checked,
      onChange: (value) => {
        setSubscribedNotifications(value);
      }
    }
  );
};
var NotificationSettings = () => {
  const subscribedNotifications = useSubscribedNotifications();
  return /* @__PURE__ */ React47.createElement(Box, { className: settingOverflow() }, /* @__PURE__ */ React47.createElement(
    NotificationItem,
    {
      label: "Peer Joined",
      type: SUBSCRIBED_NOTIFICATIONS.PEER_JOINED,
      icon: /* @__PURE__ */ React47.createElement(PeopleAddIcon2, null),
      checked: subscribedNotifications.PEER_JOINED
    }
  ), /* @__PURE__ */ React47.createElement(
    NotificationItem,
    {
      label: "Peer Leave",
      type: SUBSCRIBED_NOTIFICATIONS.PEER_LEFT,
      icon: /* @__PURE__ */ React47.createElement(PeopleRemoveIcon2, null),
      checked: subscribedNotifications.PEER_LEFT
    }
  ), /* @__PURE__ */ React47.createElement(
    NotificationItem,
    {
      label: "Hand Raised",
      type: SUBSCRIBED_NOTIFICATIONS.METADATA_UPDATED,
      icon: /* @__PURE__ */ React47.createElement(HandIcon2, null),
      checked: subscribedNotifications.METADATA_UPDATED
    }
  ), /* @__PURE__ */ React47.createElement(
    NotificationItem,
    {
      label: "Error",
      type: SUBSCRIBED_NOTIFICATIONS.ERROR,
      icon: /* @__PURE__ */ React47.createElement(AlertOctagonIcon, null),
      checked: subscribedNotifications.ERROR
    }
  ));
};

// src/Prebuilt/components/Settings/common.js
var settingOverflow = css({
  flex: "1 1 0",
  pr: "$12",
  mr: "-$12",
  overflowY: "auto"
});
var settingContent = css({
  display: "flex",
  flexDirection: "column",
  "&[hidden]": {
    display: "none"
  }
});
var settingsList = [
  {
    tabName: "devices",
    title: "Device Settings",
    icon: SettingsIcon,
    content: DeviceSettings_default
  },
  {
    tabName: "notifications",
    title: "Notifications",
    icon: NotificationsIcon,
    content: NotificationSettings
  },
  {
    tabName: "layout",
    title: "Layout",
    icon: GridFourIcon,
    content: LayoutSettings
  }
];

// src/Prebuilt/components/Settings/SettingsModal.jsx
var SettingsModal = ({ open, onOpenChange, screenType, children = /* @__PURE__ */ React48.createElement(React48.Fragment, null) }) => {
  const mediaQueryLg = config.media.md;
  const isMobile = useMedia4(mediaQueryLg);
  const [showSetting, setShowSetting] = useState15(
    () => settingsList.reduce((obj, { tabName }) => __spreadProps(__spreadValues({}, obj), { [tabName]: true }), {})
  );
  const hideSettingByTabName = useCallback15(
    (key) => (hide) => setShowSetting((prev) => __spreadProps(__spreadValues({}, prev), { [key]: !hide })),
    [setShowSetting]
  );
  useEffect21(() => {
    if (screenType === "hls_live_streaming") {
      hideSettingByTabName("layout")(true);
    }
  }, [screenType, hideSettingByTabName]);
  const [selection, setSelection] = useState15(() => {
    var _a7;
    return (_a7 = Object.keys(showSetting).find((key) => showSetting[key])) != null ? _a7 : "";
  });
  const resetSelection = useCallback15(() => {
    setSelection("");
  }, []);
  useEffect21(() => {
    var _a7;
    if (isMobile) {
      setSelection("");
    } else {
      const firstNotHiddenTabName = (_a7 = Object.keys(showSetting).find((key) => showSetting[key])) != null ? _a7 : "";
      setSelection(firstNotHiddenTabName);
    }
  }, [isMobile, showSetting]);
  return isMobile ? /* @__PURE__ */ React48.createElement(
    MobileSettingModal,
    {
      open,
      onOpenChange,
      selection,
      setSelection,
      showSetting,
      hideSettingByTabName,
      resetSelection,
      children
    }
  ) : /* @__PURE__ */ React48.createElement(
    DesktopSettingModal,
    {
      open,
      onOpenChange,
      selection,
      setSelection,
      showSetting,
      hideSettingByTabName,
      resetSelection,
      children
    }
  );
};
var MobileSettingModal = ({
  open,
  onOpenChange,
  selection,
  setSelection,
  showSetting,
  hideSettingByTabName,
  resetSelection,
  children = /* @__PURE__ */ React48.createElement(React48.Fragment, null)
}) => {
  return /* @__PURE__ */ React48.createElement(Sheet.Root, { open, onOpenChange }, /* @__PURE__ */ React48.createElement(Sheet.Trigger, { asChild: true }, children), /* @__PURE__ */ React48.createElement(
    Sheet.Content,
    {
      css: {
        bg: "$surface_dim",
        overflowY: "auto"
      }
    },
    /* @__PURE__ */ React48.createElement(Sheet.Title, { css: { py: "$10", px: "$8", alignItems: "center" } }, /* @__PURE__ */ React48.createElement(Flex, { direction: "row", justify: "between", css: { w: "100%" } }, !selection ? /* @__PURE__ */ React48.createElement(Text, { variant: "h6", css: { display: "flex" } }, "Settings") : /* @__PURE__ */ React48.createElement(Text, { variant: "h6", css: { display: "flex" } }, /* @__PURE__ */ React48.createElement(Box, { as: "span", css: { r: "$round", mr: "$2" }, onClick: resetSelection }, /* @__PURE__ */ React48.createElement(ChevronLeftIcon, null)), (selection == null ? void 0 : selection.charAt(0).toUpperCase()) + selection.slice(1)), /* @__PURE__ */ React48.createElement(Sheet.Close, null, /* @__PURE__ */ React48.createElement(IconButton, { as: "div", "data-testid": "dialog_cross_icon" }, /* @__PURE__ */ React48.createElement(CrossIcon6, null))))),
    /* @__PURE__ */ React48.createElement(HorizontalDivider, null),
    !selection ? /* @__PURE__ */ React48.createElement(
      Flex,
      {
        direction: "column",
        css: {
          px: "$8",
          pb: "$8",
          overflowY: "auto"
        }
      },
      settingsList.filter(({ tabName }) => showSetting[tabName]).map(({ icon: Icon2, tabName, title }) => {
        return /* @__PURE__ */ React48.createElement(
          Box,
          {
            key: tabName,
            value: tabName,
            onClick: () => {
              setSelection(tabName);
            },
            as: "div",
            css: {
              all: "unset",
              fontFamily: "$sans",
              py: "$10",
              display: "flex",
              alignItems: "center",
              fontSize: "$sm",
              lineHeight: "$sm",
              color: "$on_surface_high",
              userSelect: "none",
              gap: "$8",
              cursor: "pointer",
              "&:hover": {
                bg: "$surface_brighter",
                r: "$1",
                gap: "$8",
                border: "none"
              },
              borderBottom: "1px solid $border_default"
            }
          },
          /* @__PURE__ */ React48.createElement(Icon2, null),
          title
        );
      })
    ) : /* @__PURE__ */ React48.createElement(
      Box,
      {
        direction: "column",
        css: { overflowY: "scroll", px: "$8", py: "$10", maxHeight: "70vh", overflowX: "hidden" }
      },
      settingsList.filter(({ tabName }) => showSetting[tabName] && selection === tabName).map(({ content: Content10, title, tabName }) => {
        return /* @__PURE__ */ React48.createElement(Content10, { key: title, setHide: hideSettingByTabName(tabName) });
      })
    )
  ));
};
var DesktopSettingModal = ({
  open,
  onOpenChange,
  selection,
  setSelection,
  showSetting,
  hideSettingByTabName,
  resetSelection,
  children = /* @__PURE__ */ React48.createElement(React48.Fragment, null)
}) => {
  return /* @__PURE__ */ React48.createElement(Dialog.Root, { open, onOpenChange }, /* @__PURE__ */ React48.createElement(Dialog.Trigger, { asChild: true }, children), /* @__PURE__ */ React48.createElement(Dialog.Portal, null, /* @__PURE__ */ React48.createElement(Dialog.Overlay, null), /* @__PURE__ */ React48.createElement(
    Dialog.Content,
    {
      css: {
        w: "min(800px, 90%)",
        height: "min(656px, 90%)",
        p: 0,
        r: "$4"
      }
    },
    /* @__PURE__ */ React48.createElement(
      Tabs.Root,
      {
        value: selection,
        activationMode: "automatic",
        onValueChange: setSelection,
        css: { size: "100%", position: "relative" }
      },
      /* @__PURE__ */ React48.createElement(
        Tabs.List,
        {
          css: {
            w: "18.625rem",
            flexDirection: "column",
            bg: "$background_default",
            p: "$14 $10",
            borderTopLeftRadius: "$4",
            borderBottomLeftRadius: "$4"
          }
        },
        /* @__PURE__ */ React48.createElement(Text, { variant: "h5" }, "Settings "),
        /* @__PURE__ */ React48.createElement(Flex, { direction: "column", css: { mx: 0, overflowY: "auto", pt: "$10" } }, settingsList.filter(({ tabName }) => showSetting[tabName]).map(({ icon: Icon2, tabName, title }) => {
          return /* @__PURE__ */ React48.createElement(Tabs.Trigger, { key: tabName, value: tabName, css: { gap: "$8" } }, /* @__PURE__ */ React48.createElement(Icon2, null), title);
        }))
      ),
      selection && /* @__PURE__ */ React48.createElement(
        Flex,
        {
          direction: "column",
          css: {
            flex: "1 1 0",
            minWidth: 0,
            mr: "$4"
          }
        },
        settingsList.filter(({ tabName }) => showSetting[tabName]).map(({ content: Content10, title, tabName }) => {
          return /* @__PURE__ */ React48.createElement(Tabs.Content, { key: tabName, value: tabName, className: settingContent() }, /* @__PURE__ */ React48.createElement(SettingsContentHeader, { onBack: resetSelection, isMobile: false }, title), /* @__PURE__ */ React48.createElement(Content10, { setHide: hideSettingByTabName(tabName) }));
        })
      )
    ),
    /* @__PURE__ */ React48.createElement(Dialog.Close, { css: { position: "absolute", right: "$10", top: "$10" } }, /* @__PURE__ */ React48.createElement(IconButton, { as: "div", "data-testid": "dialog_cross_icon" }, /* @__PURE__ */ React48.createElement(CrossIcon6, null)))
  )));
};
var SettingsContentHeader = ({ children, isMobile, onBack }) => {
  return /* @__PURE__ */ React48.createElement(Text, { variant: "h6", css: { mb: "$12", display: "flex", alignItems: "center" } }, isMobile && /* @__PURE__ */ React48.createElement(Box, { as: "span", css: { bg: "$surface_bright", mr: "$4", r: "$round", p: "$2" }, onClick: onBack }, /* @__PURE__ */ React48.createElement(ChevronLeftIcon, null)), children);
};
var SettingsModal_default = SettingsModal;

// src/Prebuilt/components/VideoTile.jsx
import React53, { Fragment as Fragment5, useCallback as useCallback16, useEffect as useEffect23, useMemo as useMemo3, useRef as useRef7, useState as useState18 } from "react";
import { useMedia as useMedia8 } from "react-use";
import {
  selectAudioTrackByPeerID as selectAudioTrackByPeerID2,
  selectHasPeerHandRaised as selectHasPeerHandRaised3,
  selectIsPeerAudioEnabled,
  selectLocalPeerID as selectLocalPeerID5,
  selectPeerMetadata as selectPeerMetadata2,
  selectPeerNameByID,
  selectSessionStore as selectSessionStore3,
  selectTrackAudioByID,
  selectVideoTrackByID as selectVideoTrackByID4,
  selectVideoTrackByPeerID as selectVideoTrackByPeerID3,
  useHMSStore as useHMSStore24,
  useHMSVanillaStore as useHMSVanillaStore6
} from "@100mslive/react-sdk";
import { BrbTileIcon, HandIcon as HandIcon3, MicOffIcon as MicOffIcon3 } from "@100mslive/react-icons";

// src/Prebuilt/components/TileMenu/TileMenu.jsx
import React52, { useState as useState17 } from "react";
import { useMedia as useMedia7 } from "react-use";
import {
  selectLocalPeerID as selectLocalPeerID4,
  selectPeerByID as selectPeerByID2,
  selectPermissions as selectPermissions3,
  selectTrackByID as selectTrackByID3,
  selectVideoTrackByPeerID as selectVideoTrackByPeerID2,
  useHMSStore as useHMSStore23,
  useRemoteAVToggle as useRemoteAVToggle2
} from "@100mslive/react-sdk";
import { CrossIcon as CrossIcon8, VerticalMenuIcon as VerticalMenuIcon2 } from "@100mslive/react-icons";

// src/Prebuilt/components/MoreSettings/ChangeNameModal.jsx
import React50, { useState as useState16 } from "react";
import { useMedia as useMedia5 } from "react-use";
import { selectLocalPeerName, useHMSActions as useHMSActions16, useHMSStore as useHMSStore21 } from "@100mslive/react-sdk";

// src/Prebuilt/components/MoreSettings/ChangeNameContent.jsx
import React49 from "react";
import { ChevronLeftIcon as ChevronLeftIcon2, CrossIcon as CrossIcon7 } from "@100mslive/react-icons";
var ChangeNameContent = ({
  changeName,
  setCurrentName,
  currentName,
  localPeerName,
  isMobile,
  onExit,
  onBackClick
}) => {
  return /* @__PURE__ */ React49.createElement(
    "form",
    {
      onSubmit: (e) => __async(void 0, null, function* () {
        e.preventDefault();
        yield changeName();
      })
    },
    /* @__PURE__ */ React49.createElement(
      Text,
      {
        variant: isMobile ? "md" : "lg",
        css: {
          color: "$on_surface_high",
          fontWeight: "$semiBold",
          display: "flex",
          pb: "$4",
          "@md": { px: "$8", borderBottom: "1px solid $border_default" }
        }
      },
      isMobile ? /* @__PURE__ */ React49.createElement(ChevronLeftIcon2, { onClick: onBackClick, style: { marginRight: "0.5rem" } }) : null,
      "Change Name",
      /* @__PURE__ */ React49.createElement(
        Box,
        {
          css: { color: "inherit", ml: "auto", "&:hover": { color: "$on_surface_medium", cursor: "pointer" } },
          onClick: onExit
        },
        /* @__PURE__ */ React49.createElement(CrossIcon7, null)
      )
    ),
    /* @__PURE__ */ React49.createElement(Text, { variant: "sm", css: { color: "$on_surface_medium", pb: "$6", mb: "$8", "@md": { px: "$8", mt: "$4" } } }, "Your name will be visible to other participants in the session."),
    /* @__PURE__ */ React49.createElement(Flex, { justify: "center", align: "center", css: { my: "$8", w: "100%", "@md": { px: "$8" } } }, /* @__PURE__ */ React49.createElement(
      Input,
      {
        css: { width: "100%", bg: "$surface_default" },
        value: currentName,
        type: isMobile ? "search" : "text",
        onChange: (e) => {
          setCurrentName(e.target.value);
        },
        autoComplete: "name",
        required: true,
        "data-testid": "change_name_field",
        onKeyDown: (e) => __async(void 0, null, function* () {
          if (e.key === "Enter" && currentName.trim().length > 0 && currentName !== localPeerName) {
            e.preventDefault();
            if (isMobile) {
              return;
            }
            changeName();
          }
        })
      }
    )),
    /* @__PURE__ */ React49.createElement(
      Flex,
      {
        justify: "between",
        align: "center",
        css: {
          width: "100%",
          gap: "$md",
          mt: "$10",
          "@md": { px: "$4" }
        }
      },
      isMobile ? null : /* @__PURE__ */ React49.createElement(
        Button,
        {
          variant: "standard",
          css: { w: "100%" },
          outlined: true,
          type: "submit",
          disabled: !localPeerName,
          onClick: onExit
        },
        "Cancel"
      ),
      /* @__PURE__ */ React49.createElement(
        Button,
        {
          variant: "primary",
          css: { width: "100%" },
          type: "submit",
          disabled: !currentName.trim() || currentName.trim() === localPeerName,
          "data-testid": "popup_change_btn"
        },
        "Change"
      )
    )
  );
};

// src/Prebuilt/components/MoreSettings/ChangeNameModal.jsx
var ChangeNameModal = ({ onOpenChange, openParentSheet = null }) => {
  const [previewPreference, setPreviewPreference] = useUserPreferences(UserPreferencesKeys.PREVIEW);
  const hmsActions = useHMSActions16();
  const localPeerName = useHMSStore21(selectLocalPeerName);
  const [currentName, setCurrentName] = useState16(localPeerName);
  const isMobile = useMedia5(config.media.md);
  const changeName = () => __async(void 0, null, function* () {
    const name = currentName.trim();
    if (!name || name === localPeerName) {
      return;
    }
    try {
      yield hmsActions.changeName(name);
      setPreviewPreference(__spreadProps(__spreadValues({}, previewPreference || {}), {
        name
      }));
    } catch (error) {
      console.error("failed to update name", error);
      ToastManager.addToast({ title: error.message });
    } finally {
      onOpenChange(false);
    }
  });
  const props = {
    changeName,
    setCurrentName,
    currentName,
    localPeerName,
    isMobile,
    onExit: () => onOpenChange(false),
    onBackClick: () => {
      onOpenChange(false);
      openParentSheet();
    }
  };
  if (isMobile) {
    return /* @__PURE__ */ React50.createElement(Sheet.Root, { defaultOpen: true, onOpenChange }, /* @__PURE__ */ React50.createElement(Sheet.Content, { css: { bg: "$surface_dim", p: "$8 0" } }, /* @__PURE__ */ React50.createElement(ChangeNameContent, __spreadValues({}, props))));
  }
  return /* @__PURE__ */ React50.createElement(Dialog.Root, { defaultOpen: true, onOpenChange }, /* @__PURE__ */ React50.createElement(Dialog.Portal, null, /* @__PURE__ */ React50.createElement(Dialog.Overlay, null), /* @__PURE__ */ React50.createElement(Dialog.Content, { css: { bg: "$surface_dim", width: "min(400px,80%)", p: "$10" } }, /* @__PURE__ */ React50.createElement(ChangeNameContent, __spreadValues({}, props)))));
};

// src/Prebuilt/components/TileMenu/TileMenuContent.jsx
import React51, { Fragment as Fragment4 } from "react";
import { useMedia as useMedia6 } from "react-use";
import {
  selectPermissions as selectPermissions2,
  selectSessionStore as selectSessionStore2,
  selectTrackByID as selectTrackByID2,
  useCustomEvent as useCustomEvent2,
  useHMSActions as useHMSActions17,
  useHMSStore as useHMSStore22,
  useRemoteAVToggle
} from "@100mslive/react-sdk";
import {
  MicOffIcon as MicOffIcon2,
  MicOnIcon as MicOnIcon5,
  PencilIcon,
  PinIcon as PinIcon2,
  RemoveUserIcon,
  ShareScreenIcon as ShareScreenIcon2,
  ShrinkIcon,
  SpeakerIcon as SpeakerIcon3,
  StarIcon,
  VideoOffIcon as VideoOffIcon2,
  VideoOnIcon as VideoOnIcon3
} from "@100mslive/react-icons";
var isSameTile = ({ trackId, videoTrackID, audioTrackID }) => trackId && (videoTrackID && videoTrackID === trackId || audioTrackID && audioTrackID === trackId);
var spacingCSS = { "@md": { my: "$8", fontWeight: "$semiBold", fontSize: "sm" } };
var SpotlightActions = ({
  peerId,
  onSpotLightClick = () => {
    return;
  }
}) => {
  const hmsActions = useHMSActions17();
  const spotlightPeerId = useHMSStore22(selectSessionStore2(SESSION_STORE_KEY.SPOTLIGHT));
  const isTileSpotlighted = spotlightPeerId === peerId;
  const setSpotlightPeerId = (peer) => hmsActions.sessionStore.set(SESSION_STORE_KEY.SPOTLIGHT, peer).catch((err) => ToastManager.addToast({ title: err.description }));
  return /* @__PURE__ */ React51.createElement(
    StyledMenuTile.ItemButton,
    {
      css: spacingCSS,
      onClick: () => {
        if (isTileSpotlighted) {
          setSpotlightPeerId();
        } else {
          setSpotlightPeerId(peerId);
        }
        onSpotLightClick();
      }
    },
    /* @__PURE__ */ React51.createElement(StarIcon, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, isTileSpotlighted ? "Remove from Spotlight" : "Spotlight Tile for everyone")
  );
};
var PinActions = ({ audioTrackID, videoTrackID }) => {
  const [pinnedTrackId, setPinnedTrackId] = useSetAppDataByKey(APP_DATA.pinnedTrackId);
  const isTilePinned = isSameTile({
    trackId: pinnedTrackId,
    videoTrackID,
    audioTrackID
  });
  return /* @__PURE__ */ React51.createElement(React51.Fragment, null, /* @__PURE__ */ React51.createElement(
    StyledMenuTile.ItemButton,
    {
      css: spacingCSS,
      onClick: () => isTilePinned ? setPinnedTrackId() : setPinnedTrackId(videoTrackID || audioTrackID)
    },
    /* @__PURE__ */ React51.createElement(PinIcon2, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, isTilePinned ? "Unpin" : "Pin", " Tile for myself")
  ));
};
var MinimiseInset = () => {
  const [minimised, setMinimised] = useSetAppDataByKey(APP_DATA.minimiseInset);
  return /* @__PURE__ */ React51.createElement(React51.Fragment, null, /* @__PURE__ */ React51.createElement(StyledMenuTile.ItemButton, { css: spacingCSS, onClick: () => setMinimised(!minimised) }, /* @__PURE__ */ React51.createElement(ShrinkIcon, { height: 20, width: 20 }), /* @__PURE__ */ React51.createElement("span", null, minimised ? "Show" : "Minimise", " your video")));
};
var SimulcastLayers = ({ trackId }) => {
  var _a7;
  const track = useHMSStore22(selectTrackByID2(trackId));
  const actions = useHMSActions17();
  const bg = useDropdownSelection();
  if (!((_a7 = track == null ? void 0 : track.layerDefinitions) == null ? void 0 : _a7.length) || track.degraded || !track.enabled) {
    return null;
  }
  const currentLayer = track.layerDefinitions.find((layer) => layer.layer === track.layer);
  return /* @__PURE__ */ React51.createElement(Fragment4, null, /* @__PURE__ */ React51.createElement(StyledMenuTile.ItemButton, { css: { color: "$on_surface_medium", cursor: "default" } }, "Select maximum resolution"), track.layerDefinitions.map((layer) => {
    return /* @__PURE__ */ React51.createElement(
      StyledMenuTile.ItemButton,
      {
        key: layer.layer,
        onClick: () => __async(void 0, null, function* () {
          yield actions.setPreferredLayer(trackId, layer.layer);
        }),
        css: {
          justifyContent: "space-between",
          bg: track.preferredLayer === layer.layer ? bg : void 0,
          "&:hover": {
            bg: track.preferredLayer === layer.layer ? bg : void 0
          }
        }
      },
      /* @__PURE__ */ React51.createElement(
        Text,
        {
          as: "span",
          css: {
            textTransform: "capitalize",
            mr: "$2",
            fontWeight: track.preferredLayer === layer.layer ? "$semiBold" : "$regular",
            color: track.preferredLayer === layer.layer ? "$on_primary_high" : "$on_surface_high"
          }
        },
        layer.layer
      ),
      /* @__PURE__ */ React51.createElement(
        Text,
        {
          as: "span",
          variant: "xs",
          css: {
            color: track.preferredLayer === layer.layer ? "$on_primary_high" : "$on_surface_high"
          }
        },
        layer.resolution.width,
        "x",
        layer.resolution.height
      )
    );
  }), /* @__PURE__ */ React51.createElement(StyledMenuTile.ItemButton, null, /* @__PURE__ */ React51.createElement(Text, { as: "span", variant: "xs", css: { color: "$on_surface_medium" } }, "Currently streaming:", /* @__PURE__ */ React51.createElement(
    Text,
    {
      as: "span",
      variant: "xs",
      css: {
        fontWeight: "$semiBold",
        textTransform: "capitalize",
        color: "$on_surface_medium",
        ml: "$2"
      }
    },
    currentLayer ? /* @__PURE__ */ React51.createElement(React51.Fragment, null, track.layer, " (", currentLayer.resolution.width, "x", currentLayer.resolution.height, ")") : "-"
  ))));
};
var TileMenuContent = (props) => {
  const actions = useHMSActions17();
  const { removeOthers } = useHMSStore22(selectPermissions2);
  const {
    videoTrackID,
    audioTrackID,
    isLocal,
    isScreenshare,
    showSpotlight,
    showPinAction,
    peerID,
    canMinimise,
    closeSheetOnClick = () => {
      return;
    },
    openNameChangeModal = () => {
      return;
    }
  } = props;
  const { isAudioEnabled, isVideoEnabled, setVolume, toggleAudio, toggleVideo, volume } = useRemoteAVToggle(
    audioTrackID,
    videoTrackID
  );
  const { sendEvent } = useCustomEvent2({
    type: REMOTE_STOP_SCREENSHARE_TYPE
  });
  const isMobile = useMedia6(config.media.md);
  return isLocal ? (showPinAction || canMinimise) && /* @__PURE__ */ React51.createElement(React51.Fragment, null, showPinAction && /* @__PURE__ */ React51.createElement(PinActions, { audioTrackID, videoTrackID }), showSpotlight && /* @__PURE__ */ React51.createElement(SpotlightActions, { peerId: peerID, onSpotLightClick: () => closeSheetOnClick() }), canMinimise && /* @__PURE__ */ React51.createElement(MinimiseInset, null), /* @__PURE__ */ React51.createElement(
    StyledMenuTile.ItemButton,
    {
      onClick: () => {
        openNameChangeModal();
        closeSheetOnClick();
      }
    },
    /* @__PURE__ */ React51.createElement(PencilIcon, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement(Text, { variant: "sm", css: { "@md": { fontWeight: "$semiBold" }, c: "$on_surface_high" } }, "Change Name")
  )) : /* @__PURE__ */ React51.createElement(React51.Fragment, null, toggleVideo ? /* @__PURE__ */ React51.createElement(
    StyledMenuTile.ItemButton,
    {
      css: spacingCSS,
      onClick: () => {
        toggleVideo();
        closeSheetOnClick();
      },
      "data-testid": isVideoEnabled ? "mute_video_participant_btn" : "unmute_video_participant_btn"
    },
    isVideoEnabled ? /* @__PURE__ */ React51.createElement(VideoOnIcon3, { height: 20, width: 20 }) : /* @__PURE__ */ React51.createElement(VideoOffIcon2, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, isVideoEnabled ? "Mute" : "Request Unmute")
  ) : null, toggleAudio ? /* @__PURE__ */ React51.createElement(
    StyledMenuTile.ItemButton,
    {
      css: spacingCSS,
      onClick: () => {
        toggleAudio();
        closeSheetOnClick();
      },
      "data-testid": isVideoEnabled ? "mute_audio_participant_btn" : "unmute_audio_participant_btn"
    },
    isAudioEnabled ? /* @__PURE__ */ React51.createElement(MicOnIcon5, { height: 20, width: 20 }) : /* @__PURE__ */ React51.createElement(MicOffIcon2, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, isAudioEnabled ? "Mute" : "Request Unmute")
  ) : null, audioTrackID ? /* @__PURE__ */ React51.createElement(StyledMenuTile.VolumeItem, { "data-testid": "participant_volume_slider", css: __spreadProps(__spreadValues({}, spacingCSS), { mb: "$0" }) }, /* @__PURE__ */ React51.createElement(Flex, { align: "center", gap: 1 }, /* @__PURE__ */ React51.createElement(SpeakerIcon3, { height: 20, width: 20 }), /* @__PURE__ */ React51.createElement(Box, { as: "span", css: { ml: "$4" } }, "Volume (", volume, ")")), /* @__PURE__ */ React51.createElement(Slider, { css: { my: "0.5rem" }, step: 5, value: [volume], onValueChange: (e) => setVolume(e[0]) })) : null, showPinAction && /* @__PURE__ */ React51.createElement(React51.Fragment, null, /* @__PURE__ */ React51.createElement(PinActions, { audioTrackID, videoTrackID }), showSpotlight && /* @__PURE__ */ React51.createElement(SpotlightActions, { peerId: peerID, onSpotLightClick: () => closeSheetOnClick() })), isMobile ? null : /* @__PURE__ */ React51.createElement(SimulcastLayers, { trackId: videoTrackID }), removeOthers ? /* @__PURE__ */ React51.createElement(
    StyledMenuTile.RemoveItem,
    {
      css: __spreadProps(__spreadValues({}, spacingCSS), { borderTop: "none" }),
      onClick: () => __async(void 0, null, function* () {
        try {
          yield actions.removePeer(peerID, "");
        } catch (error) {
        }
        closeSheetOnClick();
      }),
      "data-testid": "remove_participant_btn"
    },
    /* @__PURE__ */ React51.createElement(RemoveUserIcon, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, "Remove Participant")
  ) : null, removeOthers && isScreenshare ? /* @__PURE__ */ React51.createElement(
    StyledMenuTile.RemoveItem,
    {
      onClick: () => {
        sendEvent({});
        closeSheetOnClick();
      },
      css: spacingCSS
    },
    /* @__PURE__ */ React51.createElement(ShareScreenIcon2, { height: 20, width: 20 }),
    /* @__PURE__ */ React51.createElement("span", null, "Stop Screenshare")
  ) : null);
};

// src/Prebuilt/components/hooks/useDropdownList.jsx
import { useEffect as useEffect22 } from "react";
var useDropdownList = ({ name, open }) => {
  const [dropdownList = [], setDropdownList] = useSetAppDataByKey(APP_DATA.dropdownList);
  useEffect22(() => {
    if (open) {
      if (!dropdownList.includes(name)) {
        setDropdownList([...dropdownList, name]);
      }
    } else {
      const index = dropdownList.indexOf(name);
      if (index >= 0) {
        const newDropdownList = [...dropdownList];
        newDropdownList.splice(index, 1);
        setDropdownList(newDropdownList);
      }
    }
  }, [open, name]);
};

// src/Prebuilt/components/TileMenu/TileMenu.jsx
var TileMenu = ({
  audioTrackID,
  videoTrackID,
  peerID,
  isScreenshare = false,
  canMinimise,
  enableSpotlightingPeer = true
}) => {
  var _a7, _b7;
  const [open, setOpen] = useState17(false);
  const { theme: theme2 } = useTheme();
  const localPeerID = useHMSStore23(selectLocalPeerID4);
  const isLocal = localPeerID === peerID;
  const { removeOthers } = useHMSStore23(selectPermissions3);
  const { setVolume, toggleAudio, toggleVideo } = useRemoteAVToggle2(audioTrackID, videoTrackID);
  const showSpotlight = enableSpotlightingPeer;
  const isPrimaryVideoTrack = ((_a7 = useHMSStore23(selectVideoTrackByPeerID2(peerID))) == null ? void 0 : _a7.id) === videoTrackID;
  const showPinAction = audioTrackID || videoTrackID && isPrimaryVideoTrack;
  const track = useHMSStore23(selectTrackByID3(videoTrackID));
  const hideSimulcastLayers = !((_b7 = track == null ? void 0 : track.layerDefinitions) == null ? void 0 : _b7.length) || track.degraded || !track.enabled;
  const isMobile = useMedia7(config.media.md);
  const peer = useHMSStore23(selectPeerByID2(peerID));
  const [showNameChangeModal, setShowNameChangeModal] = useState17(false);
  useDropdownList({ open, name: "TileMenu" });
  if (!(removeOthers || toggleAudio || toggleVideo || setVolume || showPinAction) && hideSimulcastLayers) {
    return null;
  }
  const openNameChangeModal = () => setShowNameChangeModal(true);
  const props = {
    isLocal,
    isScreenshare,
    audioTrackID,
    videoTrackID,
    peerID,
    isPrimaryVideoTrack,
    showSpotlight,
    showPinAction,
    canMinimise,
    openNameChangeModal
  };
  return /* @__PURE__ */ React52.createElement(React52.Fragment, null, /* @__PURE__ */ React52.createElement(StyledMenuTile.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React52.createElement(
    StyledMenuTile.Trigger,
    {
      "data-testid": "participant_menu_btn",
      css: { bg: `${theme2.colors.background_dim.value}A3`, p: "$2", w: "unset", h: "unset" },
      onClick: (e) => e.stopPropagation(),
      className: isMobile ? "__cancel-drag-event" : ""
    },
    /* @__PURE__ */ React52.createElement(VerticalMenuIcon2, { width: 20, height: 20 })
  ), isMobile ? /* @__PURE__ */ React52.createElement(Sheet.Root, { open, onOpenChange: setOpen }, /* @__PURE__ */ React52.createElement(Sheet.Content, { css: { bg: "$surface_dim", pt: "$8" } }, /* @__PURE__ */ React52.createElement(
    Flex,
    {
      css: {
        color: "$on_surface_high",
        display: "flex",
        w: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        px: "$10",
        pb: "$8",
        borderBottom: "1px solid $border_default"
      }
    },
    /* @__PURE__ */ React52.createElement(Box, null, /* @__PURE__ */ React52.createElement(Text, { css: { color: "$on_surface_high", fontWeight: "$semiBold" } }, peer.name, isLocal ? ` (You)` : null), (peer == null ? void 0 : peer.roleName) ? /* @__PURE__ */ React52.createElement(Text, { variant: "xs", css: { color: "$on_surface_low", mt: "$2" } }, peer.roleName) : null),
    /* @__PURE__ */ React52.createElement(Sheet.Close, { css: { color: "inherit" } }, /* @__PURE__ */ React52.createElement(CrossIcon8, null))
  ), /* @__PURE__ */ React52.createElement(Box, { css: { px: "$8", pb: "$8", maxHeight: "80vh", overflowY: "auto" } }, /* @__PURE__ */ React52.createElement(TileMenuContent, __spreadProps(__spreadValues({}, props), { closeSheetOnClick: () => setOpen(false) }))))) : /* @__PURE__ */ React52.createElement(StyledMenuTile.Content, { side: "top", align: "end", css: { maxHeight: "$80", overflowY: "auto" } }, /* @__PURE__ */ React52.createElement(TileMenuContent, __spreadValues({}, props)))), showNameChangeModal && /* @__PURE__ */ React52.createElement(ChangeNameModal, { onOpenChange: setShowNameChangeModal }));
};
var TileMenu_default = TileMenu;

// src/Prebuilt/components/peerTileUtils.jsx
var PEER_NAME_PLACEHOLDER = "peerName";
var labelMap = /* @__PURE__ */ new Map([
  [[true, "screen"].toString(), "Your Screen"],
  [[true, "regular"].toString(), `You (${PEER_NAME_PLACEHOLDER})`],
  [[false, "screen"].toString(), `${PEER_NAME_PLACEHOLDER}'s Screen`],
  [[false, "regular"].toString(), PEER_NAME_PLACEHOLDER],
  [[true, void 0].toString(), `You (${PEER_NAME_PLACEHOLDER})`],
  [[false, void 0].toString(), `${PEER_NAME_PLACEHOLDER}`]
]);
var getVideoTileLabel = ({ peerName, isLocal, track }) => {
  const isPeerPresent = peerName !== void 0;
  if (!isPeerPresent || !track) {
    return isPeerPresent ? labelMap.get([isLocal, void 0].toString()).replace(PEER_NAME_PLACEHOLDER, peerName) : "";
  }
  const isLocallyMuted = track.volume === 0;
  let label = labelMap.get([isLocal, track.source].toString());
  if (label) {
    label = label.replace(PEER_NAME_PLACEHOLDER, peerName);
  } else {
    label = `${peerName} ${track.source}`;
  }
  return `${label}${isLocallyMuted ? " (Muted for you)" : ""}`;
};

// src/Prebuilt/components/VideoTile.jsx
var Tile = ({
  peerId,
  trackId,
  width,
  height,
  objectFit = "cover",
  canMinimise = false,
  isDragabble = false,
  rootCSS = {},
  containerCSS = {},
  enableSpotlightingPeer = true,
  hideParticipantNameOnTile = false,
  roundedVideoTile = true,
  hideAudioMuteOnTile = false,
  hideMetadataOnTile = false
}) => {
  var _a7, _b7;
  const trackSelector = trackId ? selectVideoTrackByID4(trackId) : selectVideoTrackByPeerID3(peerId);
  const track = useHMSStore24(trackSelector);
  const isMobile = useMedia8(config.media.md);
  const peerName = useHMSStore24(selectPeerNameByID(peerId));
  const audioTrack = useHMSStore24(selectAudioTrackByPeerID2(peerId));
  const localPeerID = useHMSStore24(selectLocalPeerID5);
  const isAudioOnly = useUISettings(UI_SETTINGS.isAudioOnly);
  const mirrorLocalVideo = useUISettings(UI_SETTINGS.mirrorLocalVideo);
  const showStatsOnTiles = useUISettings(UI_SETTINGS.showStatsOnTiles);
  const isAudioMuted = !useHMSStore24(selectIsPeerAudioEnabled(peerId));
  const isVideoMuted = !(track == null ? void 0 : track.enabled);
  const [isMouseHovered, setIsMouseHovered] = useState18(false);
  const isVideoDegraded = track == null ? void 0 : track.degraded;
  const isLocal = localPeerID === peerId;
  const [pinnedTrackId] = useSetAppDataByKey(APP_DATA.pinnedTrackId);
  const pinned = isSameTile({
    trackId: pinnedTrackId,
    videoTrackID: track == null ? void 0 : track.id,
    audioTrackID: audioTrack == null ? void 0 : audioTrack.id
  });
  const spotlighted = useHMSStore24(selectSessionStore3(SESSION_STORE_KEY.SPOTLIGHT)) === peerId;
  const label = getVideoTileLabel({
    peerName,
    track,
    isLocal
  });
  const onHoverHandler = useCallback16((event) => {
    setIsMouseHovered(event.type === "mouseenter");
  }, []);
  const ref = useRef7(null);
  const calculatedHeight = ((_a7 = ref.current) == null ? void 0 : _a7.clientHeight) || "";
  const calculatedWidth = ((_b7 = ref.current) == null ? void 0 : _b7.clientWidth) || "";
  const isTileBigEnoughToShowStats = calculatedHeight >= 180 && calculatedWidth >= 180;
  const avatarSize = useMemo3(() => {
    if (!calculatedWidth || !calculatedHeight) {
      return void 0;
    }
    if (calculatedWidth <= 150 || calculatedHeight <= 150) {
      return "small";
    } else if (calculatedWidth <= 300 || calculatedHeight <= 300) {
      return "medium";
    }
    return "large";
  }, [calculatedWidth, calculatedHeight]);
  return /* @__PURE__ */ React53.createElement(
    StyledVideoTile.Root,
    {
      ref,
      css: __spreadValues({
        width,
        height
      }, rootCSS),
      "data-testid": `participant_tile_${peerName}`
    },
    peerName !== void 0 ? /* @__PURE__ */ React53.createElement(
      StyledVideoTile.Container,
      {
        onMouseEnter: onHoverHandler,
        onMouseLeave: onHoverHandler,
        noRadius: !roundedVideoTile,
        css: containerCSS
      },
      showStatsOnTiles && isTileBigEnoughToShowStats ? /* @__PURE__ */ React53.createElement(VideoTileStats, { audioTrackID: audioTrack == null ? void 0 : audioTrack.id, videoTrackID: track == null ? void 0 : track.id, peerID: peerId, isLocal }) : null,
      /* @__PURE__ */ React53.createElement(
        Video,
        {
          trackId: track == null ? void 0 : track.id,
          attach: isLocal ? void 0 : !isAudioOnly,
          mirror: mirrorLocalVideo && peerId === localPeerID && (track == null ? void 0 : track.source) === "regular" && (track == null ? void 0 : track.facingMode) !== "environment",
          noRadius: !roundedVideoTile,
          "data-testid": "participant_video_tile",
          css: {
            objectFit,
            filter: isVideoDegraded ? "blur($space$2)" : void 0,
            bg: "transparent"
          }
        }
      ),
      isVideoMuted || !isLocal && isAudioOnly ? /* @__PURE__ */ React53.createElement(StyledVideoTile.AvatarContainer, null, /* @__PURE__ */ React53.createElement(Avatar, { name: peerName || "", "data-testid": "participant_avatar_icon", size: avatarSize })) : null,
      !hideAudioMuteOnTile ? isAudioMuted ? /* @__PURE__ */ React53.createElement(
        StyledVideoTile.AudioIndicator,
        {
          "data-testid": "participant_audio_mute_icon",
          size: getAttributeBoxSize(calculatedWidth, calculatedHeight)
        },
        /* @__PURE__ */ React53.createElement(MicOffIcon3, null)
      ) : /* @__PURE__ */ React53.createElement(AudioLevel, { trackId: audioTrack == null ? void 0 : audioTrack.id }) : null,
      isMouseHovered || isDragabble && isMobile ? /* @__PURE__ */ React53.createElement(
        TileMenu_default,
        {
          peerID: peerId,
          audioTrackID: audioTrack == null ? void 0 : audioTrack.id,
          videoTrackID: track == null ? void 0 : track.id,
          canMinimise,
          enableSpotlightingPeer
        }
      ) : null,
      !hideMetadataOnTile && /* @__PURE__ */ React53.createElement(PeerMetadata, { peerId, height: calculatedHeight, width: calculatedWidth }),
      /* @__PURE__ */ React53.createElement(
        TileConnection_default,
        {
          hideLabel: hideParticipantNameOnTile,
          name: label,
          isTile: true,
          peerId,
          width,
          pinned,
          spotlighted
        }
      )
    ) : null
  );
};
var heightAnimation = (value) => keyframes({
  "50%": {
    transform: `scale3d(1,${value},1)`
  },
  "100%": {
    transform: `scale3d(1,1,1)`
  }
});
var AudioLevelIndicator = ({ trackId, value, delay }) => {
  const vanillaStore = useHMSVanillaStore6();
  const ref = useRef7();
  useEffect23(() => {
    const unsubscribe = vanillaStore.subscribe((audioLevel) => {
      if (ref.current) {
        ref.current.style["animation"] = `${heightAnimation(
          audioLevel ? value : 1
        )} 0.3s cubic-bezier(0.61, 1, 0.88, 1) infinite ${delay}s`;
      }
    }, selectTrackAudioByID(trackId));
    return unsubscribe;
  }, [vanillaStore, trackId, value, delay]);
  return /* @__PURE__ */ React53.createElement(
    Box,
    {
      ref,
      css: {
        w: 4,
        height: 6,
        r: 2,
        bg: "$on_primary_high"
      }
    }
  );
};
var AudioLevel = ({ trackId }) => {
  return /* @__PURE__ */ React53.createElement(StyledVideoTile.AudioIndicator, null, /* @__PURE__ */ React53.createElement(Flex, { align: "center", justify: "center", css: { gap: "$2" } }, [3, 2, 3].map((v, i) => /* @__PURE__ */ React53.createElement(AudioLevelIndicator, { trackId, value: v, delay: i * 0.15, key: i }))));
};
var PeerMetadata = ({ peerId, height, width }) => {
  const metaData = useHMSStore24(selectPeerMetadata2(peerId));
  const isBRB = (metaData == null ? void 0 : metaData.isBRBOn) || false;
  const isHandRaised = useHMSStore24(selectHasPeerHandRaised3(peerId));
  return /* @__PURE__ */ React53.createElement(Fragment5, null, isHandRaised ? /* @__PURE__ */ React53.createElement(StyledVideoTile.AttributeBox, { size: getAttributeBoxSize(width, height), "data-testid": "raiseHand_icon_onTile" }, /* @__PURE__ */ React53.createElement(HandIcon3, { width: 24, height: 24 })) : null, isBRB ? /* @__PURE__ */ React53.createElement(StyledVideoTile.AttributeBox, { size: getAttributeBoxSize(width, height), "data-testid": "brb_icon_onTile" }, /* @__PURE__ */ React53.createElement(BrbTileIcon, { width: 22, height: 22 })) : null);
};
var VideoTile = React53.memo(Tile);
var VideoTile_default = VideoTile;

// src/Prebuilt/components/Preview/PreviewForm.tsx
import React54 from "react";
import { useMedia as useMedia9 } from "react-use";
import { JoinForm_JoinBtnType as JoinForm_JoinBtnType2 } from "@100mslive/types-prebuilt/elements/join_form";
import { useRecordingStreaming as useRecordingStreaming3 } from "@100mslive/react-sdk";
import { RadioIcon } from "@100mslive/react-icons";
var PreviewForm = ({
  name,
  onChange,
  onJoin,
  enableJoin,
  cannotPublishVideo = false,
  cannotPublishAudio = false
}) => {
  var _a7, _b7, _c;
  const formSubmit = (e) => {
    e.preventDefault();
  };
  const isMobile = useMedia9(config.media.md);
  const { isHLSRunning } = useRecordingStreaming3();
  const layout = useRoomLayout();
  const { join_form: joinForm = {} } = ((_c = (_b7 = (_a7 = layout == null ? void 0 : layout.screens) == null ? void 0 : _a7.preview) == null ? void 0 : _b7.default) == null ? void 0 : _c.elements) || {};
  const showGoLive = (joinForm == null ? void 0 : joinForm.join_btn_type) === JoinForm_JoinBtnType2.JOIN_BTN_TYPE_JOIN_AND_GO_LIVE && !isHLSRunning;
  return /* @__PURE__ */ React54.createElement(
    Form,
    {
      css: { flexDirection: cannotPublishVideo ? "column" : "row", "@md": { flexDirection: "row" } },
      onSubmit: formSubmit
    },
    /* @__PURE__ */ React54.createElement(Flex, { align: "center", css: { gap: "$8", w: "100%" } }, /* @__PURE__ */ React54.createElement(
      Input,
      {
        required: true,
        id: "name",
        css: { w: "100%", boxSizing: "border-box" },
        value: name,
        onChange: (e) => onChange(e.target.value.trimStart()),
        placeholder: "Enter name",
        autoFocus: true,
        autoComplete: "name",
        onKeyDown: (e) => {
          if (e.key === "Enter" && name.trim().length > 0) {
            e.preventDefault();
            if (isMobile) {
              return;
            }
            onJoin();
          }
        }
      }
    ), cannotPublishAudio && cannotPublishVideo && !isMobile ? /* @__PURE__ */ React54.createElement(PreviewSettings, null) : null),
    /* @__PURE__ */ React54.createElement(Button, { type: "submit", icon: true, disabled: !name || !enableJoin, onClick: onJoin }, showGoLive ? /* @__PURE__ */ React54.createElement(RadioIcon, { height: 18, width: 18 }) : null, showGoLive ? joinForm.go_live_btn_label : joinForm.join_btn_label)
  );
};
var Form = styled("form", {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "$8",
  mt: "$10",
  mb: "$10",
  "@md": {
    gap: "$4"
  }
});
var PreviewForm_default = PreviewForm;

// src/Prebuilt/components/Preview/PreviewJoin.tsx
var VirtualBackground = React55.lazy(() => import("./VirtualBackground-3TI5NA4V.js"));
var getParticipantChipContent = (peerCount = 0) => {
  if (peerCount === 0) {
    return "You are the first to join";
  }
  const formattedNum = getFormattedCount(peerCount);
  return `${formattedNum} other${parseInt(formattedNum) === 1 ? "" : "s"} in the session`;
};
var PreviewJoin = ({
  onJoin,
  skipPreview,
  initialName,
  asRole
}) => {
  var _a7, _b7, _c;
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );
  const { isStreamingOn } = useRecordingStreaming4();
  const authToken = useAuthToken();
  const [name, setName] = useState19(initialName || previewPreference.name);
  const { toggleAudio, toggleVideo } = useAVToggle2();
  const [previewError, setPreviewError] = useState19(false);
  const { endpoints } = useHMSPrebuiltContext();
  const { peerCount } = useParticipants();
  const { enableJoin, preview, join } = usePreviewJoin({
    name,
    token: authToken,
    initEndpoint: endpoints == null ? void 0 : endpoints.init,
    initialSettings: {
      isAudioMuted: skipPreview || previewPreference.isAudioMuted,
      isVideoMuted: skipPreview || previewPreference.isVideoMuted,
      speakerAutoSelectionBlacklist: ["Yeti Stereo Microphone"]
    },
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        setPreviewError(true);
      }
    },
    asRole
  });
  const roomState = useHMSStore25(selectRoomState6);
  const savePreferenceAndJoin = useCallback17(() => {
    setPreviewPreference({
      name
    });
    join();
    onJoin && onJoin();
  }, [join, name, setPreviewPreference, onJoin]);
  const roomLayout = useRoomLayout();
  const { preview_header: previewHeader = {} } = ((_c = (_b7 = (_a7 = roomLayout == null ? void 0 : roomLayout.screens) == null ? void 0 : _a7.preview) == null ? void 0 : _b7.default) == null ? void 0 : _c.elements) || {};
  const localPeer = useHMSStore25(selectLocalPeer);
  const videoTrack = useHMSStore25(selectVideoTrackByID5(localPeer == null ? void 0 : localPeer.videoTrack));
  const isMobile = useMedia10(config.media.md);
  const aspectRatio = (videoTrack == null ? void 0 : videoTrack.width) && (videoTrack == null ? void 0 : videoTrack.height) ? videoTrack.width / videoTrack.height : isMobile ? 9 / 16 : 16 / 9;
  useEffect24(() => {
    if (authToken) {
      if (skipPreview) {
        savePreferenceAndJoin();
      } else {
        preview();
      }
    }
  }, [authToken, skipPreview]);
  useEffect24(() => {
    if (initialName) {
      setName(initialName);
    }
  }, [initialName]);
  return roomState === HMSRoomState6.Preview ? /* @__PURE__ */ React55.createElement(Container3, { css: { h: "100%", pt: "$10", "@md": { justifyContent: "space-between" } } }, toggleVideo ? null : /* @__PURE__ */ React55.createElement(Box, null), /* @__PURE__ */ React55.createElement(Flex, { direction: "column", justify: "center", css: { w: "100%", maxWidth: "640px" } }, /* @__PURE__ */ React55.createElement(Logo, null), /* @__PURE__ */ React55.createElement(
    Text,
    {
      variant: "h4",
      css: { wordBreak: "break-word", textAlign: "center", mt: "$14", mb: "$4", "@md": { mt: "$8", mb: "$2" } }
    },
    previewHeader.title
  ), /* @__PURE__ */ React55.createElement(
    Text,
    {
      css: { c: "$on_surface_medium", my: "0", textAlign: "center", maxWidth: "100%", wordWrap: "break-word" },
      variant: "sm"
    },
    previewHeader.sub_title
  ), /* @__PURE__ */ React55.createElement(Flex, { justify: "center", css: { mt: "$14", "@md": { mt: "$8", mb: "0" }, gap: "$4" } }, isStreamingOn ? /* @__PURE__ */ React55.createElement(
    Chip_default,
    {
      content: "LIVE",
      backgroundColor: "$alert_error_default",
      textColor: "#FFF",
      icon: /* @__PURE__ */ React55.createElement(Box, { css: { h: "$sm", w: "$sm", backgroundColor: "$on_primary_high", borderRadius: "$round" } })
    }
  ) : null, /* @__PURE__ */ React55.createElement(Chip_default, { content: getParticipantChipContent(peerCount), hideIfNoContent: true }))), toggleVideo ? /* @__PURE__ */ React55.createElement(
    Flex,
    {
      align: "center",
      justify: "center",
      css: {
        mt: "$14",
        "@md": { mt: 0 },
        "@sm": { width: "100%" },
        flexDirection: "column"
      }
    },
    /* @__PURE__ */ React55.createElement(PreviewTile, { name, error: previewError })
  ) : null, /* @__PURE__ */ React55.createElement(Box, { css: { w: "100%", maxWidth: `${Math.max(aspectRatio, 1) * 360}px` } }, /* @__PURE__ */ React55.createElement(PreviewControls, { hideSettings: !toggleVideo && !toggleAudio }), /* @__PURE__ */ React55.createElement(
    PreviewForm_default,
    {
      name,
      onChange: setName,
      enableJoin,
      onJoin: savePreferenceAndJoin,
      cannotPublishVideo: !toggleVideo,
      cannotPublishAudio: !toggleAudio
    }
  ))) : /* @__PURE__ */ React55.createElement(FullPageProgress_default, null);
};
var Container3 = styled("div", __spreadProps(__spreadValues({
  width: "100%"
}, flexCenter), {
  flexDirection: "column",
  px: "$10"
}));
var PreviewTile = ({ name, error }) => {
  const localPeer = useHMSStore25(selectLocalPeer);
  const { isLocalAudioEnabled, toggleAudio } = useAVToggle2();
  const isVideoOn = useHMSStore25(selectIsLocalVideoEnabled5);
  const mirrorLocalVideo = useUISettings(UI_SETTINGS.mirrorLocalVideo);
  const trackSelector = selectVideoTrackByID5(localPeer == null ? void 0 : localPeer.videoTrack);
  const track = useHMSStore25(trackSelector);
  const showMuteIcon = !isLocalAudioEnabled || !toggleAudio;
  const videoTrack = useHMSStore25(selectVideoTrackByID5(localPeer == null ? void 0 : localPeer.videoTrack));
  const isMobile = useMedia10(config.media.md);
  const aspectRatio = (videoTrack == null ? void 0 : videoTrack.width) && (videoTrack == null ? void 0 : videoTrack.height) ? videoTrack.width / videoTrack.height : isMobile ? 9 / 16 : 16 / 9;
  return /* @__PURE__ */ React55.createElement(
    StyledVideoTile.Container,
    {
      css: {
        bg: "$surface_default",
        aspectRatio,
        height: "min(360px, 70vh)",
        maxWidth: "640px",
        overflow: "clip",
        "@md": {
          width: "min(220px, 70vw)",
          maxWidth: "100%",
          my: "$4"
        }
      }
    },
    localPeer ? /* @__PURE__ */ React55.createElement(React55.Fragment, null, /* @__PURE__ */ React55.createElement(TileConnection_default, { name, peerId: localPeer.id, hideLabel: true }), /* @__PURE__ */ React55.createElement(
      Video,
      {
        mirror: (track == null ? void 0 : track.facingMode) !== "environment" && mirrorLocalVideo,
        trackId: localPeer.videoTrack,
        "data-testid": "preview_tile"
      }
    ), !isVideoOn ? /* @__PURE__ */ React55.createElement(StyledVideoTile.AvatarContainer, null, /* @__PURE__ */ React55.createElement(Avatar, { name, "data-testid": "preview_avatar_tile" })) : null) : !error ? /* @__PURE__ */ React55.createElement(FullPageProgress_default, null) : null,
    showMuteIcon ? /* @__PURE__ */ React55.createElement(StyledVideoTile.AudioIndicator, null, /* @__PURE__ */ React55.createElement(MicOffIcon4, null)) : /* @__PURE__ */ React55.createElement(AudioLevel, { trackId: localPeer == null ? void 0 : localPeer.audioTrack })
  );
};
var PreviewControls = ({ hideSettings }) => {
  const isMobile = useMedia10(config.media.md);
  return /* @__PURE__ */ React55.createElement(
    Flex,
    {
      justify: hideSettings && isMobile ? "center" : "between",
      css: {
        width: "100%",
        mt: "$8"
      }
    },
    /* @__PURE__ */ React55.createElement(Flex, { css: { gap: "$4" } }, /* @__PURE__ */ React55.createElement(AudioVideoToggle, null), /* @__PURE__ */ React55.createElement(Suspense, { fallback: "" }, !isMobile ? /* @__PURE__ */ React55.createElement(VirtualBackground, null) : null)),
    !hideSettings ? /* @__PURE__ */ React55.createElement(PreviewSettings, null) : null
  );
};
var PreviewSettings = React55.memo(() => {
  const [open, setOpen] = useState19(false);
  return /* @__PURE__ */ React55.createElement(Fragment6, null, /* @__PURE__ */ React55.createElement(IconButton_default, { "data-testid": "preview_setting_btn", css: { flexShrink: 0 }, onClick: () => setOpen((value) => !value) }, /* @__PURE__ */ React55.createElement(SettingsIcon2, null)), open && /* @__PURE__ */ React55.createElement(SettingsModal_default, { open, onOpenChange: setOpen }));
});
var PreviewJoin_default = PreviewJoin;

// src/Prebuilt/components/Preview/PreviewContainer.tsx
var PreviewContainer = () => {
  var _a7, _b7, _c;
  const navigate = useNavigate4();
  const { isPreviewScreenEnabled } = useRoomLayoutPreviewScreen();
  const skipPreview = !isPreviewScreenEnabled;
  const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE);
  const { userName } = useHMSPrebuiltContext();
  const initialName = userName || (skipPreview ? "Beam" : "");
  const { roomId: urlRoomId, role: userRole } = useParams4();
  const authToken = useAuthToken();
  const roomLayout = useRoomLayout();
  const { preview_header: previewHeader = {} } = ((_c = (_b7 = (_a7 = roomLayout == null ? void 0 : roomLayout.screens) == null ? void 0 : _a7.preview) == null ? void 0 : _b7.default) == null ? void 0 : _c.elements) || {};
  const onJoin = () => {
    let meetingURL = `/meeting/${urlRoomId}`;
    if (userRole) {
      meetingURL += `/${userRole}`;
    }
    navigate(meetingURL);
  };
  return /* @__PURE__ */ React56.createElement(Flex, { direction: "column", css: { size: "100%" } }, /* @__PURE__ */ React56.createElement(
    Flex,
    {
      css: { flex: "1 1 0", position: "relative", overflowY: "auto", color: "$primary_default" },
      justify: "center",
      align: "center"
    },
    authToken && Object.keys(previewHeader).length > 0 ? /* @__PURE__ */ React56.createElement(
      PreviewJoin_default,
      {
        initialName,
        skipPreview,
        asRole: previewAsRole != null ? previewAsRole : void 0,
        onJoin
      }
    ) : /* @__PURE__ */ React56.createElement(FullPageProgress_default, null)
  ));
};
var PreviewContainer_default = PreviewContainer;

// src/Prebuilt/components/Toast/ToastContainer.jsx
import React58, { useEffect as useEffect25, useState as useState20 } from "react";
import { selectIsConnectedToRoom as selectIsConnectedToRoom3, useHMSStore as useHMSStore26 } from "@100mslive/react-sdk";

// src/Prebuilt/components/Toast/Toast.jsx
import React57 from "react";
var Toast2 = (_a7) => {
  var _b7 = _a7, { title, description, close = true, open, duration, onOpenChange, icon } = _b7, props = __objRest(_b7, ["title", "description", "close", "open", "duration", "onOpenChange", "icon"]);
  return /* @__PURE__ */ React57.createElement(
    Toast.HMSToast,
    __spreadValues({
      title,
      description,
      open,
      isClosable: close,
      onOpenChange,
      duration: !close ? 6e5 : duration,
      icon
    }, props)
  );
};

// src/Prebuilt/components/Toast/ToastContainer.jsx
var ToastContainer = () => {
  const isConnected = useHMSStore26(selectIsConnectedToRoom3);
  const [toasts, setToast] = useState20([]);
  useEffect25(() => {
    ToastManager.addListener(setToast);
    return () => {
      ToastManager.removeListener(setToast);
    };
  }, []);
  return /* @__PURE__ */ React58.createElement(Toast.Provider, { swipeDirection: "left", duration: 3e3 }, toasts.slice(0, MAX_TOASTS).map((toast) => {
    return /* @__PURE__ */ React58.createElement(Toast2, __spreadProps(__spreadValues({ key: toast.id }, toast), { onOpenChange: (value) => !value && ToastManager.removeToast(toast.id) }));
  }), /* @__PURE__ */ React58.createElement(
    Toast.Viewport,
    {
      css: __spreadValues({
        position: "absolute"
      }, !isConnected ? {} : { bottom: "$24" })
    }
  ));
};

// src/Prebuilt/plugins/FlyingEmoji.jsx
import React59, { useCallback as useCallback18, useEffect as useEffect26, useMemo as useMemo4, useState as useState21 } from "react";
import { useMedia as useMedia11 } from "react-use";
import {
  selectLocalPeerID as selectLocalPeerID6,
  selectPeerNameByID as selectPeerNameByID2,
  useCustomEvent as useCustomEvent3,
  useHMSStore as useHMSStore27,
  useHMSVanillaStore as useHMSVanillaStore7
} from "@100mslive/react-sdk";
var emojiCount = 1;
var flyAndFade = keyframes({
  "20%": { opacity: 1 },
  "100%": { bottom: "60%", opacity: 0 }
});
var wiggleLeftRight = keyframes({
  "0%": { marginLeft: "-50px" },
  "100%": { marginLeft: "50px" }
});
var wiggleRightLeft = keyframes({
  "0%": { marginLeft: "50px" },
  "100%": { marginLeft: "-50px" }
});
var getStartingPoints = (isMobile) => {
  let arr = [];
  const min = 5;
  const max = isMobile ? 30 : 20;
  const inc = isMobile ? 8 : 5;
  for (let i = min; i <= max; i += inc) {
    arr.push(i);
  }
  return arr;
};
function FlyingEmoji() {
  const localPeerId = useHMSStore27(selectLocalPeerID6);
  const vanillaStore = useHMSVanillaStore7();
  const [emojis, setEmojis] = useState21([]);
  const isMobile = useMedia11(config.media.md);
  const startingPoints = useMemo4(() => getStartingPoints(isMobile), [isMobile]);
  const showFlyingEmoji = useCallback18(
    ({ emojiId, senderId }) => {
      if (!emojiId || !senderId || document.hidden) {
        return;
      }
      const senderPeerName = vanillaStore.getState(selectPeerNameByID2(senderId));
      const nameToShow = localPeerId === senderId ? "You" : senderPeerName;
      const startingPoint = startingPoints[emojiCount % startingPoints.length];
      const id = emojiCount++;
      setEmojis((emojis2) => {
        return [
          ...emojis2,
          {
            id,
            emojiId,
            senderName: nameToShow,
            startingPoint: `${startingPoint}%`,
            wiggleType: Math.random() < 0.5 ? 0 : 1
          }
        ];
      });
    },
    [localPeerId, vanillaStore, startingPoints]
  );
  useCustomEvent3({
    type: EMOJI_REACTION_TYPE,
    onEvent: showFlyingEmoji
  });
  useEffect26(() => {
    window.showFlyingEmoji = showFlyingEmoji;
  }, [showFlyingEmoji]);
  return /* @__PURE__ */ React59.createElement(
    Box,
    {
      css: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 999
      }
    },
    emojis.map((emoji) => {
      return /* @__PURE__ */ React59.createElement(
        Flex,
        {
          key: emoji.id,
          css: {
            left: emoji.startingPoint,
            flexDirection: "column",
            alignItems: "center",
            position: "absolute",
            bottom: 0,
            animation: `${flyAndFade()} 5s forwards, ${emoji.wiggleType === 0 ? wiggleLeftRight() : wiggleRightLeft()} 1s ease-in-out infinite alternate`
          },
          onAnimationEnd: () => {
            setEmojis(emojis.filter((item) => item.id !== emoji.id));
          }
        },
        /* @__PURE__ */ React59.createElement(Box, null, /* @__PURE__ */ React59.createElement("em-emoji", { id: emoji.emojiId, size: "48px", set: "apple" })),
        /* @__PURE__ */ React59.createElement(
          Box,
          {
            css: {
              width: "fit-content",
              padding: "$2 $4",
              background: "$surface_bright",
              borderRadius: "$1"
            }
          },
          /* @__PURE__ */ React59.createElement(
            Text,
            {
              css: {
                fontSize: "$space$6",
                lineHeight: "$xs",
                color: "$on_surface_high"
              }
            },
            emoji.senderName
          )
        )
      );
    })
  );
}

// src/Prebuilt/plugins/RemoteStopScreenshare.jsx
import React60, { useCallback as useCallback19 } from "react";
import { useCustomEvent as useCustomEvent4, useHMSActions as useHMSActions18 } from "@100mslive/react-sdk";
function RemoteStopScreenshare() {
  const actions = useHMSActions18();
  const onRemoteStopScreenshare = useCallback19(() => __async(this, null, function* () {
    yield actions.setScreenShareEnabled(false);
  }), [actions]);
  useCustomEvent4({
    type: REMOTE_STOP_SCREENSHARE_TYPE,
    onEvent: onRemoteStopScreenshare
  });
  return /* @__PURE__ */ React60.createElement(React60.Fragment, null);
}

// src/Prebuilt/components/hooks/useAutoStartStreaming.tsx
import { useCallback as useCallback20, useEffect as useEffect28, useRef as useRef9 } from "react";
import {
  selectIsConnectedToRoom as selectIsConnectedToRoom5,
  selectPermissions as selectPermissions4,
  useHMSActions as useHMSActions19,
  useHMSStore as useHMSStore29,
  useRecordingStreaming as useRecordingStreaming5
} from "@100mslive/react-sdk";

// src/Prebuilt/common/hooks.js
import { useEffect as useEffect27, useRef as useRef8, useState as useState22 } from "react";
import { JoinForm_JoinBtnType as JoinForm_JoinBtnType3 } from "@100mslive/types-prebuilt/elements/join_form";
import {
  selectAvailableRoleNames as selectAvailableRoleNames2,
  selectIsConnectedToRoom as selectIsConnectedToRoom4,
  selectPeerCount,
  selectPeerMetadata as selectPeerMetadata3,
  selectPeers,
  selectRemotePeers,
  useHMSStore as useHMSStore28,
  useHMSVanillaStore as useHMSVanillaStore8
} from "@100mslive/react-sdk";
var useFilteredRoles = () => {
  const roles = useHMSStore28(selectAvailableRoleNames2);
  return roles;
};
var useShowStreamingUI = () => {
  var _a7, _b7, _c;
  const layout = useRoomLayout();
  const { join_form } = ((_c = (_b7 = (_a7 = layout == null ? void 0 : layout.screens) == null ? void 0 : _a7.preview) == null ? void 0 : _b7.default) == null ? void 0 : _c.elements) || {};
  return (join_form == null ? void 0 : join_form.join_btn_type) === JoinForm_JoinBtnType3.JOIN_BTN_TYPE_JOIN_AND_GO_LIVE;
};
var useParticipants2 = (params) => {
  var _a7;
  const isConnected = useHMSStore28(selectIsConnectedToRoom4);
  const peerCount = useHMSStore28(selectPeerCount);
  const availableRoles = useHMSStore28(selectAvailableRoleNames2);
  let participantList = useHMSStore28(isConnected ? selectPeers : selectRemotePeers);
  const rolesWithParticipants = Array.from(new Set(participantList.map((peer) => peer.roleName)));
  const vanillaStore = useHMSVanillaStore8();
  if ((_a7 = params == null ? void 0 : params.metadata) == null ? void 0 : _a7.isHandRaised) {
    participantList = participantList.filter((peer) => {
      return vanillaStore.getState(selectPeerMetadata3(peer.id)).isHandRaised;
    });
  }
  if ((params == null ? void 0 : params.role) && availableRoles.includes(params.role)) {
    participantList = participantList.filter((peer) => peer.roleName === params.role);
  }
  if (params == null ? void 0 : params.search) {
    const search = params.search;
    participantList = participantList.filter((peer) => peer.name.toLowerCase().includes(search));
  }
  return { participants: participantList, isConnected, peerCount, rolesWithParticipants };
};

// src/Prebuilt/components/hooks/useAutoStartStreaming.tsx
var useAutoStartStreaming = () => {
  const [isHLSStarted, setHLSStarted] = useSetAppDataByKey(APP_DATA.hlsStarted);
  const permissions = useHMSStore29(selectPermissions4);
  const showStreamingUI = useShowStreamingUI();
  const hmsActions = useHMSActions19();
  const isConnected = useHMSStore29(selectIsConnectedToRoom5);
  const { isHLSRunning } = useRecordingStreaming5();
  const streamStartedRef = useRef9(false);
  const startHLS = useCallback20(() => __async(void 0, null, function* () {
    var _a7;
    try {
      if (isHLSStarted || !showStreamingUI || isHLSRunning) {
        return;
      }
      setHLSStarted(true);
      streamStartedRef.current = true;
      yield hmsActions.startHLSStreaming();
    } catch (error) {
      if ((_a7 = error.message) == null ? void 0 : _a7.includes("beam already started")) {
        return;
      }
      streamStartedRef.current = false;
      setHLSStarted(false);
    }
  }), [hmsActions, isHLSRunning, isHLSStarted, setHLSStarted, showStreamingUI]);
  useEffect28(() => {
    if (!isHLSStarted && !isHLSRunning) {
      streamStartedRef.current = false;
    }
  }, [isHLSStarted, isHLSRunning]);
  useEffect28(() => {
    if (!isConnected || streamStartedRef.current || !(permissions == null ? void 0 : permissions.hlsStreaming)) {
      return;
    }
    startHLS();
  }, [isConnected]);
};

// src/Prebuilt/App.tsx
var Conference = React61.lazy(() => import("./conference-JNABIZBG.js"));
var HMSPrebuilt = React61.forwardRef(
  ({
    roomCode = "",
    authToken = "",
    roomId = "",
    role = "",
    logo,
    typography,
    themes,
    options: { userName = "", userId = "", endpoints } = {},
    screens,
    onLeave
  }, ref) => {
    var _a7, _b7, _c, _d;
    const metadata = "";
    const reactiveStore = useRef10();
    const [hydrated, setHydrated] = React61.useState(false);
    useEffect29(() => {
      setHydrated(true);
      const hms = new HMSReactiveStore();
      const hmsStore = hms.getStore();
      const hmsActions = hms.getActions();
      const hmsNotifications = hms.getNotifications();
      const hmsStats = hms.getStats();
      hms.triggerOnSubscribe();
      reactiveStore.current = {
        hmsActions,
        hmsStats,
        hmsStore,
        hmsNotifications
      };
    }, []);
    useEffect29(() => {
      if (!ref || !reactiveStore.current) {
        return;
      }
      ref.current = __spreadValues({}, reactiveStore.current);
    }, [ref]);
    useEffect29(
      () => () => {
        var _a8;
        (_a8 = reactiveStore == null ? void 0 : reactiveStore.current) == null ? void 0 : _a8.hmsActions.leave();
      },
      []
    );
    const endpointsObj = endpoints;
    const tokenByRoomCodeEndpoint = (endpointsObj == null ? void 0 : endpointsObj.tokenByRoomCode) || "";
    const initEndpoint = (endpointsObj == null ? void 0 : endpointsObj.init) || "";
    const roomLayoutEndpoint = (endpointsObj == null ? void 0 : endpointsObj.roomLayout) || "";
    const tokenByRoomIdRoleEndpoint = (endpointsObj == null ? void 0 : endpointsObj.tokenByRoomIdRole) || "";
    const overrideLayout = {
      logo,
      themes,
      typography,
      screens
    };
    if (!roomCode && !(authToken && roomId && role)) {
      console.error(`
          HMSPrebuilt can be initialised by providing: 
          either just "roomCode" or "authToken" and "roomId" and "role".
          Please check if you are providing the above values for initialising prebuilt.
        `);
      throw Error("Incorrect initializing params for HMSPrebuilt component");
    }
    if (!hydrated) {
      return null;
    }
    globalStyles();
    return /* @__PURE__ */ React61.createElement(ErrorBoundary, null, /* @__PURE__ */ React61.createElement(
      HMSPrebuiltContext.Provider,
      {
        value: {
          roomCode,
          roomId,
          role,
          onLeave,
          userName,
          userId,
          endpoints: {
            tokenByRoomCode: tokenByRoomCodeEndpoint,
            init: initEndpoint,
            tokenByRoomIdRole: tokenByRoomIdRoleEndpoint,
            roomLayout: roomLayoutEndpoint
          }
        }
      },
      /* @__PURE__ */ React61.createElement(
        HMSRoomProvider,
        {
          isHMSStatsOn: FeatureFlags.enableStatsForNerds,
          actions: (_a7 = reactiveStore.current) == null ? void 0 : _a7.hmsActions,
          store: (_b7 = reactiveStore.current) == null ? void 0 : _b7.hmsStore,
          notifications: (_c = reactiveStore.current) == null ? void 0 : _c.hmsNotifications,
          stats: (_d = reactiveStore.current) == null ? void 0 : _d.hmsStats
        },
        /* @__PURE__ */ React61.createElement(RoomLayoutProvider, { roomLayoutEndpoint, overrideLayout }, /* @__PURE__ */ React61.createElement(RoomLayoutContext.Consumer, null, (data) => {
          var _a8;
          const layout = data == null ? void 0 : data.layout;
          const theme2 = ((_a8 = layout == null ? void 0 : layout.themes) == null ? void 0 : _a8[0]) || {};
          const { typography: typography2 } = layout || {};
          let fontFamily = ["sans-serif"];
          if (typography2 == null ? void 0 : typography2.font_family) {
            fontFamily = [`${typography2 == null ? void 0 : typography2.font_family}`, ...fontFamily];
          }
          return /* @__PURE__ */ React61.createElement(
            HMSThemeProvider,
            {
              themeType: `${theme2.name}-${Date.now()}`,
              theme: {
                //@ts-ignore: Prebuilt theme to match stiches theme
                colors: theme2.palette,
                fonts: {
                  //@ts-ignore: font list to match token types of stiches
                  sans: fontFamily
                }
              }
            },
            /* @__PURE__ */ React61.createElement(AppData, { appDetails: metadata, tokenEndpoint: tokenByRoomIdRoleEndpoint }),
            /* @__PURE__ */ React61.createElement(Init, null),
            /* @__PURE__ */ React61.createElement(
              Box,
              {
                css: {
                  bg: "$background_dim",
                  size: "100%",
                  lineHeight: "1.5",
                  "-webkit-text-size-adjust": "100%"
                }
              },
              /* @__PURE__ */ React61.createElement(AppRoutes, { authTokenByRoomCodeEndpoint: tokenByRoomCodeEndpoint, defaultAuthToken: authToken })
            )
          );
        }))
      )
    ));
  }
);
HMSPrebuilt.displayName = "HMSPrebuilt";
var Redirector = ({ showPreview }) => {
  const { roomId, role } = useParams5();
  return /* @__PURE__ */ React61.createElement(Navigate, { to: `/${showPreview ? "preview" : "meeting"}/${roomId}/${role || ""}` });
};
var RouteList = () => {
  const { isPreviewScreenEnabled } = useRoomLayoutPreviewScreen();
  const { isLeaveScreenEnabled } = useRoomLayoutLeaveScreen();
  useAutoStartStreaming();
  return /* @__PURE__ */ React61.createElement(Routes, null, isPreviewScreenEnabled ? /* @__PURE__ */ React61.createElement(Route, { path: "preview" }, /* @__PURE__ */ React61.createElement(
    Route,
    {
      path: ":roomId/:role",
      element: /* @__PURE__ */ React61.createElement(Suspense2, { fallback: /* @__PURE__ */ React61.createElement(FullPageProgress_default, { text: "Loading preview..." }) }, /* @__PURE__ */ React61.createElement(PreviewContainer_default, null))
    }
  ), /* @__PURE__ */ React61.createElement(
    Route,
    {
      path: ":roomId",
      element: /* @__PURE__ */ React61.createElement(Suspense2, { fallback: /* @__PURE__ */ React61.createElement(FullPageProgress_default, { text: "Loading preview..." }) }, /* @__PURE__ */ React61.createElement(PreviewContainer_default, null))
    }
  )) : null, /* @__PURE__ */ React61.createElement(Route, { path: "meeting" }, /* @__PURE__ */ React61.createElement(
    Route,
    {
      path: ":roomId/:role",
      element: /* @__PURE__ */ React61.createElement(Suspense2, { fallback: /* @__PURE__ */ React61.createElement(FullPageProgress_default, { text: "Joining..." }) }, /* @__PURE__ */ React61.createElement(Conference, null))
    }
  ), /* @__PURE__ */ React61.createElement(
    Route,
    {
      path: ":roomId",
      element: /* @__PURE__ */ React61.createElement(Suspense2, { fallback: /* @__PURE__ */ React61.createElement(FullPageProgress_default, { text: "Joining..." }) }, /* @__PURE__ */ React61.createElement(Conference, null))
    }
  )), isLeaveScreenEnabled ? /* @__PURE__ */ React61.createElement(Route, { path: "leave" }, /* @__PURE__ */ React61.createElement(Route, { path: ":roomId/:role", element: /* @__PURE__ */ React61.createElement(PostLeave_default, null) }), /* @__PURE__ */ React61.createElement(Route, { path: ":roomId", element: /* @__PURE__ */ React61.createElement(PostLeave_default, null) })) : null, /* @__PURE__ */ React61.createElement(Route, { path: "/:roomId/:role", element: /* @__PURE__ */ React61.createElement(Redirector, { showPreview: isPreviewScreenEnabled }) }), /* @__PURE__ */ React61.createElement(Route, { path: "/:roomId/", element: /* @__PURE__ */ React61.createElement(Redirector, { showPreview: isPreviewScreenEnabled }) }));
};
var BackSwipe = () => {
  const isConnectedToRoom = useHMSStore30(selectIsConnectedToRoom6);
  const hmsActions = useHMSActions20();
  useEffect29(() => {
    const onRouteLeave = () => __async(void 0, null, function* () {
      if (isConnectedToRoom) {
        yield hmsActions.leave();
      }
    });
    window.addEventListener("popstate", onRouteLeave);
    return () => {
      window.removeEventListener("popstate", onRouteLeave);
    };
  }, [hmsActions, isConnectedToRoom]);
  return null;
};
var Router = ({ children }) => {
  const { roomId, role, roomCode } = useHMSPrebuiltContext();
  return [roomId, role, roomCode].every((value) => !value) ? /* @__PURE__ */ React61.createElement(BrowserRouter, null, children) : /* @__PURE__ */ React61.createElement(MemoryRouter, { initialEntries: [`/${roomCode ? roomCode : `${roomId}/${role || ""}`}`], initialIndex: 0 }, children);
};
function AppRoutes({
  authTokenByRoomCodeEndpoint,
  defaultAuthToken
}) {
  const roomLayout = useRoomLayout();
  const isNotificationsDisabled = useIsNotificationDisabled();
  return /* @__PURE__ */ React61.createElement(Router, null, /* @__PURE__ */ React61.createElement(React61.Fragment, null, /* @__PURE__ */ React61.createElement(ToastContainer, null), /* @__PURE__ */ React61.createElement(Notifications, null), /* @__PURE__ */ React61.createElement(BackSwipe, null), !isNotificationsDisabled && /* @__PURE__ */ React61.createElement(FlyingEmoji, null), /* @__PURE__ */ React61.createElement(RemoteStopScreenshare, null), /* @__PURE__ */ React61.createElement(KeyboardHandler, null), /* @__PURE__ */ React61.createElement(AuthToken_default, { authTokenByRoomCodeEndpoint, defaultAuthToken }), roomLayout && /* @__PURE__ */ React61.createElement(Routes, null, /* @__PURE__ */ React61.createElement(Route, { path: "/*", element: /* @__PURE__ */ React61.createElement(RouteList, null) }))));
}

// src/Progress/index.tsx
import { Indicator as Indicator3, Root as Root24 } from "@radix-ui/react-progress";
var StyledIndicator = styled(Indicator3, { h: "$4", backgroundColor: "$primary_default" });
var StyledRoot2 = styled(Root24, {
  w: "100%",
  h: "$4",
  borderRadius: "$round",
  backgroundColor: "$secondary_dim",
  overflow: "hidden"
});
var Progress = {
  Root: StyledRoot2,
  Content: StyledIndicator
};

export {
  ThemeTypes,
  ThemeContext,
  HMSThemeProvider,
  useTheme,
  Button,
  StyledAvatar,
  Avatar,
  VerticalDivider,
  HorizontalDivider,
  Switch,
  Select,
  Slider,
  Dialog,
  Input,
  PasswordInput,
  StyledVideoTile,
  getLeft,
  StyledVideoList,
  StyledPagination,
  Dropdown,
  StyledVideo,
  Video,
  StyledMenuTile,
  useBorderAudioLevel,
  Popover2 as Popover,
  VideoTileStats,
  Checkbox,
  Label3 as Label,
  RadioGroup,
  Toast,
  Accordion,
  Fieldset,
  Footer,
  ReactSelect_exports,
  Tabs,
  QRCode,
  Link,
  Collapsible,
  UserPreferencesKeys,
  useUserPreferences,
  RTMP_RECORD_RESOLUTION_MIN,
  RTMP_RECORD_RESOLUTION_MAX,
  RTMP_RECORD_DEFAULT_RESOLUTION,
  EMOJI_REACTION_TYPE,
  CHAT_SELECTOR,
  APP_DATA,
  UI_SETTINGS,
  SIDE_PANE_OPTIONS,
  isChrome,
  isIOS,
  isMacOS,
  isAndroid,
  isIPadOS,
  SESSION_STORE_KEY,
  ROLE_CHANGE_DECLINED,
  useIsSidepaneTypeOpen,
  useSidepaneToggle,
  useSidepaneReset,
  useUISettings,
  useSetUiSettings,
  useWaitingViewerRole,
  useAuthToken,
  useUrlToEmbed,
  usePDFAnnotator,
  usePinnedTrack,
  useSetSubscribedChatSelector,
  useSetAppDataByKey,
  useHMSPrebuiltContext,
  DialogContent,
  DialogRow,
  DialogCol,
  DialogSelect,
  DialogInputFile,
  FullPageProgress_default,
  ToastManager,
  PictureInPicture,
  useRoomLayoutPreviewScreen,
  useRoomLayoutConferencingScreen,
  useRedirectToLeave,
  getMetadata,
  isScreenshareSupported,
  getFormattedCount,
  Sheet,
  useMyMetadata,
  StopRecordingInSheet,
  Header2 as Header,
  AudioVideoToggle,
  ConnectionIndicator,
  DialogDropdownTrigger,
  useDropdownSelection,
  SettingsModal_default,
  ChangeNameModal,
  useDropdownList,
  TileMenu_default,
  getVideoTileLabel,
  VideoTile_default,
  PreviewTile,
  PreviewControls,
  useFilteredRoles,
  useParticipants2 as useParticipants,
  HMSPrebuilt,
  Progress
};
